import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Grid as VirtualGrid } from "react-window";
import { AutoSizer } from "react-virtualized-auto-sizer";
import TVGuide from "./TVGuide";
import SpotifyPage from "./spotify/SpotifyPage";
import DisplayPage from "./display/DisplayPage";
import StartupGate from "./StartupGate";
import { setPlayerState } from "./state/playerState";
import { getTMDBPlaylists } from "./api/tmdbPlaylists";
import NapflixBoot from "./NapflixBoot";
import {
  searchTMDBMovie,
  searchTMDBTV,
  getTMDBWatchProviders,
  getTMDBBrandLogo,
} from "./api/tmdb";
import {
  installNapflixOSRuntimeShell,
} from "./os/napflixOS";
import {
  getSystemState,
  installSystemStateRuntime,
} from "./os/SystemState";
import { installBootManager } from "./os/BootManager";
import { installAmbientManager } from "./os/AmbientManager";
import AmbientOverlay from "./os/AmbientOverlay";
import NotificationOverlay from "./os/NotificationOverlay";
import CommandCenterOverlay from "./os/CommandCenterOverlay";
import SearchOverlay from "./os/SearchOverlay";
import NowPlayingOverlay from "./os/NowPlayingOverlay";
import { installRouteManager } from "./os/RouteManager";
import SystemStatusOverlay from "./os/SystemStatusOverlay";
import { installBackRuntime } from "./os/BackManager";

const PLEX_URL = "";
const TOKEN = "";
const MOVIE_KEYS = [3, 38, 30, 42];
const SHOW_KEYS = [11, 2, 31, 41];
const MUSIC_KEY = 40;
const MUSIC_VIDEO_KEY = 33;
const MUSIC_BANDS_KEY = 37;
// YOUTUBE_KEYS removed — re-add when moving to Unraid

const STUDIO_LOGOS = {
  "HBO": { bg: "#000", color: "#fff", text: "HBO" },
  "Max": { bg: "#000", color: "#fff", text: "MAX" },
  "HBO Max": { bg: "#000", color: "#fff", text: "MAX" },
  "A24": { bg: "#fff", color: "#000", text: "A24" },
  "Netflix": { bg: "#e50914", color: "#fff", text: "NETFLIX" },
  "Warner Bros": { bg: "#1a1a2e", color: "#f5c518", text: "WB" },
  "Warner Bros.": { bg: "#1a1a2e", color: "#f5c518", text: "WB" },
  "Universal": { bg: "#000080", color: "#fff", text: "UNIVERSAL" },
  "Universal Pictures": { bg: "#000080", color: "#fff", text: "UNIVERSAL" },
  "Paramount": { bg: "#0067b8", color: "#fff", text: "PARAMOUNT" },
  "Paramount Pictures": { bg: "#0067b8", color: "#fff", text: "PARAMOUNT" },
  "Disney": { bg: "#113ccf", color: "#fff", text: "DISNEY" },
  "Walt Disney": { bg: "#113ccf", color: "#fff", text: "DISNEY" },
  "Marvel": { bg: "#ed1d24", color: "#fff", text: "MARVEL" },
  "Marvel Studios": { bg: "#ed1d24", color: "#fff", text: "MARVEL" },
  "Sony": { bg: "#003087", color: "#fff", text: "SONY" },
  "Columbia": { bg: "#003087", color: "#fff", text: "COLUMBIA" },
  "Criterion": { bg: "#fff", color: "#000", text: "CRITERION" },
  "Criterion Collection": { bg: "#fff", color: "#000", text: "CRITERION" },
  "Miramax": { bg: "#c8a951", color: "#000", text: "MIRAMAX" },
  "Lionsgate": { bg: "#f7941d", color: "#fff", text: "LIONSGATE" },
  "Amazon": { bg: "#232f3e", color: "#ff9900", text: "PRIME" },
  "Amazon Studios": { bg: "#232f3e", color: "#ff9900", text: "PRIME" },
  "Apple": { bg: "#000", color: "#fff", text: "APPLE TV+" },
  "FX": { bg: "#000", color: "#fff", text: "FX" },
  "Hulu": { bg: "#1ce783", color: "#000", text: "HULU" },
  "Showtime": { bg: "#c00", color: "#fff", text: "SHOWTIME" },
  "AMC": { bg: "#000", color: "#fff", text: "AMC" },
  "BBC": { bg: "#bb1919", color: "#fff", text: "BBC" },
  "IFC": { bg: "#000", color: "#fff", text: "IFC" },
  "Neon": { bg: "#00ff88", color: "#000", text: "NEON" },
  "Focus Features": { bg: "#fff", color: "#000", text: "FOCUS" },
  "Rough House": { bg: "#222", color: "#fff", text: "ROUGH HOUSE" },
  "WWE": { bg: "#d4af37", color: "#000", text: "WWE" },
  "Peacock": { bg: "#000", color: "#fff", text: "PEACOCK" },
  "Starz": { bg: "#000", color: "#fff", text: "STARZ" },
  "Paramount+": { bg: "#0067b8", color: "#fff", text: "PARAMOUNT+" },
};

const NETWORK_LOGOS = {
  ...STUDIO_LOGOS,

  // ── Broadcast ─────────────────
  "ABC": { bg: "#000", color: "#fff", text: "ABC" },
  "NBC": { bg: "#003366", color: "#fff", text: "NBC" },
  "CBS": { bg: "#1f5aa6", color: "#fff", text: "CBS" },
  "FOX": { bg: "#111", color: "#fff", text: "FOX" },
  "PBS": { bg: "#2638c4", color: "#fff", text: "PBS" },

  // ── Premium / Cable ───────────
  "HBO": { bg: "#000", color: "#fff", text: "HBO" },
  "HBO Max": { bg: "#000", color: "#fff", text: "MAX" },
  "Max": { bg: "#000", color: "#fff", text: "MAX" },
  "Showtime": { bg: "#c00", color: "#fff", text: "SHOWTIME" },
  "Starz": { bg: "#000", color: "#fff", text: "STARZ" },
  "Cinemax": { bg: "#000", color: "#fff", text: "CINEMAX" },

  "AMC": { bg: "#000", color: "#fff", text: "AMC" },
  "FX": { bg: "#000", color: "#fff", text: "FX" },
  "FXX": { bg: "#000", color: "#fff", text: "FXX" },
  "TNT": { bg: "#fdb913", color: "#000", text: "TNT" },
  "TBS": { bg: "#00a3e0", color: "#fff", text: "TBS" },
  "USA": { bg: "#1d1d1d", color: "#fff", text: "USA" },
  "Syfy": { bg: "#6c3cff", color: "#fff", text: "SYFY" },

  "A&E": { bg: "#000", color: "#fff", text: "A&E" },
  "History": { bg: "#000", color: "#fff", text: "HISTORY" },
  "Discovery": { bg: "#0f1c2e", color: "#fff", text: "DISCOVERY" },
  "National Geographic": { bg: "#000", color: "#fff", text: "NAT GEO" },

  "VH1": { bg: "#000", color: "#fff", text: "VH1" },
  "MTV": { bg: "#ffea00", color: "#000", text: "MTV" },
  "Comedy Central": { bg: "#000", color: "#fff", text: "CC" },

  // ── Streaming ────────────────
  "Netflix": { bg: "#e50914", color: "#fff", text: "NETFLIX" },
  "Hulu": { bg: "#1ce783", color: "#000", text: "HULU" },
  "Apple TV+": { bg: "#000", color: "#fff", text: "APPLE TV+" },
  "Prime Video": { bg: "#232f3e", color: "#ff9900", text: "PRIME" },
  "Amazon": { bg: "#232f3e", color: "#ff9900", text: "PRIME" },
  "Disney+": { bg: "#113ccf", color: "#fff", text: "DISNEY+" },
  "Paramount+": { bg: "#0067b8", color: "#fff", text: "PARAMOUNT+" },
  "Peacock": { bg: "#000", color: "#fff", text: "PEACOCK" },

  "YouTube": { bg: "#ff0000", color: "#fff", text: "YT" },
  "YouTube Originals": { bg: "#ff0000", color: "#fff", text: "YT" },

  // ── International / Premium TV ─────────
  "BBC": { bg: "#bb1919", color: "#fff", text: "BBC" },
  "BBC One": { bg: "#bb1919", color: "#fff", text: "BBC" },
  "BBC Two": { bg: "#bb1919", color: "#fff", text: "BBC" },
  "ITV": { bg: "#00a9e0", color: "#fff", text: "ITV" },
  "Channel 4": { bg: "#00ff00", color: "#000", text: "C4" },

  // ── Animation / Kids ─────────
  "Cartoon Network": { bg: "#000", color: "#fff", text: "CN" },
  "Adult Swim": { bg: "#000", color: "#fff", text: "AS" },
  "Nickelodeon": { bg: "#ff6700", color: "#fff", text: "NICK" },
  "Disney Channel": { bg: "#113ccf", color: "#fff", text: "DISNEY" },
  "Disney XD": { bg: "#113ccf", color: "#fff", text: "DXD" },

  // ── Studios (behave like networks in UI) ─────────
  "A24": { bg: "#fff", color: "#000", text: "A24" },
  "Neon": { bg: "#00ff88", color: "#000", text: "NEON" },
  "Blumhouse": { bg: "#000", color: "#fff", text: "BLUMHOUSE" },
  "Pixar": { bg: "#113ccf", color: "#fff", text: "PIXAR" },
  "DreamWorks": { bg: "#0033a0", color: "#fff", text: "DREAMWORKS" },
  "Lucasfilm": { bg: "#000", color: "#fff", text: "LUCASFILM" },
  "Marvel Studios": { bg: "#ed1d24", color: "#fff", text: "MARVEL" },

  // ── Sports / Misc ─────────
  "ESPN": { bg: "#c8102e", color: "#fff", text: "ESPN" },
  "WWE": { bg: "#d4af37", color: "#000", text: "WWE" },
};

function getStudioLogo(studio) {
  if (!studio) return null;
  for (const [key, val] of Object.entries(STUDIO_LOGOS)) {
    if (studio.toLowerCase().includes(key.toLowerCase())) return val;
  }
  return null;
}

function getNetworkName(item) {
  return (
    item?.Network?.[0]?.tag ||
    item?.network ||
    item?.studio ||
    ""
  );
}

function getNetworkLogo(network) {
  if (!network) return null;

  const lower = network.toLowerCase();

  for (const [key, val] of Object.entries(NETWORK_LOGOS)) {
    if (lower.includes(key.toLowerCase())) return val;
  }

  return null;
}

function getRTInfo(item) {
  if (!item.ratingImage) return null;
  const img = item.ratingImage;
  const score = item.audienceRating ? Math.round(item.audienceRating * 10) : null;
  if (img.includes("certified_fresh")) return { icon: "🍅✓", label: "Certified Fresh", color: "#fa320a", score };
  if (img.includes("ripe")) return { icon: "🍅", label: "Fresh", color: "#fa320a", score };
  if (img.includes("rotten")) return { icon: "🦠", label: "Rotten", color: "#9b9b9b", score };
  return null;
}

function getIMDbRating(item) {
  if (item.rating && parseFloat(item.rating) > 0) return parseFloat(item.rating).toFixed(1);
  return null;
}

const S = `
@import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Outfit:wght@300;400;500;600;700&display=swap');
*,*::before,*::after{margin:0;padding:0;box-sizing:border-box}
:root{--gold:#e5a00d;--bg:#060609;--s1:#0d0d16;--s2:#13131f;--s3:#1c1c2c;--t1:#fff;--t2:rgba(255,255,255,0.72);--t3:rgba(255,255,255,0.4);--t4:rgba(255,255,255,0.14)}
html{scroll-behavior:smooth}
body,#root{background:var(--bg);color:var(--t1);font-family:'Outfit',sans-serif;min-height:100vh;overflow-x:hidden}
::-webkit-scrollbar{width:4px}::-webkit-scrollbar-track{background:transparent}::-webkit-scrollbar-thumb{background:rgba(229,160,13,0.2);border-radius:2px}

.nav{position:fixed;top:0;left:0;right:0;z-index:300;height:62px;display:flex;align-items:center;justify-content:center;padding:0 52px;transition:background .3s}
.nav.solid{background:none;border-bottom:none}
.nav-right-group{display:flex;align-items:center;gap:1px}
.pj-logo-fixed{position:fixed;top:20px;left:40px;z-index:310;cursor:pointer;transition:width .2s ease,height .2s ease}
.logo{font-family:'Bebas Neue',sans-serif;font-size:24px;letter-spacing:5px;color:var(--gold);cursor:pointer;text-shadow:0 0 24px rgba(229,160,13,0.3);user-select:none}
.nav-links{display:flex;gap:1px;list-style:none}
.nav-btn{background:none;border:none;color:var(--t2);font-family:'Outfit',sans-serif;font-size:14px;font-weight:400;padding:6px 14px;border-radius:20px;cursor:pointer;transition:all .18s}
.nav-btn:hover{color:var(--t1);background:rgba(255,255,255,0.07)}
.nav-btn.on{color:var(--gold);background:rgba(229,160,13,0.09)}
.search-wrap{position:relative}
.search-inp{background:rgba(255,255,255,0.07);border:1px solid rgba(255,255,255,0.09);border-radius:20px;padding:7px 14px 7px 34px;color:var(--t1);font-family:'Outfit',sans-serif;font-size:13px;font-weight:300;width:200px;outline:none;transition:all .22s}
.search-inp:focus{width:260px;border-color:rgba(229,160,13,0.3);background:rgba(255,255,255,0.1)}
.search-inp::placeholder{color:var(--t3)}
.search-ico{position:absolute;left:11px;top:50%;transform:translateY(-50%);opacity:.3;pointer-events:none}

.hero{position:relative;height:90vh;min-height:600px;display:flex;flex-direction:column;justify-content:flex-end;overflow:hidden}
.hero-bg{position:absolute;inset:0;background-size:cover;background-position:center 18%}
.hero-bg::after{content:'';position:absolute;inset:0;background:linear-gradient(to right,rgba(6,6,9,0.45) 0%,rgba(6,6,9,0.32) 35%,rgba(6,6,9,0.08) 65%,transparent 100%),linear-gradient(to top,rgba(6,6,9,1) 0%,rgba(6,6,9,0.65) 18%,rgba(6,6,9,0.1) 50%,transparent 70%)}
.hero-main{position:relative;z-index:2;padding:0 52px 26px;max-width:660px}
.hero-eyebrow{display:flex;align-items:center;gap:8px;margin-bottom:14px;flex-wrap:wrap}
.hero-tag{background:var(--gold);color:#000;font-size:9px;font-weight:700;letter-spacing:2.5px;text-transform:uppercase;padding:3px 9px;border-radius:2px}
.hero-genre-pill{font-size:10px;color:rgba(255,255,255,0.75);letter-spacing:2px;text-transform:uppercase;font-weight:500;padding:3px 10px;border:1px solid rgba(255,255,255,0.18);border-radius:2px;backdrop-filter:blur(4px);background:rgba(255,255,255,0.06)}
.hero-title{font-family:'Bebas Neue',sans-serif;line-height:.87;letter-spacing:.5px;margin-bottom:12px}
.hero-meta-row{display:flex;align-items:center;gap:8px;margin-bottom:10px;flex-wrap:wrap}
.hmi{font-size:14px;color:var(--t2);font-weight:400}
.hdiv{width:3px;height:3px;border-radius:50%;background:var(--t3)}
.hero-badges-row{display:flex;gap:5px;flex-wrap:wrap;margin-bottom:16px;align-items:center}
.hero-credits{font-size:13px;color:var(--t3);margin-bottom:10px;line-height:2;font-weight:300}
.hero-credits b{color:var(--t2);font-weight:500}
.hero-desc{font-size:15px;line-height:1.72;color:var(--t2);margin-bottom:24px;max-width:520px;font-weight:300;display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden}
.hero-btns{display:flex;gap:10px;align-items:center}
.btn-play{display:flex;align-items:center;gap:9px;background:var(--t1);color:#000;border:none;padding:13px 32px;border-radius:4px;font-family:'Outfit',sans-serif;font-size:16px;font-weight:600;cursor:pointer;transition:all .18s}
.btn-play:hover{background:#e0e0e0;transform:translateY(-1px)}
.btn-more{display:flex;align-items:center;gap:8px;background:rgba(255,255,255,0.12);color:var(--t1);border:none;padding:13px 26px;border-radius:4px;font-family:'Outfit',sans-serif;font-size:16px;font-weight:400;cursor:pointer;transition:all .18s;backdrop-filter:blur(6px)}
.btn-more:hover{background:rgba(255,255,255,0.2)}
.btn-wl{width:44px;height:44px;border-radius:50%;background:rgba(255,255,255,0.1);border:2px solid rgba(255,255,255,0.28);color:var(--t1);cursor:pointer;transition:all .18s;display:flex;align-items:center;justify-content:center;font-size:20px}
.btn-wl:hover{border-color:var(--gold);color:var(--gold)}
.btn-wl.on{background:rgba(229,160,13,0.12);border-color:var(--gold);color:var(--gold)}
.btn-trailer{display:flex;align-items:center;gap:7px;background:rgba(255,255,255,0.08);color:var(--t2);border:1px solid rgba(255,255,255,0.15);padding:10px 20px;border-radius:4px;font-family:'Outfit',sans-serif;font-size:14px;font-weight:400;cursor:pointer;transition:all .18s}
.btn-trailer:hover{background:rgba(255,255,255,0.14);color:var(--t1)}

.featured-bar{position:relative;z-index:10;padding:0 52px}
.featured-bar-label{font-size:10px;letter-spacing:2px;text-transform:uppercase;color:var(--t3);margin-bottom:10px;font-weight:400}
.featured-row{display:flex;gap:10px;overflow-x:auto;padding-bottom:28px;-ms-overflow-style:none;scrollbar-width:none}
.featured-row::-webkit-scrollbar{display:none}
.feat-card{flex:0 0 120px;cursor:pointer;position:relative;border-radius:5px;overflow:hidden;aspect-ratio:2/3;background:var(--s2);border:2px solid transparent;transition:border-color .2s,transform .2s,opacity .2s}
.feat-card:hover{transform:scale(1.05)}
.feat-card.active{border-color:var(--gold)}
.feat-card:not(.active){opacity:.6}
.feat-card:not(.active):hover{opacity:1}
.feat-card img{width:100%;height:100%;object-fit:cover;display:block}

.badge{display:inline-flex;align-items:center;gap:3px;padding:2px 7px;border-radius:2px;font-size:9px;font-weight:700;letter-spacing:.6px;text-transform:uppercase;font-family:'Outfit',sans-serif}
.b4k{background:rgba(255,255,255,0.08);border:1px solid rgba(255,255,255,0.24);color:var(--t1)}
.buhd{background:rgba(0,168,255,0.07);border:1px solid rgba(0,168,255,0.32);color:#00a8ff}
.bdv{background:rgba(0,100,255,0.07);border:1px solid rgba(0,100,255,0.3);color:#4a9eff}
.batmos{background:rgba(80,180,255,0.07);border:1px solid rgba(80,180,255,0.28);color:#50b4ff}
.bhdr{background:rgba(255,200,0,0.07);border:1px solid rgba(255,200,0,0.3);color:#ffc800}
.bcrit{background:rgba(200,200,200,0.06);border:1px solid rgba(200,200,200,0.24);color:#c0c0c0}
.brated{background:rgba(255,255,255,0.06);border:1px solid rgba(255,255,255,0.15);color:var(--t2)}
.bimdb{background:rgba(245,197,24,0.08);border:1px solid rgba(245,197,24,0.28);color:#f5c518}
.brt-fresh{background:rgba(250,50,10,0.08);border:1px solid rgba(250,50,10,0.28);color:#fa320a}
.brt-rotten{background:rgba(100,100,100,0.08);border:1px solid rgba(100,100,100,0.28);color:#9b9b9b}
.studio-badge{padding:3px 9px;border-radius:2px;font-size:10px;font-weight:700;letter-spacing:.8px;text-transform:uppercase;font-family:'Outfit',sans-serif}

.content{position:relative;z-index:10;padding:32px 0 100px}
.sec{margin-bottom:44px}
.sec-head{display:flex;align-items:flex-end;justify-content:space-between;padding:0 52px;margin-bottom:16px}
.sec-left{display:flex;align-items:center;gap:10px}
.sec-bar{width:3px;height:20px;background:var(--gold);border-radius:2px;flex-shrink:0}
.sec-title{font-family:'Bebas Neue',sans-serif;font-size:24px;letter-spacing:2px}
.sec-sub{font-size:12px;color:var(--t3);margin-top:2px;font-weight:300}
.sec-all{background:none;border:none;color:var(--gold);font-family:'Outfit',sans-serif;font-size:10px;letter-spacing:1.5px;text-transform:uppercase;cursor:pointer;padding:0;font-weight:400}
.sec-all:hover{opacity:.6}
.row-wrap{padding:0 52px}
.row{display:flex;gap:12px;overflow-x:auto;padding-bottom:8px;-ms-overflow-style:none;scrollbar-width:none}
.row::-webkit-scrollbar{display:none}

.card{flex:0 0 200px;position:relative;border-radius:6px;overflow:hidden;cursor:pointer;transition:transform .26s ease,box-shadow .26s ease;aspect-ratio:2/3;background:var(--s2)}
.card:hover{transform:scale(1.06) translateY(-4px);z-index:20;box-shadow:0 22px 60px rgba(0,0,0,0.85)}
.card img{width:100%;height:100%;object-fit:cover;display:block}
.card-shade{position:absolute;inset:0;background:linear-gradient(to top,rgba(0,0,0,0.9) 0%,transparent 55%);opacity:0;transition:opacity .26s}
.card:hover .card-shade{opacity:1}
.card-foot{position:absolute;bottom:0;left:0;right:0;padding:12px 10px;transform:translateY(3px);opacity:0;transition:all .26s}
.card:hover .card-foot{transform:none;opacity:1}
.card-n{font-size:13px;font-weight:500;line-height:1.3;margin-bottom:3px}
.card-y{font-size:11px;color:rgba(255,255,255,0.45)}
.card-play{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:46px;height:46px;border-radius:50%;background:rgba(229,160,13,0.88);display:flex;align-items:center;justify-content:center;opacity:0;transition:opacity .26s;pointer-events:none}
.card:hover .card-play{opacity:1}
.card-ph{width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:linear-gradient(135deg,var(--s2) 0%,var(--s3) 100%);color:var(--t3);font-size:12px;text-align:center;padding:14px;line-height:1.5}
.img-shell{
width:100%;
height:100%;
position:relative;
overflow:hidden;
background:linear-gradient(135deg,var(--s2) 0%,var(--s3) 100%)
}

.img-placeholder{
position:absolute;
inset:0;
display:flex;
align-items:center;
justify-content:center;
padding:16px;
text-align:center;
color:rgba(255,255,255,.24);
font-size:11px;
line-height:1.35;
background:
radial-gradient(circle at 30% 20%,rgba(255,255,255,.08),transparent 35%),
linear-gradient(135deg,rgba(255,255,255,.03),rgba(255,255,255,.08));
overflow:hidden;
z-index:1
}

.img-placeholder::after{
content:"";
position:absolute;
inset:0;
transform:translateX(-100%);
background:linear-gradient(90deg,transparent,rgba(255,255,255,.08),transparent);
animation:imgShimmer 1.5s infinite
}

.img-placeholder span{
position:relative;
z-index:2
}

.img-blur{
position:absolute;
inset:0;
width:100%;
height:100%;
object-fit:cover;
filter:blur(18px) saturate(1.15);
transform:scale(1.08);
opacity:.9;
z-index:1
}

.img-shell img{
width:100%;
height:100%;
object-fit:cover;
display:block
}

.img-loading{
opacity:0;
position:relative;
z-index:3
}

.img-loaded{
opacity:1;
position:relative;
z-index:3;
transition:opacity .22s ease
}

@keyframes imgShimmer{
100%{
transform:translateX(100%)
}
}
.card-wide{flex:0 0 310px;aspect-ratio:16/9}

.filters{display:flex;gap:7px;padding:0 52px;margin-bottom:26px;flex-wrap:wrap}
.fpill{padding:7px 18px;border-radius:20px;font-size:13px;font-weight:400;cursor:pointer;border:1px solid rgba(255,255,255,0.09);background:rgba(255,255,255,0.04);color:var(--t2);transition:all .16s;font-family:'Outfit',sans-serif}
.fpill:hover{border-color:rgba(229,160,13,0.35);color:var(--t1)}
.fpill.on{background:var(--gold);border-color:var(--gold);color:#000;font-weight:600}
.alnav{display:flex;gap:3px;padding:0 52px;margin-bottom:22px;flex-wrap:wrap}
.albtn{width:30px;height:30px;border-radius:4px;border:none;background:rgba(255,255,255,0.05);color:var(--t3);font-family:'Outfit',sans-serif;font-size:12px;font-weight:400;cursor:pointer;transition:all .14s;display:flex;align-items:center;justify-content:center}
.albtn:hover{background:rgba(255,255,255,0.1);color:var(--t1)}
.albtn.on{background:var(--gold);color:#000}
.agrid{padding:0 52px;display:grid;grid-template-columns:repeat(auto-fill,minmax(185px,1fr));gap:12px}

/* SCROLL SCRUBBER */
.scroll-scrubber{position:fixed;right:6px;top:50%;transform:translateY(-50%);z-index:200;width:4px;height:55vh;background:rgba(255,255,255,0.05);border-radius:2px;cursor:pointer;transition:width .15s}
.scroll-scrubber:hover{width:6px}
.scroll-scrubber-thumb{position:absolute;left:0;width:100%;background:rgba(229,160,13,0.6);border-radius:2px;min-height:20px;transition:background .15s}
.scroll-scrubber:hover .scroll-scrubber-thumb{background:var(--gold)}

/* ALPHA JUMP BAR */
.alpha-jump{position:fixed;top:70px;left:50%;transform:translateX(-50%);z-index:250;display:flex;gap:3px;background:rgba(6,6,9,0.94);backdrop-filter:blur(16px);border:1px solid rgba(255,255,255,0.08);border-radius:8px;padding:8px 12px;flex-wrap:wrap;justify-content:center;max-width:80vw;transition:opacity .25s,transform .25s}
.alpha-jump.hidden{opacity:0;transform:translateX(-50%) translateY(-10px);pointer-events:none}
.aj-btn{width:24px;height:24px;border-radius:3px;border:none;background:transparent;color:var(--t3);font-family:'Outfit',sans-serif;font-size:11px;cursor:pointer;transition:all .1s;display:flex;align-items:center;justify-content:center}
.aj-btn:hover,.aj-btn.has{color:var(--gold)}
.aj-btn:hover{background:rgba(255,255,255,0.07)}

/* LIB TAB */
.lib-sub-tabs{display:flex;gap:0;padding:0 52px;border-bottom:1px solid rgba(255,255,255,0.05);margin-bottom:0}
.lib-sub-tab{padding:14px 22px;border:none;border-bottom:2px solid transparent;background:none;color:var(--t3);font-family:'Outfit',sans-serif;font-size:14px;cursor:pointer;transition:all .18s;margin-bottom:-1px}
.lib-sub-tab:hover{color:var(--t1)}
.lib-sub-tab.on{color:var(--gold);border-bottom-color:var(--gold)}

.ph{padding:94px 52px 32px}
.ph-title{font-family:'Bebas Neue',sans-serif;font-size:48px;letter-spacing:3px;margin-bottom:5px}
.ph-sub{font-size:14px;color:var(--t3);font-weight:300}

.loading{min-height:100vh;display:flex;align-items:center;justify-content:center;flex-direction:column;gap:18px}
.spin{width:44px;height:44px;border:2px solid rgba(229,160,13,0.1);border-top-color:var(--gold);border-radius:50%;animation:spin .8s linear infinite}
@keyframes spin{to{transform:rotate(360deg)}}
@keyframes pj-pulse{0%,100%{opacity:1}50%{opacity:0.25}}
.spin-txt{font-size:11px;color:var(--t3);letter-spacing:3px;text-transform:uppercase;font-weight:300}

/* VIDEO PLAYER */
.player-overlay{position:fixed;inset:0;z-index:600;background:#000;display:flex;flex-direction:column}
.player-topbar{display:flex;align-items:center;justify-content:space-between;padding:16px 24px;background:rgba(0,0,0,0.8);position:absolute;top:0;left:0;right:0;z-index:10}
.player-title{font-size:16px;font-weight:500;color:var(--t1)}
.player-close{background:none;border:none;color:var(--t1);font-size:24px;cursor:pointer;padding:4px 8px;border-radius:4px;transition:background .18s}
.player-close:hover{background:rgba(255,255,255,0.1)}
.player-video{width:100%;height:100%;object-fit:contain;background:#000}
.player-error{display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;gap:16px;padding:40px;text-align:center}
.player-error-title{font-size:20px;font-weight:500;margin-bottom:8px}
.player-error-msg{font-size:14px;color:var(--t2);font-weight:300;margin-bottom:24px;max-width:500px;line-height:1.7}

/* DETAIL PAGE */
.detail-page{position:fixed;inset:0;z-index:500;background:var(--bg);overflow-y:auto;animation:fi .2s ease}
@keyframes fi{from{opacity:0}to{opacity:1}}
.detail-hero{position:relative;min-height:100vh;display:flex;align-items:flex-end}
.detail-art{position:absolute;inset:0;background-size:cover;background-position:center 18%}
.detail-art::after{content:'';position:absolute;inset:0;background:linear-gradient(to right,rgba(6,6,9,0.45) 0%,rgba(6,6,9,0.32) 38%,rgba(6,6,9,0.08) 65%,transparent 100%),linear-gradient(to top,rgba(6,6,9,1) 0%,rgba(6,6,9,0.8) 25%,transparent 55%)}
.detail-poster{width:220px;height:330px;border-radius:8px;overflow:hidden;box-shadow:0 24px 60px rgba(0,0,0,0.9);border:2px solid rgba(255,255,255,0.12);margin-bottom:24px}
.detail-poster img{width:100%;height:100%;object-fit:cover}
.detail-back{position:fixed;top:20px;left:24px;z-index:600;background:rgba(0,0,0,0.7);border:1px solid rgba(255,255,255,0.12);color:var(--t1);padding:8px 16px;border-radius:20px;cursor:pointer;font-family:'Outfit',sans-serif;font-size:13px;font-weight:400;backdrop-filter:blur(8px);transition:background .18s}
.detail-back:hover{background:rgba(0,0,0,0.9)}
.detail-content{position:relative;z-index:5;padding:0 52px 52px;max-width:680px}
.detail-studio{margin-bottom:14px}
.detail-title{font-family:'Bebas Neue',sans-serif;font-size:clamp(56px,9vw,112px);line-height:.88;letter-spacing:.5px;margin-bottom:14px}
.detail-meta{display:flex;align-items:center;gap:9px;margin-bottom:12px;flex-wrap:wrap}
.dmi{font-size:15px;color:var(--t2);font-weight:400}
.ddiv{width:3px;height:3px;border-radius:50%;background:var(--t3)}
.detail-badges{display:flex;gap:5px;flex-wrap:wrap;align-items:center;margin-bottom:18px}
.detail-summary-short{font-size:15px;line-height:1.74;color:var(--t2);margin-bottom:16px;font-weight:300;max-width:580px;display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden}
.detail-credits{font-size:13px;color:var(--t3);margin-bottom:22px;line-height:2;font-weight:300}
.detail-credits b{color:var(--t2);font-weight:500;cursor:pointer}
.detail-credits b:hover{color:var(--gold);text-decoration:underline}
.detail-btns{display:flex;gap:10px;align-items:center;flex-wrap:wrap}

.detail-body{padding:0 0 60px;max-width:100%}
.detail-info-grid{display:flex;flex-direction:column;margin-bottom:40px;border-top:1px solid rgba(255,255,255,0.07);padding:0 52px}
.detail-row{display:flex;padding:14px 0;border-bottom:1px solid rgba(255,255,255,0.06)}
.detail-lbl{font-size:12px;color:var(--t3);letter-spacing:.8px;text-transform:uppercase;min-width:150px;padding-top:1px;flex-shrink:0;font-weight:400}
.detail-val{font-size:15px;color:var(--t2);line-height:1.6;font-weight:300}
.detail-val span{cursor:pointer;transition:color .18s}
.detail-val span:hover{color:var(--gold);text-decoration:underline}
.detail-genre-pills{display:flex;gap:6px;flex-wrap:wrap}
.detail-genre-pill{padding:3px 10px;border:1px solid rgba(255,255,255,0.15);border-radius:2px;font-size:12px;color:var(--t2);letter-spacing:.5px;transition:border-color .18s,color .18s;cursor:default}
.detail-genre-pill:hover{border-color:rgba(229,160,13,0.4);color:var(--gold)}

.detail-section{margin-bottom:48px;padding:0 52px}
.detail-sec-title{font-size:12px;letter-spacing:2px;text-transform:uppercase;color:var(--t3);margin-bottom:18px;font-weight:400}

.seasons-row{display:flex;gap:14px;overflow-x:auto;padding-bottom:6px;padding-left:2px;-ms-overflow-style:none;scrollbar-width:none}
.seasons-row::-webkit-scrollbar{display:none}
.season-card{flex:0 0 160px;cursor:pointer;text-align:center}
.season-poster{width:100%;aspect-ratio:2/3;border-radius:7px;overflow:hidden;background:var(--s2);margin-bottom:8px;border:2px solid transparent;transition:border-color .2s,transform .2s}
.season-card:hover .season-poster{transform:scale(1.03)}
.season-card.on .season-poster{border-color:var(--gold)}
.season-poster img{width:100%;height:100%;object-fit:cover}
.season-n{font-size:14px;color:var(--t2);font-weight:500;margin-bottom:2px}
.season-ep{font-size:12px;color:var(--t3);font-weight:300}

.episodes-grid{margin-top:22px;display:grid;grid-template-columns:repeat(auto-fill,minmax(300px,1fr));gap:14px}
.ep-card{background:var(--s2);border-radius:8px;overflow:hidden;cursor:pointer;transition:background .18s}
.ep-card:hover{background:var(--s3)}
.ep-thumb{width:100%;aspect-ratio:16/9;overflow:hidden;background:var(--s3);position:relative}
.ep-thumb img{width:100%;height:100%;object-fit:cover}
.ep-play{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,0.3);opacity:0;transition:opacity .2s}
.ep-card:hover .ep-play{opacity:1}
.ep-body{padding:12px 14px 14px}
.ep-meta{font-size:11px;color:var(--t3);margin-bottom:4px;font-weight:300}
.ep-title{font-size:15px;font-weight:500;margin-bottom:5px}
.ep-desc{font-size:12.5px;color:var(--t3);line-height:1.55;font-weight:300;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}

.cast-row{display:flex;gap:14px;overflow-x:auto;padding:0 52px 8px;-ms-overflow-style:none;scrollbar-width:none}.cast-row::-webkit-scrollbar{display:none}
.cast-card{text-align:center;cursor:pointer}
.castavatar{width:88px;height:88px;border-radius:50%;overflow:hidden;background:var(--s2);margin:0 auto 8px;border:2px solid rgba(255,255,255,0.08);transition:border-color .2s}
.cast-card:hover .castavatar{border-color:rgba(229,160,13,.4)}
.castavatar img{width:100%;height:100%;object-fit:cover;object-position:top}
.cast-n{font-size:12.5px;color:var(--t2);line-height:1.35;font-weight:400}
.cast-r{font-size:11px;color:var(--t3);margin-top:3px;font-style:italic;font-weight:300}

.rel-row-h{display:flex;gap:12px;overflow-x:auto;padding:0 52px 8px;-ms-overflow-style:none;scrollbar-width:none}.rel-row-h::-webkit-scrollbar{display:none}
.rel-card{cursor:pointer;flex:0 0 170px}
.rel-thumb{width:100%;aspect-ratio:2/3;border-radius:7px;overflow:hidden;background:var(--s2);margin-bottom:7px;transition:transform .2s}
.rel-card:hover .rel-thumb{transform:scale(1.05)}
.rel-thumb img{width:100%;height:100%;object-fit:cover}
.rel-n{font-size:13px;color:var(--t2);line-height:1.35;font-weight:400}
.rel-y{font-size:11px;color:var(--t3);font-weight:300}

.extras-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:12px;padding:0 4px}
.extra-card{cursor:pointer;background:var(--s2);border-radius:8px;overflow:hidden;transition:background .18s}
.extra-card:hover{background:var(--s3)}
.extra-thumb{width:100%;aspect-ratio:16/9;overflow:hidden;background:var(--s3);position:relative}
.extra-thumb img{width:100%;height:100%;object-fit:cover}
.extra-body{padding:10px 12px 12px}
.extra-title{font-size:13px;font-weight:500;margin-bottom:3px}
.extra-type{font-size:11px;color:var(--t3);font-weight:300}

.sovl{position:fixed;inset:0;z-index:400;background:rgba(6,6,9,0.97);padding:80px 52px 40px;animation:fi .14s ease;overflow-y:auto}
.shead{font-family:'Bebas Neue',sans-serif;font-size:28px;letter-spacing:3px;color:var(--t3);margin-bottom:22px}
.sgrid{display:grid;grid-template-columns:repeat(auto-fill,minmax(185px,1fr));gap:12px}
.sclose{position:fixed;top:18px;right:22px;background:none;border:none;color:var(--t2);font-size:24px;cursor:pointer;z-index:401}

.cats-grid{padding:0 52px 80px;display:grid;grid-template-columns:repeat(auto-fill,minmax(205px,1fr));gap:10px}
.cat-tile{background:var(--s2);border:1px solid rgba(255,255,255,0.05);border-radius:8px;padding:18px 20px;cursor:pointer;transition:all .18s}
.cat-tile:hover{border-color:rgba(229,160,13,.25);background:rgba(229,160,13,.03)}
.cat-prefix{font-size:8.5px;color:var(--gold);letter-spacing:1.5px;text-transform:uppercase;margin-bottom:5px;font-weight:400}
.cat-name{font-family:'Bebas Neue',sans-serif;font-size:21px;letter-spacing:1.5px;margin-bottom:4px}
.cat-count{font-size:11px;color:var(--t3);font-weight:300}

/* MUSIC / YOUTUBE PAGES */
.media-page{padding:90px 52px 80px}
.media-page-title{font-family:'Bebas Neue',sans-serif;font-size:48px;letter-spacing:3px;margin-bottom:10px}
.media-page-sub{font-size:14px;color:var(--t3);font-weight:300;margin-bottom:28px}
.media-tabs{display:flex;gap:6px;margin-bottom:32px;flex-wrap:wrap}
.mtab{padding:8px 22px;border-radius:20px;border:1px solid rgba(255,255,255,0.09);background:none;color:var(--t2);font-family:'Outfit',sans-serif;font-size:14px;cursor:pointer;transition:all .18s;font-weight:400}
.mtab.on{background:var(--gold);border-color:var(--gold);color:#000;font-weight:600}

.artist-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(145px,1fr));gap:22px}
.artist-card{cursor:pointer;text-align:center}
.artist-img{width:100%;aspect-ratio:1;border-radius:12px;overflow:hidden;background:var(--s2);margin-bottom:8px;border:2px solid rgba(255,255,255,0.06);transition:all .2s}
.artist-card:hover .artist-img{border-color:rgba(229,160,13,.4);transform:scale(1.04)}
.artist-img img{width:100%;height:100%;object-fit:cover;object-position:top}
.artist-name{font-size:14px;font-weight:500}
.artist-type{font-size:11px;color:var(--t3);font-weight:300}

.album-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(160px,1fr));gap:18px}
.album-card{cursor:pointer}
.album-img{width:100%;aspect-ratio:1;border-radius:8px;overflow:hidden;background:var(--s2);margin-bottom:8px;transition:transform .2s;box-shadow:0 8px 24px rgba(0,0,0,0.4)}
.album-card:hover .album-img{transform:scale(1.04)}
.album-img img{width:100%;height:100%;object-fit:cover}
.album-title{font-size:13px;font-weight:500;margin-bottom:2px}
.album-artist{font-size:11px;color:var(--t3);font-weight:300}

.video-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(230px,1fr));gap:14px}
.video-card{cursor:pointer}
.video-thumb{width:100%;aspect-ratio:16/9;border-radius:7px;overflow:hidden;background:var(--s2);margin-bottom:8px;position:relative;transition:transform .18s}
.video-card:hover .video-thumb{transform:scale(1.03)}
.video-thumb img{width:100%;height:100%;object-fit:cover}
.video-ov{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,0.3);opacity:0;transition:opacity .18s}
.video-card:hover .video-ov{opacity:1}
.video-title{font-size:13px;font-weight:500;margin-bottom:3px;line-height:1.4}
.video-sub{font-size:11px;color:var(--t3);font-weight:300}

.music-hero{background:linear-gradient(135deg,rgba(229,160,13,0.15) 0%,rgba(229,160,13,0.03) 100%);border:1px solid rgba(229,160,13,0.15);border-radius:12px;padding:36px 40px;margin-bottom:40px;display:flex;align-items:center;gap:32px}
.music-hero-icon{font-size:64px;opacity:.8}
.music-hero-title{font-family:'Bebas Neue',sans-serif;font-size:36px;letter-spacing:2px;margin-bottom:6px}
.music-hero-sub{font-size:14px;color:var(--t2);font-weight:300}

.wl-empty{padding:80px 52px;text-align:center}
.wl-empty-icon{font-size:48px;margin-bottom:14px;opacity:.18}
.wl-empty-txt{font-size:15px;color:var(--t3);font-weight:300}

.sec-all:hover{opacity:.6}

/* === BACK BUTTON === */
.tab-back-btn {
  position: fixed;
  top: 18px; /* sits nicely inside nav zone */
  left: 24px;
  z-index: 400; /* above nav (nav is 300) */
  background: rgba(0,0,0,0.65);
  color: white;
  border: 1px solid rgba(255,255,255,0.25);
  border-radius: 10px;
  padding: 8px 12px; /* slightly tighter */
  font-size: 13px;
  cursor: pointer;
  backdrop-filter: blur(8px);
}


.startup-screen {
  min-height: 100vh;
  background:
    radial-gradient(circle at center, rgba(30, 80, 120, 0.18), transparent 42%),
    linear-gradient(180deg, #050608 0%, #090b10 55%, #020203 100%);
  color: #d8f6ff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Courier New", monospace;
  overflow: hidden;
}

.startup-screen::before {
  content: "";
  position: fixed;
  inset: 0;
  pointer-events: none;
  background: repeating-linear-gradient(
    to bottom,
    rgba(255,255,255,0.035) 0px,
    rgba(255,255,255,0.035) 1px,
    transparent 2px,
    transparent 5px
  );
  opacity: 0.35;
}

.startup-box {
  width: min(560px, calc(100vw - 40px));
  border: 1px solid rgba(160, 230, 255, 0.35);
  background: rgba(0, 8, 16, 0.82);
  box-shadow: 0 0 40px rgba(0, 160, 255, 0.16), inset 0 0 24px rgba(255,255,255,0.035);
  border-radius: 14px;
  padding: 28px;
}

.startup-title {
  font-size: 34px;
  letter-spacing: 6px;
  font-weight: 900;
  color: #ffffff;
  text-shadow: 0 0 18px rgba(80, 190, 255, 0.7);
}

.startup-sub {
  margin-top: 6px;
  font-size: 12px;
  letter-spacing: 3px;
  color: rgba(216, 246, 255, 0.7);
}

.startup-terminal {
  margin-top: 28px;
  min-height: 152px;
  padding: 18px;
  border-radius: 10px;
  background: rgba(0,0,0,0.42);
  border: 1px solid rgba(255,255,255,0.08);
}

.startup-line {
  font-size: 14px;
  line-height: 1.7;
  color: rgba(216, 246, 255, 0.62);
}

.startup-line.active {
  color: #ffffff;
}

.startup-progress {
  margin-top: 22px;
  height: 10px;
  border-radius: 999px;
  overflow: hidden;
  background: rgba(255,255,255,0.12);
}

.startup-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #7ee7ff, #ffffff);
  box-shadow: 0 0 16px rgba(126, 231, 255, 0.65);
  transition: width 420ms ease;
}

.startup-foot {
  margin-top: 12px;
  font-size: 11px;
  letter-spacing: 2px;
  color: rgba(216, 246, 255, 0.68);
}

.tab-back-btn:hover {
  background: rgba(255,255,255,0.12);
}


/* NAPFLIX FOCUS FINAL OVERRIDE */
.card.napflix-tv-focused,
.feat-card.napflix-tv-focused,
.rel-card.napflix-tv-focused,
.video-card.napflix-tv-focused,
.album-card.napflix-tv-focused,
.season-card.napflix-tv-focused,
.ep-card.napflix-tv-focused,
.extra-card.napflix-tv-focused {
  outline: none !important;
  transform: scale(1.055) translateY(-3px) !important;
  z-index: 999999 !important;
  position: relative !important;
  border-radius: 12px !important;
  box-shadow:
    0 0 0 4px #ffd54a,
    0 0 38px rgba(255,213,74,0.75),
    0 36px 90px rgba(0,0,0,0.95) !important;
}

.row,
.featured-row,
.rel-row-h,
.seasons-row,
.cast-row {
  overflow: visible !important;
  padding-top: 18px !important;
  padding-bottom: 34px !important;
}

.row-wrap,
.featured-bar,
.sec {
  overflow: visible !important;
}

\n
/* Hide See All links in Pi/Napflix kiosk mode */
html.napflix-kiosk .sec-all {
  display: none !important;
}
\n

/* NAPFLIX ROW SCROLL FIX */
.row,
.featured-row,
.rel-row-h,
.seasons-row,
.cast-row {
  overflow-x: auto !important;
  overflow-y: visible !important;
  scroll-behavior: smooth !important;
  padding-top: 18px !important;
  padding-bottom: 36px !important;
}

.row-wrap,
.featured-bar,
.sec {
  overflow: visible !important;
}



/* NAPFLIX FINAL KIOSK POLISH */
html.napflix-kiosk .row,
html.napflix-kiosk .featured-row,
html.napflix-kiosk .rel-row-h,
html.napflix-kiosk .seasons-row,
html.napflix-kiosk .cast-row {
  overflow-x: auto !important;
  overflow-y: visible !important;
  padding-top: 42px !important;
  padding-bottom: 46px !important;
  scroll-behavior: smooth !important;
}

html.napflix-kiosk .row-wrap,
html.napflix-kiosk .featured-bar,
html.napflix-kiosk .sec,
html.napflix-kiosk .section {
  overflow: visible !important;
}

html.napflix-kiosk .napflix-tv-focused {
  box-shadow:
    0 0 0 4px #ffd54a,
    0 0 24px rgba(255,213,74,0.58),
    0 22px 60px rgba(0,0,0,0.85) !important;
  transform: scale(1.055) translateY(-3px) !important;
}



/* NAPFLIX KIOSK EDGE + DETAIL FOCUS FIX */
html.napflix-kiosk .row,
html.napflix-kiosk .featured-row,
html.napflix-kiosk .rel-row-h,
html.napflix-kiosk .seasons-row,
html.napflix-kiosk .cast-row {
  padding-left: 18px !important;
  padding-right: 18px !important;
  scroll-padding-left: 72px !important;
  scroll-padding-right: 72px !important;
}

html.napflix-kiosk .row-wrap {
  overflow: visible !important;
}



/* NAPFLIX KIOSK AUDIT FIXES */
html.napflix-kiosk .row,
html.napflix-kiosk .featured-row,
html.napflix-kiosk .rel-row-h {
  padding-top: 52px !important;
  padding-bottom: 72px !important;
}

html.napflix-kiosk .detail-page {
  scroll-padding-top: 120px !important;
  scroll-padding-bottom: 160px !important;
}

html.napflix-kiosk .btn-play.napflix-tv-focused,
html.napflix-kiosk .btn-trailer.napflix-tv-focused,
html.napflix-kiosk .btn-wl.napflix-tv-focused {
  transform: scale(1.06) translateY(0) !important;
}



/* NAPFLIX KIOSK ROW DENSITY + DETAIL TOP FIX */
html.napflix-kiosk .row,
html.napflix-kiosk .featured-row,
html.napflix-kiosk .rel-row-h {
  padding-top: 26px !important;
  padding-bottom: 34px !important;
  padding-left: 36px !important;
  padding-right: 36px !important;
  scroll-padding-left: 96px !important;
  scroll-padding-right: 96px !important;
}

html.napflix-kiosk .sec {
  margin-bottom: 4px !important;
  padding-bottom: 0 !important;
}

html.napflix-kiosk .sec-title,
html.napflix-kiosk .section-title,
html.napflix-kiosk .row-title {
  margin-bottom: 8px !important;
}

html.napflix-kiosk .row-wrap {
  overflow: visible !important;
}

html.napflix-kiosk .detail-page {
  scroll-padding-top: 0 !important;
}



/* NAPFLIX KIOSK TIGHTER ROW SPACING */
html.napflix-kiosk .sec {
  margin-top: 10px !important;
  margin-bottom: -10px !important;
  padding-bottom: 0 !important;
}

html.napflix-kiosk .sec-head {
  margin-bottom: -6px !important;
}

html.napflix-kiosk .sec-title,
html.napflix-kiosk .section-title,
html.napflix-kiosk .row-title {
  margin-bottom: 0 !important;
  padding-bottom: 0 !important;
}

html.napflix-kiosk .row,
html.napflix-kiosk .featured-row,
html.napflix-kiosk .rel-row-h {
  padding-top: 14px !important;
  padding-bottom: 18px !important;
}

html.napflix-kiosk .row-wrap {
  margin-bottom: -8px !important;
}



/* NAPFLIX KIOSK BRAND/EDGE FOCUS FIX */
html.napflix-kiosk .lib-sub-tabs {
  overflow: visible !important;
  padding-top: 18px !important;
  padding-bottom: 24px !important;
}

html.napflix-kiosk .lib-sub-tab {
  scroll-margin-top: 80px !important;
}

html.napflix-kiosk .row,
html.napflix-kiosk .featured-row,
html.napflix-kiosk .rel-row-h,
html.napflix-kiosk .seasons-row,
html.napflix-kiosk .cast-row {
  padding-left: 48px !important;
  padding-right: 48px !important;
  scroll-padding-left: 110px !important;
  scroll-padding-right: 110px !important;
}

html.napflix-kiosk div[style*="overflow-x: auto"],
html.napflix-kiosk div[style*="overflowX: auto"] {
  overflow-y: visible !important;
  padding-top: 28px !important;
  padding-left: 38px !important;
  padding-right: 38px !important;
}

html.napflix-kiosk button.napflix-tv-focused {
  position: relative !important;
  z-index: 999999 !important;
  box-shadow:
    0 0 0 4px #ffd54a,
    0 0 24px rgba(255,213,74,0.58),
    0 18px 50px rgba(0,0,0,0.8) !important;
}

`;

const pi = (p, w = 300, h = null) => {
  if (!p) return null;
  if (p.startsWith("http")) return p;

  const params = new URLSearchParams({
    path: p,
    w: String(w),
    h: String(h || w),
  });

  return `/api/img?${params.toString()}`;
};

const cachedPi = (p, w = 300, h = null) => {
  if (!p) return null;
  if (p.startsWith("http")) return p;

  const params = new URLSearchParams({
    path: p,
    w: String(w),
    h: String(h || w),
  });

  return `/api/img?${params.toString()}`;
};

async function plex(path) {
  const p = path.replace(/[?&]+$/, "");
  const r = await fetch(`/api/plex?path=${encodeURIComponent(p)}`, {
    headers: { Accept: "application/json" },
  });
  const d = await r.json();
  return d.MediaContainer;
}

function shuffle(a) {
  const arr = [...a];

  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }

  return arr;
}
function is4K(i) { return i.Media?.some(m => m.videoResolution === "4k" || m.videoResolution === "2160"); }
function isAtmos(i) { return i.Media?.some(m => m.Part?.some(p => p.Stream?.some(s => s.displayTitle?.toLowerCase().includes("atmos")))); }
function isHDR(i) { return i.Media?.some(m => m.Part?.some(p => p.Stream?.some(s => s.colorTransfer?.includes("smpte2084")))); }
function isDV(i) { return i.Media?.some(m => m.Part?.some(p => p.Stream?.some(s => s.DOVIPresent))); }
function isCrit(i) {
  const s = (i.studio || "").toLowerCase();
  const label = (i.Label || []).some(l => l.tag?.toLowerCase().includes("criterion"));
  const coll = (i.Collection || []).some(c => c.tag?.toLowerCase().includes("criterion"));
  return s.includes("criterion") || label || coll;
}

function getHeroTitleSize(title) {
  const len = title?.length || 0;
  if (len > 40) return "clamp(28px,4vw,52px)";
  if (len > 30) return "clamp(36px,5.5vw,68px)";
  if (len > 20) return "clamp(48px,7.5vw,92px)";
  return "clamp(60px,10vw,116px)";
}

function getSmartRecs(item, pool, limit = 18) {
  const genres = new Set(item.Genre?.map(g => g.tag) || []);
  const type = item.type;
  return pool
    .filter(m => m.ratingKey !== item.ratingKey && m.type === type)
    .map(m => {
      let score = 0;
      m.Genre?.forEach(g => { if (genres.has(g.tag)) score += 3; });
      if (m.studio === item.studio) score += 2;
      if (Math.abs((m.year || 0) - (item.year || 0)) < 15) score += 1;
      if (m.rating) score += parseFloat(m.rating) * 0.3;
      return { item: m, score };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(x => x.item);
}

function Badges({ item }) {
  const logo = getStudioLogo(item.studio);
  const rt = getRTInfo(item);
  const imdb = getIMDbRating(item);
  return (
    <div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center" }}>
      {logo && <span className="studio-badge" style={{ background: logo.bg, color: logo.color, border: "1px solid rgba(255,255,255,0.15)" }}>{logo.text}</span>}
      {is4K(item) && <span className="badge b4k">4K</span>}
      {is4K(item) && <span className="badge buhd">UHD</span>}
      {isDV(item) && <span className="badge bdv">Dolby Vision</span>}
      {isAtmos(item) && <span className="badge batmos">Atmos</span>}
      {isHDR(item) && !isDV(item) && <span className="badge bhdr">HDR</span>}
      {isCrit(item) && <span className="badge bcrit">Criterion</span>}
      {item.contentRating && <span className="badge brated">{item.contentRating}</span>}
      {rt && <span className={`badge ${rt.label.includes("Rotten") ? "brt-rotten" : "brt-fresh"}`}>{rt.icon} {rt.score ? `${rt.score}%` : rt.label}</span>}
      {imdb && <span className="badge bimdb">★ {imdb} IMDb</span>}
    </div>
  );
}

const CardImg = React.memo(function CardImg({ src, blurSrc, alt }) {
  const [e, setE] = useState(false);
  const [visible, setVisible] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    setVisible(false);
    setLoaded(false);
    setE(false);

    if (!src) return;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { rootMargin: "1600px" }
    );

    if (ref.current) obs.observe(ref.current);

    return () => obs.disconnect();
  }, [src]);

  if (!src || e) {
    return <div className="card-ph">{alt}</div>;
  }

  return (
  <div ref={ref} className="img-shell">
    {!loaded && blurSrc && (
      <img
        decoding="async"
        src={blurSrc}
        alt=""
        aria-hidden="true"
        className="img-blur"
      />
    )}

    {!loaded && !blurSrc && (
      <div className="img-placeholder">
        <span>{alt}</span>
      </div>
    )}

    {visible && (
      <img
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        fetchPriority="low"
        onLoad={() => setLoaded(true)}
        onError={() => setE(true)}
        className={loaded ? "img-loaded" : "img-loading"}
      />
    )}
  </div>
);
});

const MediaCard = React.memo(function MediaCard({ item, wide, onClick }) {
  const src = wide
  ? pi(item.art || item.thumb, 420, 236)
  : pi(item.thumb, 300, 450);
  return (
    <div className={`card${wide ? " card-wide" : ""}`} onClick={() => onClick?.(item)}>
      <CardImg src={src} alt={item.title} />
      <div className="card-play"><svg width="14" height="14" viewBox="0 0 16 16" fill="#000"><path d="M4 2l10 6-10 6V2z" /></svg></div>
      <div className="card-shade" />
      <div className="card-foot"><div className="card-n">{item.title}</div>{item.year && <div className="card-y">{item.year}</div>}</div>
    </div>
  );
});


const VirtualMediaGrid = React.memo(function VirtualMediaGrid({ items, onCard }) {
  const CARD_WIDTH = 212;
  const CARD_HEIGHT = 318;

  if (!items?.length) return null;

  return (
    <div style={{ height: "72vh", padding: "0 52px" }}>
      <AutoSizer>
        {({ height, width }) => {
          const columnCount = Math.max(1, Math.floor(width / CARD_WIDTH));
          const rowCount = Math.ceil(items.length / columnCount);

          return (
            <VirtualGrid
              columnCount={columnCount}
              columnWidth={CARD_WIDTH}
              height={height}
              rowCount={rowCount}
              rowHeight={CARD_HEIGHT}
              width={width}
              overscanRowCount={2}
            >
              {({ columnIndex, rowIndex, style }) => {
                const index = rowIndex * columnCount + columnIndex;
                const item = items[index];

                if (!item) return null;

                return (
                  <div style={{ ...style, padding: 6 }}>
                    <MediaCard item={item} onClick={onCard} />
                  </div>
                );
              }}
            </VirtualGrid>
          );
        }}
      </AutoSizer>
    </div>
  );
});

const Row = React.memo(function Row({ title, sub, items, wide, onCard }) {
  const [expanded, setExpanded] = useState(false);
  if (!items?.length) return null;
  if (expanded) {
    return (
      <div className="sec">
        <div className="sec-head">
          <div className="sec-left"><div className="sec-bar" /><div><div className="sec-title">{title}</div>{sub && <div className="sec-sub">{sub}</div>}</div></div>
          <button className="sec-all" onClick={() => setExpanded(false)}>← Back</button>
        </div>
        <div className="agrid">
          {items.map(i => (
            <MediaCard key={i.ratingKey} item={i} onClick={onCard} />
          ))}
        </div>
        <div style={{ height: 24 }} />
      </div>
    );
  }
  return (
    <div className="sec">
      <div className="sec-head">
        <div className="sec-left"><div className="sec-bar" /><div><div className="sec-title">{title}</div>{sub && <div className="sec-sub">{sub}</div>}</div></div>
        <button className="sec-all" onClick={() => setExpanded(true)}>See All →</button>
      </div>
      <div className="row-wrap">
        <div className="row">
          {items.slice(0, 18).map(i => (
            <MediaCard key={i.ratingKey} item={i} wide={wide} onClick={onCard} />
          ))}
        </div>
      </div>
    </div>
  );
});

// ── SCROLL SCRUBBER ───────────────────────────────────────────────────────────
function ScrollScrubber() {
  const [pct, setPct] = useState(0);
  const [thumbH, setThumbH] = useState(40);
  const trackRef = useRef(null);
  const dragging = useRef(false);

  useEffect(() => {
    const update = () => {
      const sh = document.documentElement.scrollHeight - window.innerHeight;
      if (sh <= 0) return;
      setPct(window.scrollY / sh);
      setThumbH(Math.max(20, (window.innerHeight / document.documentElement.scrollHeight) * window.innerHeight * 0.55));
    };
    window.addEventListener("scroll", update, { passive: true });
    update();
    return () => window.removeEventListener("scroll", update);
  }, []);

  const seek = (e) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect) return;
    const p = Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height));
    window.scrollTo({ top: p * (document.documentElement.scrollHeight - window.innerHeight), behavior: "auto" });
  };

  return (
    <div ref={trackRef} className="scroll-scrubber"
      onMouseDown={e => { dragging.current = true; seek(e); }}
      onMouseMove={e => { if (dragging.current) seek(e); }}
      onMouseUp={() => { dragging.current = false; }}
      onMouseLeave={() => { dragging.current = false; }}
      onClick={seek}>
      <div className="scroll-scrubber-thumb" style={{ top: `${pct * (100 - (thumbH / (window.innerHeight * 0.55)) * 100)}%`, height: thumbH }} />
    </div>
  );
}

// ── ALPHA JUMP BAR ────────────────────────────────────────────────────────────
function AlphaJumpBar({ items, visible, onJump }) {
  const letters = ["#", ...Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i))];
  const has = new Set(items.map(m => { const c = m.title?.[0]?.toUpperCase(); return /[A-Z]/.test(c) ? c : "#"; }));
  return (
    <div className={`alpha-jump${visible ? "" : " hidden"}`}>
      {letters.map(l => (
        <button key={l} className={`aj-btn${has.has(l) ? " has" : ""}`} onClick={() => onJump(l)}>{l}</button>
      ))}
    </div>
  );
}

// ── VIDEO PLAYER ─────────────────────────────────────────────────────────────
function playInPlex(item) {
  const url = `https://app.plex.tv/desktop/#!/server/3303ac5dc6e17a0e4f990ed50d114968139b72c9/details?key=%2Flibrary%2Fmetadata%2F${item.ratingKey}&context=hub`;
  window.open(url, "_blank");
}

function VideoPlayer({ item, onClose }) {
  const videoRef = useRef(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    const handleKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://cdn.jsdelivr.net/npm/hls.js@1.4.12/dist/hls.min.js";
    script.onload = () => {
      const streamUrl = `/pjtv/play/${item.ratingKey}`;
      if (window.Hls && window.Hls.isSupported() && videoRef.current) {
        const hls = new window.Hls({
        });
        hls.loadSource(streamUrl);
        hls.attachMedia(videoRef.current);
        hls.on(window.Hls.Events.MANIFEST_PARSED, () => { setStatus("playing"); videoRef.current.play().catch(() => {}); });
        hls.on(window.Hls.Events.FRAG_LOADED, () => { if (status === "loading") setStatus("playing"); });
        hls.on(window.Hls.Events.ERROR, (event, data) => { if (data.fatal) setStatus("error"); });
        return () => hls.destroy();
      } else if (videoRef.current?.canPlayType("application/vnd.apple.mpegurl")) {
        videoRef.current.src = streamUrl;
        videoRef.current.play().catch(() => {});
        setStatus("playing");
      } else {
        setStatus("error");
      }
    };
    script.onerror = () => setStatus("error");
    document.head.appendChild(script);
    return () => { if (script.parentNode) document.head.removeChild(script); };
  }, [item.ratingKey]);

  return (
    <div className="player-overlay">
      {status === "error" ? (
        <div className="player-error">
          <div className="player-error-title">Cannot stream this file</div>
          <div className="player-error-msg">The video could not be transcoded. Open in Plex to watch.</div>
          <button className="btn-play" onClick={() => { playInPlex(item); onClose(); }}>Open in Plex</button>
          <button className="btn-more" onClick={onClose} style={{ marginTop: 8 }}>Go Back</button>
        </div>
      ) : (
        <video ref={videoRef} className="player-video" controls style={{ display: status === "error" ? "none" : "block", opacity: status === "loading" ? 0 : 1 }} />
      )}
      {status === "loading" && (
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 16 }}>
          <div className="spin" />
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", letterSpacing: 2 }}>LOADING STREAM...</div>
        </div>
      )}
    </div>
  );
}

// ── DETAIL PAGE ──────────────────────────────────────────────────────────────
function getEditionText(item) {
  return [
    item.editionTitle,
    item.edition,
    item.Edition?.map((e) => e.tag).join(" "),
    item.Label?.map((l) => l.tag).join(" "),
    item.Collection?.map((c) => c.tag).join(" "),
  ]
    .filter(Boolean)
    .join(" ");
}

function isCriterionEdition(item) {
  return getEditionText(item).toLowerCase().includes("criterion");
}
function DetailPage({ item, allMovies, allShows, onBack, onCard, onPersonSearch }) {
  const [det, setDet] = useState(null);
  const [providers, setProviders] = useState([]);
  const [tmdbBrandLogo, setTmdbBrandLogo] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [episodes, setEpisodes] = useState([]);
  const [extras, setExtras] = useState([]);
  const [selSeason, setSelSeason] = useState(null);
  const [wl, setWl] = useState(false);
  const [playItem, setPlayItem] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    window.scrollTo(0, 0);
    setDet(null); setSeasons([]); setEpisodes([]); setExtras([]); setSelSeason(null);
    const saved = JSON.parse(localStorage.getItem("wl") || "[]");
    setWl(saved.some(x => x.ratingKey === item.ratingKey));
    plex(`/library/metadata/${item.ratingKey}`).then(d => setDet(d?.Metadata?.[0] || null)).catch(() => {});
    if (item.type === "show") {
      plex(`/library/metadata/${item.ratingKey}/children`).then(d => setSeasons(d?.Metadata || [])).catch(() => {});
    }
    plex(`/library/metadata/${item.ratingKey}/extras`).then(d => setExtras(d?.Metadata || [])).catch(() => {});
    }, [item.ratingKey]);

  useEffect(() => {
    let cancelled = false;

    async function loadProviders() {
      setProviders([]);
      setTmdbBrandLogo(null);

      const title = item.title;
      const year = item.year;
      const mediaType = item.type === "show" ? "tv" : "movie";

      const tmdbMatch =
        mediaType === "tv"
          ? await searchTMDBTV(title, year)
          : await searchTMDBMovie(title, year);

      if (!tmdbMatch?.id) return;

      const brandLogo = await getTMDBBrandLogo(tmdbMatch.id, mediaType);

      if (!cancelled) {
        setTmdbBrandLogo(brandLogo);
      }

      const foundProviders = await getTMDBWatchProviders(
        tmdbMatch.id,
        mediaType,
        "US"
      );

      if (!cancelled) {
        setProviders(foundProviders);
      }
    }

    loadProviders();

    return () => {
      cancelled = true;
    };
  }, [item.title, item.year, item.type, item.ratingKey]);

  const loadEps = async (season) => {
    if (selSeason === season.ratingKey) { setSelSeason(null); setEpisodes([]); return; }
    setSelSeason(season.ratingKey);
    try { const d = await plex(`/library/metadata/${season.ratingKey}/children`); setEpisodes(d?.Metadata || []); } catch {}
  };

  const toggleWl = () => {
    const saved = JSON.parse(localStorage.getItem("wl") || "[]");
    const next = saved.some(x => x.ratingKey === item.ratingKey)
      ? saved.filter(x => x.ratingKey !== item.ratingKey)
      : [...saved, { ratingKey: item.ratingKey, title: item.title, thumb: item.thumb, year: item.year, type: item.type }];
    localStorage.setItem("wl", JSON.stringify(next));
    setWl(!wl);
  };

  if (playItem) return <VideoPlayer item={playItem} onClose={() => setPlayItem(null)} />;

  const d = det || item;const all = [...allMovies, ...allShows];
  const artSrc = pi(d.art, 1280);
  const posterSrc = pi(d.thumb, 300);
  const rt = getRTInfo(d);
  const imdb = getIMDbRating(d);
  const dur = d.duration ? `${Math.floor(d.duration / 3600000)}h ${Math.round((d.duration % 3600000) / 60000)}m` : null;
  const directors = d.Director || [];
  const writers = d.Writer || [];
  const cast = d.Role || [];
  const studio = d.studio || "";
  const editionText = getEditionText(d);
  const isCriterion = isCriterionEdition(d);

  const network = item.type === "show" ? getNetworkName(d) : "";

const logo = isCriterion
  ? getStudioLogo("Criterion Collection")
  : item.type === "show"
    ? getNetworkLogo(network)
    : getStudioLogo(studio);

const logoLabel = isCriterion
  ? "Criterion Collection"
  : item.type === "show"
    ? network
    : studio;
  const related = getSmartRecs(d, all);
  const actorRows = cast.slice(0, 4).map(actor => ({
    name: actor.tag,
    items: shuffle(all.filter(m => m.ratingKey !== item.ratingKey && m.Role?.some(r => r.tag === actor.tag))).slice(0, 18)
  })).filter(a => a.items.length > 1);
  const dirRows = directors.slice(0, 2).map(dir => ({
    name: dir.tag,
    items: shuffle(all.filter(m => m.ratingKey !== item.ratingKey && m.Director?.some(dd => dd.tag === dir.tag))).slice(0, 18)
  })).filter(a => a.items.length > 1);

  return (
    <div className="detail-page">
      <button className="detail-back" onClick={onBack}>← Back</button>
      <div className="detail-hero">
        <div className="detail-art" style={{ backgroundImage: artSrc ? `url(${artSrc})` : "none" }} />
        <div className="detail-content">
          {posterSrc && <div className="detail-poster"><img loading="lazy" decoding="async" src={posterSrc} alt={d.title} onError={e => e.target.style.display = "none"} /></div>}
          {logo && (
  <div className="detail-studio">
    <span
      className="studio-badge"
      title={logoLabel}
      style={{
        background: logo.bg,
        color: logo.color,
        border: "1px solid rgba(255,255,255,0.15)",
        fontSize: 11,
        padding: "3px 10px",
      }}
    >
      {logo.text}
    </span>
  </div>
)}
          <div className="detail-title">{d.title}</div>
          <div className="detail-meta">
            {d.year && <span className="dmi">{d.year}</span>}
            {dur && <><div className="ddiv" /><span className="dmi">{dur}</span></>}
            {item.type === "show" && d.childCount && <><div className="ddiv" /><span className="dmi">{d.childCount} Season{d.childCount > 1 ? "s" : ""}</span></>}
            {d.contentRating && <><div className="ddiv" /><span className="dmi">{d.contentRating}</span></>}
          </div>
          <div
  className="detail-badges"
  style={{
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  }}
>
  {tmdbBrandLogo?.logo && (
    <div
      title={tmdbBrandLogo.name}
      style={{
        height: 22,
        minWidth: 42,
        padding: "3px 7px",
        borderRadius: 4,
        background: "rgba(255,255,255,0.92)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <img
        decoding="async"
        src={tmdbBrandLogo.logo}
        alt={tmdbBrandLogo.name}
        style={{
          maxHeight: 15,
          maxWidth: 70,
          objectFit: "contain",
        }}
      />
    </div>
  )}

  <Badges item={d} />
</div>

          {providers.length > 0 && (
  <div
    className="provider-row"
    style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      marginTop: 12,
      marginBottom: 10,
      flexWrap: "wrap",
    }}
  >
    <span
      style={{
        fontSize: 11,
        opacity: 0.65,
        textTransform: "uppercase",
        letterSpacing: 1.2,
        marginRight: 4,
      }}
    >
      Available On
    </span>

    {providers.slice(0, 6).map((provider) => (
      <img
        decoding="async"
        key={provider.id}
        src={provider.logo}
        alt={provider.name}
        title={provider.name}
        style={{
          width: 34,
          height: 34,
          borderRadius: 8,
          objectFit: "cover",
          boxShadow: "0 4px 14px rgba(0,0,0,0.35)",
        }}
      />
    ))}
  </div>
)}
          {d.summary && <div className="detail-summary-short">{d.summary}</div>}
          {directors.length > 0 && <div className="detail-credits">Directed by {directors.map((dir, i) => <span key={i}><b onClick={() => onPersonSearch(dir.tag)}>{dir.tag}</b>{i < directors.length - 1 ? ", " : ""}</span>)}</div>}
          <div className="detail-btns">
            <button className="btn-play" onClick={async () => {
              const itemType = d.type || item.type;
              const hasMedia = d.Media && d.Media.length > 0;
              if (itemType === 'show' || (!hasMedia && (d.childCount || d.leafCount))) {
                try {
                  const seasonsData = await plex('/library/metadata/' + d.ratingKey + '/children');
                  const firstSeason = seasonsData?.Metadata?.find(s => s.type === 'season');
                  if (firstSeason) {
                    const epsData = await plex('/library/metadata/' + firstSeason.ratingKey + '/children');
                    const firstEp = epsData?.Metadata?.find(e => e.type === 'episode');
                    if (firstEp) { setPlayItem(firstEp); return; }
                  }
                } catch (e) { console.error('Error:', e); }
              }
              setPlayItem(d);
            }}>▶ Play</button>
            <button className="btn-trailer" onClick={() => {
              const trailer = extras.find(e => e.subtype === "trailer" || e.type === "clip");
              if (trailer) setPlayItem(trailer);
              else window.open(`https://www.youtube.com/results?search_query=${encodeURIComponent(d.title + " official trailer")}`, "_blank");
            }}>🎬 Trailer</button>
            <button className={`btn-wl${wl ? " on" : ""}`} onClick={toggleWl}>{wl ? "✓" : "+"}</button>
          </div>
        </div>
      </div>

      <div className="detail-body">
        <div className="detail-info-grid">
          {d.Genre && d.Genre.length > 0 && (
            <div className="detail-row">
              <div className="detail-lbl">Genre</div>
              <div className="detail-val"><div className="detail-genre-pills">{d.Genre.map(g => <span key={g.tag} className="detail-genre-pill">{g.tag}</span>)}</div></div>
            </div>
          )}
          {studio && (
            <div className="detail-row">
              <div className="detail-lbl">Studio</div>
              <div className="detail-val" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {logo && <span className="studio-badge" style={{ background: logo.bg, color: logo.color, border: "1px solid rgba(255,255,255,0.15)", fontSize: 10, padding: "2px 8px" }}>{logo.text}</span>}
                <span style={{ cursor: "default" }}>{studio}</span>
              </div>
            </div>
          )}
          {directors.length > 0 && <div className="detail-row"><div className="detail-lbl">Director</div><div className="detail-val">{directors.map((dir, i) => <span key={i} onClick={() => onPersonSearch(dir.tag)}>{dir.tag}{i < directors.length - 1 ? ", " : ""}</span>)}</div></div>}
          {writers.length > 0 && <div className="detail-row"><div className="detail-lbl">Written by</div><div className="detail-val">{writers.map((w, i) => <span key={i} onClick={() => onPersonSearch(w.tag)}>{w.tag}{i < writers.length - 1 ? ", " : ""}</span>)}</div></div>}
          {d.year && <div className="detail-row"><div className="detail-lbl">Year</div><div className="detail-val">{d.year}</div></div>}
          {dur && <div className="detail-row"><div className="detail-lbl">Runtime</div><div className="detail-val">{dur}</div></div>}
          {rt && <div className="detail-row"><div className="detail-lbl">Rotten Tomatoes</div><div className="detail-val" style={{ color: rt.label.includes("Rotten") ? "#9b9b9b" : "#fa320a" }}>{rt.icon} {rt.label}{rt.score ? ` — ${rt.score}%` : ""}</div></div>}
          {imdb && <div className="detail-row"><div className="detail-lbl">IMDb Rating</div><div className="detail-val" style={{ color: "#f5c518" }}>★ {imdb} / 10</div></div>}
        </div>

        {seasons.length > 0 && (
          <div className="detail-section">
            <div className="detail-sec-title">Seasons</div>
            <div className="seasons-row">
              {seasons.map(s => (
                <div key={s.ratingKey} className={`season-card${selSeason === s.ratingKey ? " on" : ""}`} onClick={() => loadEps(s)}>
                  <div className="season-poster">{s.thumb ? <img loading="lazy" decoding="async" src={pi(s.thumb, 150)} alt={s.title} onError={e => e.target.style.display = "none"} /> : <div className="card-ph">{s.title}</div>}</div>
                  <div className="season-n">{s.title}</div>
                  <div className="season-ep">{s.leafCount} episodes</div>
                </div>
              ))}
            </div>
            {selSeason && episodes.length > 0 && (
              <div className="episodes-grid">
                {episodes.map(ep => (
                  <div key={ep.ratingKey} className="ep-card" onClick={() => setPlayItem(ep)}>
                    <div className="ep-thumb">
                      {ep.thumb ? <img loading="lazy" decoding="async" src={pi(ep.thumb, 200)} alt={ep.title} onError={e => e.target.style.display = "none"} /> : <div style={{ width: "100%", height: "100%", background: "var(--s3)" }} />}
                      <div className="ep-play"><div style={{ width: 44, height: 44, borderRadius: "50%", background: "rgba(229,160,13,0.85)", display: "flex", alignItems: "center", justifyContent: "center" }}><svg width="14" height="14" viewBox="0 0 16 16" fill="#000"><path d="M4 2l10 6-10 6V2z" /></svg></div></div>
                    </div>
                    <div className="ep-body">
                      <div className="ep-meta">E{ep.index}{ep.duration ? ` · ${Math.round(ep.duration / 60000)}m` : ""}</div>
                      <div className="ep-title">{ep.title}</div>
                      {ep.summary && <div className="ep-desc">{ep.summary}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {extras.length > 0 && (
          <div className="detail-section">
            <div className="detail-sec-title">Extras & Trailers</div>
            <div className="extras-grid">
              {extras.map(ex => (
                <div key={ex.ratingKey} className="extra-card" onClick={() => setPlayItem(ex)}>
                  <div className="extra-thumb">
                    {ex.thumb ? <img loading="lazy" decoding="async" src={pi(ex.thumb, 200)} alt={ex.title} onError={e => e.target.style.display = "none"} /> : <div style={{ width: "100%", height: "100%", background: "var(--s3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28 }}>🎬</div>}
                  </div>
                  <div className="extra-body"><div className="extra-title">{ex.title}</div><div className="extra-type">{ex.subtype || ex.type || "Extra"}</div></div>
                </div>
              ))}
            </div>
          </div>
        )}

        {cast.length > 0 && (
          <div className="detail-section">
            <div className="detail-sec-title">Cast & Crew</div>
            <div className="cast-row">
              {cast.map((r, i) => (
                <div key={i} className="cast-card" onClick={() => onPersonSearch(r.tag)}>
                  <div className="castavatar">
                    {r.thumb ? <img loading="lazy" decoding="async" src={pi(r.thumb, 100)} alt={r.tag} onError={e => e.target.style.display = "none"} /> : <div style={{ width: "100%", height: "100%", background: "var(--s2)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--t3)", fontSize: 24 }}>{r.tag?.[0]}</div>}
                  </div>
                  <div className="cast-n">{r.tag}</div>
                  {r.role && <div className="cast-r">{r.role}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {related.length > 0 && (
          <div className="detail-section">
            <div className="detail-sec-title">More Like This</div>
            <div className="rel-row-h">{related.map(r => <div key={r.ratingKey} className="rel-card" onClick={() => onCard(r)}><div className="rel-thumb"><CardImg src={pi(r.thumb, 150)} alt={r.title} /></div><div className="rel-n">{r.title}</div>{r.year && <div className="rel-y">{r.year}</div>}</div>)}</div>
          </div>
        )}

        {actorRows.map(actor => (
          <div key={actor.name} className="detail-section">
            <div className="detail-sec-title">More with {actor.name}</div>
            <div className="rel-row-h">{actor.items.map(r => <div key={r.ratingKey} className="rel-card" onClick={() => onCard(r)}><div className="rel-thumb"><CardImg src={pi(r.thumb, 150)} alt={r.title} /></div><div className="rel-n">{r.title}</div>{r.year && <div className="rel-y">{r.year}</div>}</div>)}</div>
          </div>
        ))}

        {dirRows.map(dir => (
          <div key={dir.name} className="detail-section">
            <div className="detail-sec-title">More from {dir.name}</div>
            <div className="rel-row-h">{dir.items.map(r => <div key={r.ratingKey} className="rel-card" onClick={() => onCard(r)}><div className="rel-thumb"><CardImg src={pi(r.thumb, 150)} alt={r.title} /></div><div className="rel-n">{r.title}</div>{r.year && <div className="rel-y">{r.year}</div>}</div>)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── HERO ─────────────────────────────────────────────────────────────────────
function Hero({ items, onInfo }) {
  const [active, setActive] = useState(0);
  const [wl, setWl] = useState([]);
  useEffect(() => { setWl(JSON.parse(localStorage.getItem("wl") || "[]")); }, []);
  if (!items.length) return null;
  const item = items[active];
  const artSrc = pi(item.art || item.thumb, 1280);
  const inWl = wl.some(x => x.ratingKey === item.ratingKey);
  const dur = item.duration ? `${Math.floor(item.duration / 3600000)}h ${Math.round((item.duration % 3600000) / 60000)}m` : null;
  const directors = item.Director?.slice(0, 2).map(x => x.tag).join(", ");
  const type = item.type === "movie" ? "Film" : "Series";
  const titleSize = getHeroTitleSize(item.title);
  const toggleWl = () => {
    const saved = JSON.parse(localStorage.getItem("wl") || "[]");
    const next = saved.some(x => x.ratingKey === item.ratingKey) ? saved.filter(x => x.ratingKey !== item.ratingKey) : [...saved, { ratingKey: item.ratingKey, title: item.title, thumb: item.thumb, year: item.year, type: item.type }];
    localStorage.setItem("wl", JSON.stringify(next)); setWl(next);
  };
  return (
    <div className="hero">
      <div className="hero-bg" style={{ backgroundImage: artSrc ? `url(${artSrc})` : "none" }} />
      <div className="hero-main">
        <div className="hero-eyebrow">
          <div className="hero-tag">{type}</div>
          {item.Genre?.slice(0, 3).map(g => <div key={g.tag} className="hero-genre-pill">{g.tag}</div>)}
        </div>
        <div className="hero-title" style={{ fontSize: titleSize }}>{item.title}</div>
        <div className="hero-meta-row">
          {item.year && <span className="hmi">{item.year}</span>}
          {dur && <><div className="hdiv" /><span className="hmi">{dur}</span></>}
          {item.contentRating && <><div className="hdiv" /><span className="hmi">{item.contentRating}</span></>}
        </div>
        <div className="hero-badges-row"><Badges item={item} /></div>
        {directors && <div className="hero-credits">Directed by <b>{directors}</b></div>}
        {item.summary && <div className="hero-desc">{item.summary}</div>}
        <div className="hero-btns">
          <button className="btn-play" onClick={() => onInfo(item)}>▶ Play Now</button>
          <button className="btn-more" onClick={() => onInfo(item)}>ⓘ More Info</button>
          <button className={`btn-wl${inWl ? " on" : ""}`} onClick={toggleWl}>{inWl ? "✓" : "+"}</button>
        </div>
      </div>
      <div className="featured-bar">
        <div className="featured-bar-label">Featured</div>
        <div className="featured-row">
          {items.map((it, i) => (
            <div key={it.ratingKey} className={`feat-card${i === active ? " active" : ""}`} onClick={() => setActive(i)}>
              {it.thumb ? <img loading="lazy" decoding="async" src={pi(it.thumb, 130)} alt={it.title} onError={e => e.target.style.display = "none"} /> : <div className="card-ph">{it.title}</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


// ── HOME ─────────────────────────────────────────────────────────────────────

function sessionSeed() {
  const ROTATE_MS = 15 * 60 * 1000;
  const bucket = Math.floor(Date.now() / ROTATE_MS);
  return `pj-${bucket}`;
}

function seededShuffle(items, seedText = "") {
  const arr = [...items];

  let seed = 0;
  for (let i = 0; i < seedText.length; i++) {
    seed = (seed * 31 + seedText.charCodeAt(i)) >>> 0;
  }

  function random() {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 4294967296;
  }

  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }

  return arr;
}

function Home({ movies, shows, tmdbRows = [], onCard }) {
  const data = useRef(null);
  if (!data.current || data.current.movieCount !== movies.length) {
    const all = [...movies, ...shows];
    const seed = sessionSeed();
    const heroPool = seededShuffle(all.filter(m => m.art), `${seed}-hero`).slice(0, 18);
    const wlKeys = JSON.parse(localStorage.getItem("wl") || "[]").map(x => x.ratingKey);
    const wlItems = wlKeys.map(k => all.find(m => m.ratingKey === k)).filter(Boolean);
    const byG = (g, src) => shuffle(src.filter(m => m.Genre?.some(x => x.tag === g))).slice(0, 20);
    const byP = (name) => shuffle(all.filter(m => m.Director?.some(d => d.tag === name) || m.Role?.some(r => r.tag === name))).slice(0, 20);
    const DIRS = ["Steven Spielberg", "Martin Scorsese", "Christopher Nolan", "Quentin Tarantino", "Denis Villeneuve", "David Fincher", "Ridley Scott", "James Cameron", "Alfred Hitchcock", "Wes Anderson", "Stanley Kubrick", "Francis Ford Coppola"];
    const ACTS = ["Denzel Washington", "Tom Hanks", "Meryl Streep", "Morgan Freeman", "Leonardo DiCaprio", "Robert De Niro", "Al Pacino", "Cate Blanchett", "Viola Davis", "Brad Pitt", "Jodie Foster", "Gene Hackman"];
    const sDir = DIRS[Math.floor(Math.random() * DIRS.length)];
    const sAct = ACTS[Math.floor(Math.random() * ACTS.length)];
    data.current = {
      movieCount: movies.length, all, heroPool, wlItems, byG,
      trending: seededShuffle(all, `${seed}-trending`).slice(0, 20),
      fourK: shuffle(all.filter(is4K)).slice(0, 20),
      crit: shuffle(movies.filter(isCrit)).slice(0, 20),
      topR: [...movies].filter(m => m.rating).sort((a, b) => b.rating - a.rating).slice(0, 20),
      classics: shuffle(movies.filter(m => m.year && m.year < 1985)).slice(0, 20),
      newM: [...movies].sort((a, b) => (b.addedAt || 0) - (a.addedAt || 0)).slice(0, 20),
      newS: [...shows].sort((a, b) => (b.addedAt || 0) - (a.addedAt || 0)).slice(0, 20),
      wrestling: shuffle(all.filter(m => m.Genre?.some(g => ["Wrestling", "Sports", "Sport"].includes(g.tag)))).slice(0, 20),
      reality: shuffle(shows.filter(m => m.Genre?.some(g => g.tag === "Reality"))).slice(0, 20),
      docs: shuffle(all.filter(m => m.Genre?.some(g => g.tag === "Documentary"))).slice(0, 20),
      kids: shuffle(shows.filter(m => m.Genre?.some(g => ["Children", "Family", "Animation", "Kids"].includes(g.tag)))).slice(0, 20),
      dirI: byP(sDir), actI: byP(sAct), sDir, sAct,
    };
  }
  const { all, heroPool, wlItems, byG, fourK, crit, topR, classics, newM, newS, wrestling, reality, docs, kids, dirI, actI, sDir, sAct } = data.current;
  return (
    <>
      <Hero items={heroPool} onInfo={onCard} />
<div className="content">
  {tmdbRows.map((row) => {
      if (row.id === "growing-up-millennial") {
    const millennialItems = seededShuffle(
      all.filter((m) => {
        const year = Number(m.year || 0);

        return (
          year >= 1988 &&
          year <= 2006 &&
          (
            m.type === "show" ||
            m.Genre?.some((g) =>
              ["Comedy", "Drama", "Family", "Children", "Animation", "Romance"].includes(g.tag)
            )
          )
        );
      }),
      `${row.id}-${sessionSeed()}`
    ).slice(0, 20);

    return millennialItems.length > 3 ? (
      <Row
        key={row.id}
        title="Growing Up Millennial"
        items={millennialItems}
        onCard={onCard}
      />
    ) : null;
  }

  if (row.id === "essential-docuseries") {
    const docItems = seededShuffle(
      shows.filter((m) =>
        m.Genre?.some((g) =>
          ["Documentary", "Reality"].includes(g.tag)
        ) ||
        (m.summary || "").toLowerCase().includes("documentary")
      ),
      `${row.id}-${sessionSeed()}`
    ).slice(0, 20);

    return docItems.length > 3 ? (
      <Row
        key={row.id}
        title="Essential Docuseries"
        items={docItems}
        onCard={onCard}
      />
    ) : null;
  }

  if (row.id === "comfort-tv") {
    const comfortItems = seededShuffle(
      shows.filter((m) =>
        m.Genre?.some((g) =>
          ["Comedy", "Family", "Children", "Animation"].includes(g.tag)
        )
      ),
      `${row.id}-${sessionSeed()}`
    ).slice(0, 20);

    return comfortItems.length > 3 ? (
      <Row
        key={row.id}
        title="Comfort TV"
        items={comfortItems}
        onCard={onCard}
      />
    ) : null;
  }
  const plexMatchedItems = row.items
    .map((tmdbItem) => {
      const match = all.find((plexItem) => {
        const plexTitle = (plexItem.title || "").toLowerCase().trim();
        const tmdbTitle = (tmdbItem.title || "").toLowerCase().trim();
        const plexYear = String(plexItem.year || "");
        const tmdbYear = String(tmdbItem.year || "");

        return (
          plexTitle === tmdbTitle &&
          (!tmdbYear || !plexYear || plexYear === tmdbYear)
        );
      });

      return match || null;
    })
    .filter(Boolean);

  const organicItems = seededShuffle(
  plexMatchedItems,
  `${row.id}-${sessionSeed()}`
).slice(0, 20);

  return organicItems.length > 3 ? (
    <Row
      key={row.id}
      title={row.title}
      items={organicItems}
      onCard={onCard}
    />
  ) : null;
})}
        {wlItems.length > 0 && <Row title="My Watchlist" items={wlItems} onCard={onCard} />}
        <Row title="Trending Now" sub="Popular across all libraries" items={data.current.trending} onCard={onCard} />
        <Row title="Recently Added Movies" items={newM} onCard={onCard} />
        <Row title="Recently Added TV Shows" items={newS} onCard={onCard} />
        <Row title="Top Rated" sub="Highest rated in your collection" items={topR} onCard={onCard} />
        {fourK.length > 0 && <Row title="4K Ultra HD" sub="Premium quality" items={fourK} wide onCard={onCard} />}
        {byG("Action", movies).length > 3 && <Row title="Action & Adventure" items={byG("Action", movies)} onCard={onCard} />}
        {byG("Comedy", movies).length > 3 && <Row title="Comedy" items={byG("Comedy", movies)} onCard={onCard} />}
        {byG("Drama", movies).length > 3 && <Row title="Drama" items={byG("Drama", movies)} onCard={onCard} />}
        {byG("Thriller", movies).length > 3 && <Row title="Thriller" items={byG("Thriller", movies)} onCard={onCard} />}
        {byG("Horror", movies).length > 3 && <Row title="Horror" items={byG("Horror", movies)} onCard={onCard} />}
        {docs.length > 3 && <Row title="Documentary" items={docs} onCard={onCard} />}
        {wrestling.length > 3 && <Row title="Wrestling & Sports" items={wrestling} onCard={onCard} />}
        {reality.length > 3 && <Row title="Reality TV" items={reality} onCard={onCard} />}
        {kids.length > 3 && <Row title="Kids & Family" items={kids} onCard={onCard} />}
        {byG("Sci-Fi", movies).length > 3 && <Row title="Science Fiction" items={byG("Sci-Fi", movies)} onCard={onCard} />}
        {byG("Crime", movies).length > 3 && <Row title="Crime" items={byG("Crime", movies)} onCard={onCard} />}
        {byG("Mystery", movies).length > 3 && <Row title="Mystery" items={byG("Mystery", movies)} onCard={onCard} />}
        {byG("Romance", movies).length > 3 && <Row title="Romance" items={byG("Romance", movies)} onCard={onCard} />}
        {byG("Animation", movies).length > 3 && <Row title="Animation" items={byG("Animation", movies)} onCard={onCard} />}
        {byG("War", movies).length > 3 && <Row title="War" items={byG("War", movies)} onCard={onCard} />}
        {byG("Western", movies).length > 3 && <Row title="Western" items={byG("Western", movies)} onCard={onCard} />}
        {crit.length > 3 && <Row title="Criterion Collection" sub="Curated cinema classics" items={crit} onCard={onCard} />}
        {classics.length > 3 && <Row title="Classic Cinema" sub="Before 1985" items={classics} onCard={onCard} />}
        {dirI.length > 3 && <Row title={sDir} sub="Director spotlight" items={dirI} onCard={onCard} />}
        {actI.length > 3 && <Row title={sAct} sub="Actor spotlight" items={actI} onCard={onCard} />}
      </div>
    </>
  );
}

// ── LIBRARY TAB PAGE ──────────────────────────────────────────────────────────
function LibraryTabPage({ title, items, onCard, type }) {
  const [subTab, setSubTab] = useState("featured");
  const [genre, setGenre] = useState("All");
  const [letter, setLetter] = useState("All");
  const [activeBrand, setActiveBrand] = useState(null);
  const [alphaVisible, setAlphaVisible] = useState(false);
  const [visibleCount, setVisibleCount] = useState(96);
  const [visibleRowCount, setVisibleRowCount] = useState(5);
  const lastScrollY = useRef(0);
  const alphaTimer = useRef(null);

  // LibraryTabPage itself controls initial Pi focus
  useEffect(() => {
    if (
      typeof window === "undefined" ||
      !window.location.href.includes("kiosk=napflix")
    ) return;

    const timer = setTimeout(() => {
      const tabs = Array.from(document.querySelectorAll(".lib-sub-tab"));
      const featured = tabs.find((tab) =>
        tab.textContent?.trim().toLowerCase() === "featured"
      );

      if (!featured) return;

      document
        .querySelectorAll(".napflix-tv-focused")
        .forEach((x) => x.classList.remove("napflix-tv-focused"));

      featured.classList.add("napflix-tv-focused");
      featured.focus({ preventScroll: true });
      window.__napflixCurrentFocus = featured;
    }, 350);

    return () => clearTimeout(timer);
  }, [title, type]);

  const letters = ["All", ...Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i)), "#"];
  const librarySeed = `${type}-${items.length}-${sessionSeed()}`;

const allG = [
  "All",
  ...Array.from(new Set(items.flatMap(m => (m.Genre || []).map(g => g.tag)))).sort()
];

const brandTiles = [
  {
    label: "Netflix",
    logo: "/logos/brands/netflixlogo.png",
    collection: "PJ_Netflix_Originals",
  },
  {
    label: "Apple TV+",
    logo: "/logos/brands/apple-tv-logo.png",
    collection: "PJ_Apple_TV",
  },
  {
    label: "HBO / Max",
    logo: "/logos/brands/hbo-maxlogo.png",
    collection: "PJ_HBO",
  },
  {
    label: "Prime Video",
    logo: "/logos/brands/prime-videologo.png",
    collection: "PJ_Prime_Video",
  },
  {
    label: "Disney",
    logo: "/logos/brands/disneylogo.png",
    collection: "PJ_Disney",
  },
  {
    label: "Warner Bros.",
    logo: "/logos/brands/warner-broslogo.png",
    collection: "PJ_Warner_Bros",
  },
  {
    label: "Lionsgate",
    logo: "/logos/brands/lionsgatelogo.png",
    collection: "PJ_Lionsgate",
  },
  {
    label: "A24",
    logo: "/logos/brands/a24logo.png",
    collection: "PJ_A24",
  },
  {
    label: "Criterion",
    logo: "/logos/brands/criterionlogo.png",
    collection: "Criterion Collection",
  },
  {
    label: "Universal",
    logo: "/logos/brands/universallogo.png",
    collection: "PJ_Universal",
  },
  {
    label: "Paramount",
    logo: "/logos/brands/paramountlogo.png",
    collection: "PJ_Paramount",
  },
  {
    label: "Sony / Columbia",
    logo: "/logos/brands/sony-columbialogo.png",
    collection: "PJ_Sony_Columbia",
  },
  {
    label: "Marvel",
    logo: "/logos/brands/marvel-logo.png",
    collection: "PJ_Marvel",
  },
  {
    label: "Pixar",
    logo: "/logos/brands/pixarlogo.png",
    collection: "PJ_Pixar",
  },
  {
    label: "DreamWorks",
    logo: "/logos/brands/dreamworkslogo.png",
    collection: "PJ_DreamWorks",
  },
  {
    label: "Neon",
    logo: "/logos/brands/neonlogo.png",
    collection: "PJ_Neon",
  },
  {
    label: "MGM",
    logo: "/logos/brands/mgm-logo.png",
    collection: "PJ_MGM",
  },
  {
    label: "20th Century",
    logo: "/logos/brands/foxlogo.png",
    collection: "PJ_20th_Century",
  },
  {
    label: "Focus Features",
    logo: "/logos/brands/focuslogo.png",
    collection: "PJ_Focus_Features",
  },
  {
    label: "Miramax",
    logo: "/logos/brands/miramaxlogo.png",
    collection: "PJ_Miramax",
  },
]
  .map((brand) => ({
    ...brand,
    items: items.filter((item) =>
      item.Collection?.some((c) => c.tag === brand.collection)
    ),
  }))
  .filter((brand) => brand.items.length > 0);

const filtered = items.filter(m => {
  const active = brandTiles.find(b => b.label === activeBrand);

const brandOk =
  !activeBrand ||
  (
    active?.collection
      ? m.Collection?.some(c => c.tag === active.collection)
      : hasAnyText(m, active?.terms || [])
  );

  const gOk =
    genre === "All" ||
    (genre === "Wrestling/Sports"
      ? m.Genre?.some(g => ["Wrestling", "Sports", "Sport"].includes(g.tag))
      : m.Genre?.some(g => g.tag === genre));

  const lOk =
    letter === "All" ||
    (letter === "#"
      ? /^[^a-zA-Z]/.test(m.title)
      : m.title?.toUpperCase().startsWith(letter));

  return brandOk && gOk && lOk;
}).sort((a, b) => a.title?.localeCompare(b.title));

  const jumpTo = (l) => {
    setLetter(l);
    setVisibleCount(96);
    setAlphaVisible(false);
    window.scrollTo({ top: 280, behavior: "smooth" });
  };

  const byG = (g) => seededShuffle(items.filter(m => m.Genre?.some(x => x.tag === g)), `${librarySeed}-${g}`).slice(0, 20);


  const movieRows = [
    { title: "New Arrivals", sub: "Recently added", items: [...items].sort((a, b) => (b.addedAt || 0) - (a.addedAt || 0)).slice(0, 20) },
    { title: "Top Rated", items: [...items].filter(m => m.rating).sort((a, b) => b.rating - a.rating).slice(0, 20) },
    { title: "Criterion Collection", sub: "Essential cinema", items: seededShuffle(items.filter(isCrit), `${librarySeed}-criterion`).slice(0, 20) },
    { title: "4K Ultra HD", items: seededShuffle(items.filter(is4K), `${librarySeed}-4k`).slice(0, 20) },
    { title: "Action & Adventure", items: byG("Action") },
    { title: "Comedy", items: byG("Comedy") },
    { title: "Drama", items: byG("Drama") },
    { title: "Thriller", items: byG("Thriller") },
    { title: "Horror", items: byG("Horror") },
    { title: "Science Fiction", items: byG("Sci-Fi") },
    { title: "Crime", items: byG("Crime") },
    { title: "Documentary", items: byG("Documentary") },
    { title: "War", items: byG("War") },
    { title: "Western", items: byG("Western") },
    { title: "Animation", items: byG("Animation") },
    { title: "Romance", items: byG("Romance") },
    { title: "Classic Cinema", sub: "Pre-1985", items: seededShuffle(items.filter(m => m.year && m.year < 1985), `${librarySeed}-classic`).slice(0, 20) },
    { title: "1990s", items: seededShuffle(items.filter(m => m.year >= 1990 && m.year < 2000), `${librarySeed}-1990s`).slice(0, 20) },
    { title: "2000s", items: seededShuffle(items.filter(m => m.year >= 2000 && m.year < 2010), `${librarySeed}-2000s`).slice(0, 20) },
    { title: "Wrestling & Sports", items: seededShuffle(items.filter(m => m.Genre?.some(g => ["Wrestling", "Sports", "Sport"].includes(g.tag))), `${librarySeed}-sports`).slice(0, 20) },
  ];

  const showRows = [
    { title: "New Arrivals", items: [...items].sort((a, b) => (b.addedAt || 0) - (a.addedAt || 0)).slice(0, 20) },
    { title: "Top Rated", items: [...items].filter(s => s.rating).sort((a, b) => b.rating - a.rating).slice(0, 20) },
    { title: "Drama", items: byG("Drama") },
    { title: "Comedy", items: byG("Comedy") },
    { title: "Crime & Mystery", items: seededShuffle([...byG("Crime"), ...byG("Mystery")], `${librarySeed}-crime-mystery`).slice(0, 20) },
    { title: "Sci-Fi & Fantasy", items: seededShuffle([...byG("Sci-Fi"), ...byG("Fantasy")], `${librarySeed}-scifi-fantasy`).slice(0, 20) },
    { title: "Horror", items: byG("Horror") },
    { title: "Documentary", items: byG("Documentary") },
    { title: "Reality TV", items: byG("Reality") },
    { title: "Kids & Family", items: seededShuffle(items.filter(m => m.Genre?.some(g => ["Children", "Family", "Animation", "Kids"].includes(g.tag))), `${librarySeed}-kids`).slice(0, 20) },
    { title: "Action", items: byG("Action") },
    { title: "Thriller", items: byG("Thriller") },
    { title: "Wrestling & Sports", items: seededShuffle(items.filter(m => m.Genre?.some(g => ["Wrestling", "Sports", "Sport"].includes(g.tag))), `${librarySeed}-sports`).slice(0, 20) },
  ];

  const rows = type === "movie" ? movieRows : showRows;

  return (
    <div style={{ paddingTop: 62 }}>
      <div className="lib-sub-tabs">
        <button className={`lib-sub-tab${subTab === "featured" ? " on" : ""}`} onClick={() => { setSubTab("featured"); setVisibleRowCount(5); }}>Featured</button>
        <button className={`lib-sub-tab${subTab === "all" ? " on" : ""}`} onClick={() => { setSubTab("all"); setVisibleCount(96); }}>All {title}</button>
      </div>

      {subTab === "featured" && (
  <div style={{ paddingTop: 32, paddingBottom: 80 }}>
    <div style={{ padding: "0 52px 28px" }}>
      <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 42, letterSpacing: 3 }}>
        {title}
      </div>
      <div style={{ fontSize: 13, color: "var(--t3)", fontWeight: 300 }}>
        {items.length.toLocaleString()} titles
      </div>
    </div>

    {brandTiles.length > 0 && (
      <div style={{ padding: "0 52px 34px" }}>
        <div style={{ display: "flex", gap: 14, overflowX: "auto", paddingBottom: 10 }}>
          {brandTiles.map((brand) => (
            <button
  key={brand.label}
  onClick={() => setActiveBrand(brand.label)}
  title={`${brand.label} — ${brand.items.length} titles`}
  style={{
    minWidth: 180,
    width: 180,
    height: 100,
    borderRadius: 10,
    border:
      activeBrand === brand.label
        ? "1px solid var(--gold)"
        : "1px solid rgba(255,255,255,0.12)",
    overflow: "hidden",
    padding: 0,
    background: "#000",
    cursor: "pointer",
    position: "relative",
    transition: "all 0.25s ease",
    transform: activeBrand === brand.label ? "scale(1.02)" : "scale(1)",
    boxShadow:
      activeBrand === brand.label
        ? "0 0 0 1px rgba(229,160,13,0.28), 0 12px 30px rgba(0,0,0,0.35)"
        : "0 8px 24px rgba(0,0,0,0.28)",
  }}
>
  {brand.logo ? (
    <img
        decoding="async"
      src={brand.logo}
      alt={brand.label}
      style={{
        width: "100%",
        height: "100%",
        objectFit: "cover",
        display: "block",
      }}
    />
  ) : (
    <span
      style={{
        fontFamily: "'Bebas Neue', sans-serif",
        fontSize: 26,
        letterSpacing: 1.5,
        color: "var(--t1)",
        zIndex: 2,
      }}
    >
      {brand.label}
    </span>
  )}

  <div
    style={{
      position: "absolute",
      inset: 0,
      background:
        "radial-gradient(circle at center, transparent 52%, rgba(0,0,0,0.45) 100%)",
      pointerEvents: "none",
    }}
  />

  <div
    style={{
      position: "absolute",
      inset: 0,
      background:
        "linear-gradient(to bottom, rgba(0,0,0,0.28), transparent 48%)",
      pointerEvents: "none",
    }}
  />

  <div
    style={{
      position: "absolute",
      inset: 0,
      background:
        "linear-gradient(to top, rgba(0,0,0,0.48), transparent 62%)",
      pointerEvents: "none",
    }}
  />

  <div
    style={{
      position: "absolute",
      inset: 0,
      boxShadow:
        activeBrand === brand.label
          ? "inset 0 0 42px rgba(229,160,13,0.16)"
          : "inset 0 0 40px rgba(255,255,255,0.055)",
      pointerEvents: "none",
    }}
  />
</button>
          ))}
        </div>
      </div>
    )}

    {activeBrand ? (
      <div style={{ padding: "0 52px 44px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
          <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 34, letterSpacing: 2 }}>
            {activeBrand}
          </div>
          <div style={{ color: "var(--t3)", fontSize: 12 }}>
            {brandTiles.find((b) => b.label === activeBrand)?.items.length || 0} titles
          </div>

          <button
            onClick={() => setActiveBrand(null)}
            style={{
              border: "1px solid rgba(255,255,255,0.12)",
              background: "rgba(255,255,255,0.06)",
              color: "var(--t2)",
              borderRadius: 999,
              padding: "5px 12px",
              fontSize: 11,
              cursor: "pointer",
            }}
          >
            Clear
          </button>
        </div>

        <Row
          title={activeBrand}
            items={brandTiles.find((b) => b.label === activeBrand)?.items || []}
            onCard={onCard}
        />
      </div>
    ) : (
      <div>
        {rows
          .filter((r) => r.items?.length > 3)
          .slice(0, visibleRowCount)
          .map((r) => (
            <Row key={r.title} title={r.title} sub={r.sub} items={r.items} onCard={onCard} />
          ))}

        {visibleRowCount < rows.filter((r) => r.items?.length > 3).length && (
          <div style={{ display: "flex", justifyContent: "center", padding: "28px 0 44px" }}>
            <button
              className="sec-all"
              onClick={() => setVisibleRowCount((n) => n + 5)}
            >
              Load More Rows →
            </button>
          </div>
        )}
      </div>
    )}
  </div>
)}

{subTab === "all" && (
  <>
    <AlphaJumpBar items={items} visible={alphaVisible} onJump={jumpTo} />
    <ScrollScrubber />

    <div style={{ padding: "24px 52px 8px", display: "flex", alignItems: "baseline", gap: 14 }}>
      <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 32, letterSpacing: 2 }}>
        {activeBrand ? activeBrand : `All ${title}`}
      </div>
      <div style={{ fontSize: 13, color: "var(--t3)", fontWeight: 300 }}>
        {filtered.length.toLocaleString()} titles
      </div>
      {activeBrand && (
        <button
          onClick={() => setActiveBrand(null)}
          style={{
            border: "1px solid rgba(255,255,255,0.12)",
            background: "rgba(255,255,255,0.06)",
            color: "var(--t2)",
            borderRadius: 999,
            padding: "5px 12px",
            fontSize: 11,
            cursor: "pointer",
          }}
        >
          Clear
        </button>
      )}
    </div>

    <div className="filters">
      {allG.slice(0, 22).map((g) => (
        <button
          key={g}
          className={`fpill${genre === g ? " on" : ""}`}
          onClick={() => {
            setGenre(g);
            setLetter("All");
            setVisibleCount(96);
          }}
        >
          {g}
        </button>
      ))}
    </div>

    <div className="alnav">
      {letters.map((l) => (
        <button key={l} className={`albtn${letter === l ? " on" : ""}`} onClick={() => jumpTo(l)}>
          {l}
        </button>
      ))}
    </div>

    <div className="agrid">
      {filtered.slice(0, visibleCount).map((item) => (
        <MediaCard key={item.ratingKey} item={item} onClick={onCard} />
      ))}
    </div>

    {visibleCount < filtered.length && (
      <div style={{ display: "flex", justifyContent: "center", padding: "28px 0 10px" }}>
        <button
          className="sec-all"
          onClick={() => setVisibleCount((n) => Math.min(n + 96, filtered.length))}
        >
          Load More Titles →
        </button>
      </div>
    )}

    <div style={{ height: 80 }} />
  </>
)}

    </div>
  );
}

// ── CATEGORY HELPERS ──────────────────────────────────────────────────────────
function hasGenre(item, genres = []) {
  return item.Genre?.some((g) =>
    genres.map(x => x.toLowerCase()).includes(g.tag?.toLowerCase())
  );
}

function hasAnyText(item, terms = []) {
  const haystack = [
    item.title,
    item.originalTitle,
    item.summary,
    item.tagline,
    item.studio,
    item.network,
    item.contentRating,
    item.editionTitle,
    item.edition,
    item.grandparentTitle,
    item.parentTitle,
    item.Network?.map(n => n.tag).join(" "),
    item.Guid?.map(g => g.id).join(" "),
    item.Edition?.map(e => e.tag).join(" "),
    item.Collection?.map(c => c.tag).join(" "),
    item.Label?.map(l => l.tag).join(" "),
    item.Genre?.map(g => g.tag).join(" "),
    item.Role?.map(r => r.tag).join(" "),
    item.Director?.map(d => d.tag).join(" "),
    item.Writer?.map(w => w.tag).join(" "),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return terms.some((term) => haystack.includes(term.toLowerCase()));
}

function getStudioText(item) {
  return [
    item.studio,
    item.editionTitle,
    item.edition,
    item.Edition?.map(e => e.tag).join(" "),
    item.Collection?.map(c => c.tag).join(" "),
    item.Label?.map(l => l.tag).join(" "),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function matchesBrand(item, terms = []) {
  const studioText = getStudioText(item);

  return terms.some((term) => studioText.includes(term.toLowerCase()));
}

function isCriterionItem(item) {
  return hasAnyText(item, [
    "criterion",
    "criterion collection",
  ]);
}

function isMillennialItem(item) {
  const year = Number(item.year || 0);

  return (
    year >= 1988 &&
    year <= 2006 &&
    (
      item.type === "show" ||
      hasGenre(item, ["Comedy", "Drama", "Family", "Children", "Animation", "Romance", "Teen"])
    )
  );
}

function isDocuseriesItem(item) {
  return (
    item.type === "show" &&
    (
      hasGenre(item, ["Documentary"]) ||
      hasAnyText(item, ["docuseries", "documentary series", "true crime", "limited documentary"])
    )
  );
}

function isComfortTVItem(item) {
  return (
    item.type === "show" &&
    hasGenre(item, ["Comedy", "Family", "Children", "Animation"])
  );
}

function isLate90sSitcomItem(item) {
  const year = Number(item.year || 0);

  return (
    item.type === "show" &&
    year >= 1995 &&
    year <= 2004 &&
    hasGenre(item, ["Comedy"])
  );
}

function isTrueCrimeItem(item) {
  return (
    hasGenre(item, ["Crime", "Documentary"]) ||
    hasAnyText(item, ["true crime", "murder", "serial killer", "crime documentary"])
  );
}

function isFeelGoodItem(item) {
  return (
    hasGenre(item, ["Comedy", "Family", "Romance", "Children"]) &&
    !hasGenre(item, ["Horror", "Thriller", "Crime", "War"])
  );
}

function isHiddenGemItem(item) {
  const rating = Number(item.rating || item.audienceRating || 0);
  const year = Number(item.year || 0);

  return (
    year > 1970 &&
    rating >= 6.5 &&
    !hasAnyText(item, ["marvel", "star wars", "harry potter"])
  );
}

function isA24Item(item) {
  return hasAnyText(item, ["a24"]);
}

function collectionTags(item) {
  return (item.Collection || [])
    .map((c) => String(c.tag || "").toLowerCase());
}

function hasCollectionTag(item, tags = []) {
  const collections = collectionTags(item);
  return tags.some((tag) => collections.includes(tag.toLowerCase()));
}

function isDisneyItem(item) {
  return (
    hasCollectionTag(item, ["PJ_Disney"]) ||
    hasAnyText(item, ["disney", "walt disney", "pixar", "marvel", "lucasfilm"])
  );
}

function isPixarItem(item) {
  return (
    hasCollectionTag(item, ["PJ_Disney"]) &&
    hasAnyText(item, ["pixar"])
  ) || hasAnyText(item, ["pixar"]);
}

function isDreamworksItem(item) {
  return hasAnyText(item, ["dreamworks"]);
}

function isHBOItem(item) {
  return (
    hasCollectionTag(item, ["PJ_HBO", "PJ_Max"]) ||
    hasAnyText(item, ["hbo", "max", "hbo max"])
  );
}

function isNetflixItem(item) {
  return (
    hasCollectionTag(item, ["PJ_Netflix", "PJ_Netflix_Originals"]) ||
    hasAnyText(item, ["netflix"])
  );
}

function isPrimeItem(item) {
  return (
    hasCollectionTag(item, ["PJ_Prime", "PJ_Amazon", "PJ_Amazon_Prime"]) ||
    hasAnyText(item, ["amazon", "prime video", "amazon studios"])
  );
}

function isAppleItem(item) {
  return (
    hasCollectionTag(item, ["PJ_Apple", "PJ_AppleTV", "PJ_Apple_TV", "PJ_Apple_Originals"]) ||
    hasAnyText(item, [
      "apple tv",
      "apple tv+",
      "apple original",
      "apple originals",
      "apple studios",
      "appletv",
      "tv.apple",
      "apple",
    ])
  );
}


// ── CATEGORIES PAGE ───────────────────────────────────────────────────────────
function CatsPage({ movies, shows, onCard }) {
  const [active, setActive] = useState(null);
  const all = [...movies, ...shows];

  const DIRS = [
    "Steven Spielberg",
    "Martin Scorsese",
    "Christopher Nolan",
    "Quentin Tarantino",
    "Denis Villeneuve",
    "David Fincher",
    "Ridley Scott",
    "James Cameron",
    "Alfred Hitchcock",
    "Wes Anderson",
    "Stanley Kubrick",
    "Francis Ford Coppola",
    "Paul Thomas Anderson",
    "Terrence Malick",
    "Sofia Coppola",
    "Spike Lee",
    "Clint Eastwood",
  ];

  const ACTS = [
    "Denzel Washington",
    "Tom Hanks",
    "Meryl Streep",
    "Morgan Freeman",
    "Leonardo DiCaprio",
    "Robert De Niro",
    "Al Pacino",
    "Cate Blanchett",
    "Viola Davis",
    "Brad Pitt",
    "Jodie Foster",
    "Gene Hackman",
    "Dustin Hoffman",
    "Jack Nicholson",
    "Anthony Hopkins",
    "Sigourney Weaver",
    "Will Smith",
    "Eddie Murphy",
    "Robin Williams",
    "Marlon Brando",
    "Adam Sandler",
    "Jim Carrey",
    "James Gandolfini",
    "Kate Winslet",
  ];

  const GENRES = [
    "Action",
    "Adventure",
    "Animation",
    "Biography",
    "Children",
    "Comedy",
    "Crime",
    "Documentary",
    "Drama",
    "Family",
    "Fantasy",
    "History",
    "Horror",
    "Music",
    "Mystery",
    "Reality",
    "Romance",
    "Sci-Fi",
    "Sport",
    "Thriller",
    "War",
    "Western",
    "Wrestling",
  ];

  const byP = (name) =>
    shuffle(
      all.filter(
        (m) =>
          m.Director?.some((d) => d.tag === name) ||
          m.Role?.some((r) => r.tag === name) ||
          m.Writer?.some((w) => w.tag === name)
      )
    ).slice(0, 40);

  const byG = (g) =>
    shuffle(all.filter((m) => m.Genre?.some((x) => x.tag === g))).slice(0, 40);

  const cats = [
    ...GENRES.map((g) => ({ label: g, items: byG(g), prefix: "Genre" })),
    ...DIRS.map((d) => ({ label: d, items: byP(d), prefix: "Director" })),
    ...ACTS.map((a) => ({ label: a, items: byP(a), prefix: "Starring" })),

    { label: "Criterion Collection", items: shuffle(movies.filter(isCriterionItem)).slice(0, 40), prefix: "Collection" },
    { label: "Growing Up Millennial", items: shuffle(all.filter(isMillennialItem)).slice(0, 40), prefix: "Era" },
    { label: "Essential Docuseries", items: shuffle(shows.filter(isDocuseriesItem)).slice(0, 40), prefix: "TV Collection" },
    { label: "Comfort TV", items: shuffle(shows.filter(isComfortTVItem)).slice(0, 40), prefix: "TV Collection" },
    { label: "Late 90s Sitcoms", items: shuffle(shows.filter(isLate90sSitcomItem)).slice(0, 40), prefix: "TV Collection" },
    { label: "True Crime Spree", items: shuffle(all.filter(isTrueCrimeItem)).slice(0, 40), prefix: "Collection" },

    { label: "A24", items: shuffle(all.filter(isA24Item)).slice(0, 40), prefix: "Studio" },
    { label: "Disney Collection", items: shuffle(all.filter(isDisneyItem)).slice(0, 40), prefix: "Studio" },
    { label: "Pixar Films", items: shuffle(all.filter(isPixarItem)).slice(0, 40), prefix: "Studio" },
    { label: "DreamWorks", items: shuffle(all.filter(isDreamworksItem)).slice(0, 40), prefix: "Studio" },
    { label: "HBO Originals", items: shuffle(all.filter(isHBOItem)).slice(0, 40), prefix: "Network" },
    { label: "Netflix Originals", items: shuffle(all.filter(isNetflixItem)).slice(0, 40), prefix: "Platform" },
    { label: "Prime Originals", items: shuffle(all.filter(isPrimeItem)).slice(0, 40), prefix: "Platform" },
    { label: "Apple TV Originals", items: shuffle(all.filter(isAppleItem)).slice(0, 40), prefix: "Platform" },

    { label: "Feel-Good Flicks", items: shuffle(movies.filter(isFeelGoodItem)).slice(0, 40), prefix: "Mood" },
    { label: "Hidden Gems", items: shuffle(all.filter(isHiddenGemItem)).slice(0, 40), prefix: "Collection" },

    { label: "4K Ultra HD", items: shuffle(all.filter(is4K)).slice(0, 40), prefix: "Format" },
    { label: "Classic Cinema", items: shuffle(movies.filter((m) => m.year && m.year < 1980)).slice(0, 40), prefix: "Era" },
    { label: "80s Throwbacks", items: shuffle(movies.filter((m) => m.year >= 1980 && m.year < 1990)).slice(0, 40), prefix: "Era" },
    { label: "90s Films", items: shuffle(movies.filter((m) => m.year >= 1990 && m.year < 2000)).slice(0, 40), prefix: "Era" },
    { label: "2000s Films", items: shuffle(movies.filter((m) => m.year >= 2000 && m.year < 2010)).slice(0, 40), prefix: "Era" },

    { label: "Children's TV", items: shuffle(shows.filter((m) => hasGenre(m, ["Children", "Family", "Kids", "Animation"]))).slice(0, 40), prefix: "Category" },
    { label: "Reality TV", items: shuffle(shows.filter((m) => hasGenre(m, ["Reality"]))).slice(0, 40), prefix: "Category" },
    { label: "Wrestling & Sports", items: shuffle(all.filter((m) => hasGenre(m, ["Wrestling", "Sports", "Sport"]))).slice(0, 40), prefix: "Category" },
  ].filter((c) => c.items.length > 2);

  if (active) {
    const cat = cats.find((c) => c.label === active);

    return (
      <>
        <div className="ph">
          {cat?.prefix && (
            <div
              style={{
                fontSize: 9,
                color: "var(--gold)",
                letterSpacing: 2,
                textTransform: "uppercase",
                marginBottom: 7,
              }}
            >
              {cat.prefix}
            </div>
          )}
          <div className="ph-title">{active}</div>
          <div className="ph-sub">{cat?.items.length} titles</div>
        </div>

        <button
          onClick={() => setActive(null)}
          style={{
            margin: "0 52px 24px",
            background: "rgba(255,255,255,0.05)",
            border: "1px solid rgba(255,255,255,0.08)",
            color: "var(--t2)",
            padding: "8px 18px",
            borderRadius: 4,
            cursor: "pointer",
            fontSize: 13,
            fontFamily: "'Outfit',sans-serif",
          }}
        >
          ← All Categories
        </button>

        <div className="agrid">
          {cat?.items.map((item) => (
            <MediaCard key={item.ratingKey} item={item} onClick={onCard} />
          ))}
        </div>

        <div style={{ height: 60 }} />
      </>
    );
  }

  return (
    <>
      <div className="ph">
        <div className="ph-title">Browse</div>
        <div className="ph-sub">Genres, directors, actors & more</div>
      </div>

      <div className="cats-grid">
        {cats.map((cat) => (
          <div
            key={`${cat.prefix}-${cat.label}`}
            className="cat-tile"
            onClick={() => setActive(cat.label)}
          >
            <div className="cat-prefix">{cat.prefix}</div>
            <div className="cat-name">{cat.label}</div>
            <div className="cat-count">{cat.items.length} titles</div>
          </div>
        ))}
      </div>
    </>
  );
}

// ── PERSON SEARCH PAGE ────────────────────────────────────────────────────────
function PersonPage({ name, movies, shows, onCard, onBack }) {
  const [acted, setActed] = useState([]);
  const [directed, setDirected] = useState([]);
  const [wrote, setWrote] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const allKeys = [...MOVIE_KEYS.map(k => ({ k, t: 1 })), ...SHOW_KEYS.map(k => ({ k, t: 2 }))];
    const enc = encodeURIComponent(name);
    Promise.all(allKeys.map(({ k, t }) => Promise.all([
      plex('/library/sections/' + k + '/all?type=' + t + '&actor=' + enc).catch(() => ({ Metadata: [] })),
      plex('/library/sections/' + k + '/all?type=' + t + '&director=' + enc).catch(() => ({ Metadata: [] })),
      plex('/library/sections/' + k + '/all?type=' + t + '&writer=' + enc).catch(() => ({ Metadata: [] })),
    ]))).then(results => {
      const aMap = new Map(), dMap = new Map(), wMap = new Map();
      results.forEach(([a, d, w]) => {
        (a?.Metadata || []).forEach(m => aMap.set(m.ratingKey, m));
        (d?.Metadata || []).forEach(m => dMap.set(m.ratingKey, m));
        (w?.Metadata || []).forEach(m => wMap.set(m.ratingKey, m));
      });
      const all = [...movies, ...shows];
      all.filter(m => m.Role?.some(r => r.tag === name)).forEach(m => aMap.set(m.ratingKey, m));
      all.filter(m => m.Director?.some(d => d.tag === name)).forEach(m => dMap.set(m.ratingKey, m));
      all.filter(m => m.Writer?.some(w => w.tag === name)).forEach(m => wMap.set(m.ratingKey, m));
      setActed([...aMap.values()].sort((a, b) => (b.year || 0) - (a.year || 0)));
      setDirected([...dMap.values()].sort((a, b) => (b.year || 0) - (a.year || 0)));
      setWrote([...wMap.values()].sort((a, b) => (b.year || 0) - (a.year || 0)));
      setLoading(false);
    });
  }, [name]);
  return (
    <>
      <div className="ph">
        <div style={{ fontSize: 9, color: "var(--gold)", letterSpacing: 2, textTransform: "uppercase", marginBottom: 8 }}>Person</div>
        <div className="ph-title">{name}</div>
        <div className="ph-sub">{acted.length + directed.length} titles in your library</div>
      </div>
      <button onClick={onBack} style={{ margin: "0 52px 28px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", color: "var(--t2)", padding: "8px 18px", borderRadius: 4, cursor: "pointer", fontSize: 13, fontFamily: "'Outfit',sans-serif" }}>← Back</button>
      {directed.length > 0 && <Row title={`Directed by ${name}`} items={directed} onCard={onCard} />}
      {acted.length > 0 && <Row title={`Starring ${name}`} items={acted} onCard={onCard} />}
      {wrote.length > 0 && <Row title={`Written by ${name}`} items={wrote} onCard={onCard} />}
      <div style={{ height: 60 }} />
    </>
  );
}

// ── WATCHLIST ─────────────────────────────────────────────────────────────────
function WlPage({ movies, shows, onCard }) {
  const [items, setItems] = useState([]);
  const all = [...movies, ...shows];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { const saved = JSON.parse(localStorage.getItem("wl") || "[]"); setItems(saved.map(w => all.find(m => m.ratingKey === w.ratingKey)).filter(Boolean)); }, [movies.length, shows.length]);
  return (
    <>
      <div className="ph"><div className="ph-title">My Watchlist</div><div className="ph-sub">{items.length} saved</div></div>
      {!items.length ? <div className="wl-empty"><div className="wl-empty-icon">+</div><div className="wl-empty-txt">Save movies and shows to watch later</div></div> : <div className="agrid">{items.map(item => <MediaCard key={item.ratingKey} item={item} onClick={onCard} />)}</div>}
    </>
  );
}

// ── ALBUM MODAL ───────────────────────────────────────────────────────────────
function AlbumModal({ album, tracks, loadingTracks, onClose, onPlayTrack }) {
  const fmtDur = ms => { const s = Math.round(ms / 1000); return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0'); };
  const artSrc = album.localCover || pi(album.thumb, 500, 500);
  const firstMedia = tracks.find(t => t.Media?.[0])?.Media?.[0];
  const fmt = firstMedia?.audioCodec?.toUpperCase();
  const bitrate = firstMedia?.bitrate;
  const s1 = firstMedia?.Part?.[0]?.Stream?.find(s => s.streamType === 1);
  const bitDepth = s1?.bitDepth;
  const sampleRate = s1?.samplingRate;
  const focusAlbumModalTarget = (target) => {
    if (!target) return;

    document
      .querySelectorAll(".napflix-tv-focused")
      .forEach((node) => node.classList.remove("napflix-tv-focused"));

    target.classList.add("napflix-tv-focused");
    target.focus?.({ preventScroll: true });
    window.__napflixCurrentFocus = target;
    target.scrollIntoView?.({ block: "nearest" });
  };

  useEffect(() => {
    const h = e => {
      const modal = document.querySelector(".album-modal");
      if (!modal) return;

      const modalKeys = [
        "ArrowDown",
        "ArrowUp",
        "Enter",
        "Select",
        " ",
        "Spacebar",
        "Escape",
        "Backspace"
      ];

      if (!modalKeys.includes(e.key)) return;

      e.preventDefault();
      e.stopPropagation();

      if (e.key === "Escape" || e.key === "Backspace") {
        onClose();
        return;
      }

      const tracksEls = Array.from(modal.querySelectorAll(".album-modal-track"));
      const active = document.activeElement?.closest?.(".album-modal-track");
      const close = document.activeElement?.closest?.(".album-modal-close");
      const activeIndex = tracksEls.findIndex((el) => el === active);

      if (e.key === "ArrowDown") {
        focusAlbumModalTarget(tracksEls[Math.min(activeIndex + 1, tracksEls.length - 1)] || tracksEls[0]);
        return;
      }

      if (e.key === "ArrowUp") {
        focusAlbumModalTarget(tracksEls[Math.max(activeIndex - 1, 0)] || tracksEls[0]);
        return;
      }

      if (e.key === "Enter" || e.key === "Select" || e.key === " " || e.key === "Spacebar") {
        (active || close)?.click();
      }
    };

    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  useEffect(() => {
    if (loadingTracks) return;

    focusAlbumModalTarget(
      document.querySelector(".album-modal-track") ||
      document.querySelector(".album-modal-close")
    );
  }, [loadingTracks, tracks.length]);
  return (
    <div className="album-modal" style={{ position: 'fixed', inset: 0, zIndex: 900, background: 'rgba(0,0,0,0.82)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, backdropFilter: 'blur(3px)' }} onClick={onClose}>
      <div style={{ background: 'var(--s1)', borderRadius: 14, width: '100%', maxWidth: 860, maxHeight: '86vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', animation: 'fi .18s ease', border: '1px solid rgba(255,255,255,0.09)', boxShadow: '0 32px 80px rgba(0,0,0,0.85)' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', gap: 22, padding: '22px 22px 18px', background: 'var(--s2)', flexShrink: 0, alignItems: 'flex-start' }}>
          <div style={{ width: 260, height: 260, borderRadius: 8, overflow: 'hidden', background: 'var(--s3)', flexShrink: 0, boxShadow: '0 10px 28px rgba(0,0,0,0.7)' }}>
            {artSrc && <img loading="lazy" decoding="async" src={artSrc} alt={album.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => e.target.style.display = 'none'} />}
          </div>
          <div style={{ flex: 1, minWidth: 0, paddingTop: 4 }}>
            <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.2, marginBottom: 5 }}>{album.title}</div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', marginBottom: 4 }}>{album.parentTitle}{album.year ? ` · ${album.year}` : ''}</div>
            {album.studio && <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', marginBottom: 12 }}>{album.studio}</div>}
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' }}>
              {fmt && <span className="badge b4k">{fmt}</span>}
              {bitDepth && <span className="badge bhdr">{bitDepth}-bit</span>}
              {sampleRate && <span className="badge batmos">{sampleRate >= 1000 ? (sampleRate / 1000).toFixed(sampleRate % 1000 === 0 ? 0 : 1) + 'kHz' : sampleRate + 'Hz'}</span>}
              {bitrate && <span className="badge buhd">{bitrate} kbps</span>}
            </div>
            {album.leafCount && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginTop: 10 }}>{album.leafCount} tracks</div>}
          </div>
          <button className="album-modal-close" onClick={onClose} style={{ background: 'rgba(255,255,255,0.07)', border: 'none', color: 'rgba(255,255,255,0.5)', width: 32, height: 32, borderRadius: '50%', cursor: 'pointer', fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }} onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.14)'} onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.07)'}>✕</button>
        </div>
        <div style={{ overflowY: 'auto', flex: 1, padding: '0 10px 10px' }}>
          {loadingTracks ? <div style={{ padding: 32, display: 'flex', justifyContent: 'center' }}><div className="spin" /></div>
            : tracks.map((track, idx) => (
              <div key={track.ratingKey} className="album-modal-track" tabIndex={0} role="button" onClick={() => { onPlayTrack(track, tracks, idx); }}
                style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '9px 22px', cursor: 'pointer', borderBottom: idx < tracks.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none', transition: 'background .12s', outlineOffset: -2 }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <div style={{ width: 24, textAlign: 'right', fontSize: 12, color: 'rgba(255,255,255,0.25)', flexShrink: 0 }}>{track.index || idx + 1}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{track.title}</div>
                  {track.originalTitle && track.originalTitle !== track.grandparentTitle && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{track.originalTitle}</div>}
                </div>
                {track.Media?.[0]?.audioCodec && <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.22)', flexShrink: 0, textTransform: 'uppercase', letterSpacing: .5 }}>{track.Media[0].audioCodec}</div>}
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', flexShrink: 0, minWidth: 36, textAlign: 'right' }}>{track.duration ? fmtDur(track.duration) : ''}</div>
                <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'rgba(229,160,13,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width='9' height='9' viewBox='0 0 16 16' fill='var(--gold)'><path d='M4 2l10 6-10 6V2z' /></svg>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

// ── ARTIST PAGE ───────────────────────────────────────────────────────────────
function ArtistPage({ artist, musicArt = {}, onBack, startTrack, onArtist }) {
  const getPlex = (path) =>
    fetch(`/api/plex?path=${encodeURIComponent(path)}`, {
      headers: { Accept: "application/json" },
    }).then((r) => r.json());
  const [albums, setAlbums] = useState([]);
  const [singles, setSingles] = useState([]);
  const [musicVideos, setMusicVideos] = useState([]);
  const [liveVideos, setLiveVideos] = useState([]);
  const [liveAlbums, setLiveAlbums] = useState([]);
  const [similarArtists, setSimilarArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selAlbum, setSelAlbum] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [loadingTracks, setLoadingTracks] = useState(false);
  const [modalAlbum, setModalAlbum] = useState(null);
  const [detail, setDetail] = useState(null);
  const [playVideo, setPlayVideo] = useState(null);

  useEffect(() => {
    setLoading(true);
    setAlbums([]); setSingles([]); setMusicVideos([]); setLiveVideos([]);
    setSimilarArtists([]); setSelAlbum(null); setTracks([]); setDetail(null); setLiveAlbums([]); setModalAlbum(null); setLoadingTracks(false);
    const h = { headers: { Accept: "application/json" } };
    const get = url => fetch(url, h).then(r => r.json());
    const norm = s => s.replace(/[^a-z0-9]/gi, "").toLowerCase();

    Promise.all([
      getPlex(`/library/metadata/${artist.ratingKey}`),
      getPlex(`/library/metadata/${artist.ratingKey}/children`),
      getPlex(`/library/sections/40/folder`),
      getPlex(`/library/sections/33/folder`),
      getPlex(`/library/metadata/${artist.ratingKey}/related`).catch(() => null),
    ]).then(([detailData, childrenData, folder40Root, folder33Root, relatedData]) => {
      const fullArtist = detailData?.MediaContainer?.Metadata?.[0] || artist;
      setDetail(fullArtist);
      const allAlbums = childrenData?.MediaContainer?.Metadata || [];
      const related = relatedData?.MediaContainer?.Hub?.find(h => h.type === "artist");
      setSimilarArtists(related?.Metadata || []);

      // ── Section 40 folder: find Singles & EPs folder ─────────────────────────
      const artistFolder40 = (folder40Root?.MediaContainer?.Metadata || []).find(a => norm(a.title) === norm(artist.title));
      const singlesPromise = artistFolder40
        ? getPlex(artistFolder40.key).then(d => {
            const subfolders = d?.MediaContainer?.Metadata || [];
            const singlesFolder = subfolders.find(f => f.title === "Singles & EPs");
            if (!singlesFolder) return { singlesSet: new Set(), singlesAlbums: [] };
            return getPlex(singlesFolder.key).then(d2 => {
              const subfolderItems = d2?.MediaContainer?.Metadata || [];
              // Each subfolder contains tracks — fetch first track to get parentRatingKey
              return Promise.all(subfolderItems.map(subfolder =>
                getPlex(subfolder.key)
                  .then(d3 => {
                    const track = (d3?.MediaContainer?.Metadata || [])[0];
                    return track?.parentRatingKey || null;
                  }).catch(() => null)
              )).then(async parentKeys => {
                const keys = [...new Set(parentKeys.filter(Boolean))];
                // Fetch each album's full metadata to get Format tag
                const fetched = await Promise.all(keys.map(rk =>
                  getPlex(`/library/metadata/${rk}`)
                    .then(d4 => d4?.MediaContainer?.Metadata?.[0]).catch(() => null)
                ));
                return { singlesSet: new Set(keys), singlesAlbums: fetched.filter(Boolean) };
              });
            });
          })
        : Promise.resolve({ singlesSet: new Set(), singlesAlbums: [] });

      // ── Section 33 folder: Music Videos + Live Performances ───────────────
      const artistFolder33 = (folder33Root?.MediaContainer?.Metadata || []).find(a => norm(a.title) === norm(artist.title));
      const videosPromise = artistFolder33
        ? getPlex(artistFolder33.key).then(d => {
            const subfolders = d?.MediaContainer?.Metadata || [];
            // Two structures: Artist→Videos→MV/YT or Artist→MV/YT direct
            const videosFolder = subfolders.find(f => f.title === "Videos");
            const getMVAndYT = (subs) => {
              const mvFolder = subs.find(f => f.title === "Music Video");
              const ytFolder = subs.find(f => f.title === "YouTube");
              return Promise.all([
                mvFolder ? getPlex(mvFolder.key).then(d3 => d3?.MediaContainer?.Metadata || []) : Promise.resolve([]),
                ytFolder ? getPlex(ytFolder.key).then(d3 => d3?.MediaContainer?.Metadata || []) : Promise.resolve([]),
              ]);
            };
            if (videosFolder) {
              return getPlex(videosFolder.key)
                .then(d2 => getMVAndYT(d2?.MediaContainer?.Metadata || []));
            }
            return getMVAndYT(subfolders);
          })
        : Promise.resolve([[], []]);

      Promise.all([singlesPromise, videosPromise]).then(([{ singlesSet, singlesAlbums }, [mvs, lives]]) => {
        setAlbums(allAlbums.filter(a => !singlesSet.has(a.ratingKey)));
        // Split by Format[0].tag from MusicBrainz
        const getFormat = a => a.Format?.[0]?.tag || "Other";
        const singlesOnly = singlesAlbums.filter(a => getFormat(a) === "Single");
        const epsOnly = singlesAlbums.filter(a => getFormat(a) === "EP");
        const liveOnly = singlesAlbums.filter(a => getFormat(a) === "Live Album");
        const compilations = singlesAlbums.filter(a => getFormat(a) === "Compilation");
        const remixes = singlesAlbums.filter(a => getFormat(a) === "Remix");
        const other = singlesAlbums.filter(a => !["Single","EP","Live Album","Compilation","Remix"].includes(getFormat(a)));
        // Combine Singles + EPs into one group, rest separate
        setSingles([...singlesOnly, ...epsOnly]);
        setLiveAlbums([...liveOnly, ...compilations, ...remixes, ...other]);
        setMusicVideos(mvs);
        setLiveVideos(lives);
        setLoading(false);
      });
    }).catch(() => setLoading(false));
  }, [artist.ratingKey]);

  const fmtDur = ms => { const s = Math.round(ms / 1000); return Math.floor(s / 60) + ":" + String(s % 60).padStart(2, "0"); };
  const loadTracks = async (album) => {
  setModalAlbum(album);
  setTracks([]);
  setLoadingTracks(true);

  try {
    const d = await plex(`/library/metadata/${album.ratingKey}/children`);
    const loadedTracks = (d?.Metadata || []).map((track) => ({
      ...track,
      parentLocalCover: album.localCover,
    }));

    setTracks(loadedTracks);
  } catch (e) {}

  setLoadingTracks(false);
};
  const toTrackObj = t => ({
    title: t.title, artist: t.grandparentTitle || artist.title, album: t.parentTitle || "",
    year: t.parentYear || t.year, artUrl: t.parentThumb ? pi(t.parentThumb, 500) : (t.thumb ? pi(t.thumb, 500) : null),
    duration: Math.round((t.duration || 240000) / 1000), format: t.Media?.[0]?.audioCodec?.toUpperCase() || "FLAC",
    bitrate: t.Media?.[0]?.bitrate || 1411, bitDepth: 24, sampleRate: 96000,
    rating: t.userRating ? Math.round(t.userRating / 2) : 0, loved: (t.userRating || 0) >= 9,
    ratingKey: t.ratingKey,
    streamUrl: t.Media?.[0]?.Part?.[0]?.key ? `/api/plex-stream?path=${encodeURIComponent(t.Media[0].Part[0].key)}` : null,
  });
  const playTrack = (track, allTracks = [track], idx = 0) => {
  const safeTracks = allTracks?.length ? allTracks : [track];
  startTrack(toTrackObj(track), safeTracks.map(toTrackObj), idx);
};
  
  const canonicalSlug = (s = "") =>
  String(s)
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const localArtistArt =
  Object.values(musicArt || {}).find((entry) => entry.slug === canonicalSlug(artist.title)) ||
  null;

console.debug('Artist slug:', canonicalSlug(artist.title), 'Manifest match:', !!localArtistArt);

const extractYear = (s = "") => {
  const match = String(s).match(/\b(19|20)\d{2}\b/);
  return match ? match[0] : null;
};

const baseAlbumTitle = (s = "") =>
  String(s)
    .toLowerCase()
    .replace(/\[[^\]]*\]/g, "")
    .replace(/\((19|20)\d{2}\)/g, "")
    .replace(/\b(deluxe edition|special edition|expanded edition|anniversary edition|remastered|remaster|deluxe|expanded)\b/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const getEditionTag = (s = "") => {
  const str = String(s).toLowerCase();

  if (str.includes("deluxe")) return "deluxe";
  if (str.includes("special edition")) return "special";
  if (str.includes("expanded")) return "expanded";
  if (str.includes("remaster")) return "remaster";
  if (str.includes("anniversary")) return "anniversary";

  return "standard";
};

const getLocalAlbumEntry = (album, sectionLabel = "") => {
  const entries = Object.values(localArtistArt?.albums || {});
  if (!entries.length) return null;

  const wantedType =
    sectionLabel === "Singles & EPs" ? "Singles & EPs" : "Albums";

  const scopedEntries = entries.filter((entry) => entry.type === wantedType);

  const plexBase = baseAlbumTitle(album.title);
  const plexYear = String(album.year || album.parentYear || extractYear(album.title) || "");
  const plexEdition = getEditionTag(album.title);

  const matches = scopedEntries.filter((entry) => {
    const folderBase = baseAlbumTitle(entry.folder);
    const folderYear = extractYear(entry.folder) || "";
    const folderEdition = getEditionTag(entry.folder);

    return (
      folderBase === plexBase &&
      folderEdition === plexEdition &&
      (!plexYear || !folderYear || String(folderYear) === String(plexYear))
    );
  });

  if (matches.length === 1) return matches[0];

  return null;
};

const getLocalAlbumCover = (album, sectionLabel) =>
  getLocalAlbumEntry(album, sectionLabel)?.cover || null;

const getLocalAlbumBlur = (album, sectionLabel) =>
  getLocalAlbumEntry(album, sectionLabel)?.coverBlur || null;

const artSrc =
  localArtistArt?.background ||
  pi(detail?.art || artist.art || artist.thumb, 780);

const thumbSrc =
  localArtistArt?.poster ||
  pi(detail?.thumb || artist.thumb, 220);

console.debug('Artist art source: background', artSrc.startsWith('/art-cache') ? 'local' : 'plex', 'poster', thumbSrc.startsWith('/art-cache') ? 'local' : 'plex');

const genres = (detail?.Genre || artist.Genre || []).map(g => g.tag).join(" · ");

  function AlbumGrid({ items, label, collapsed = false }) {
  const [expanded, setExpanded] = useState(false);

  if (!items?.length) return null;

  const approxCols = Math.floor((window.innerWidth - 104) / 178) || 6;
  const visible = collapsed && !expanded ? items.slice(0, approxCols) : items;
  const showBtn = collapsed && items.length > approxCols;

  return (
    <div style={{ marginBottom: 52 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
        <div style={{ fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: "rgba(255,255,255,0.35)", fontWeight: 400 }}>
          {label}
        </div>

        {showBtn && (
          <button
            onClick={() => setExpanded(!expanded)}
            style={{ background: "none", border: "none", color: "var(--gold)", fontSize: 10, letterSpacing: "1.5px", textTransform: "uppercase", cursor: "pointer", fontFamily: "'Outfit',sans-serif" }}
          >
            {expanded ? "Show Less" : `See All (${items.length})`}
          </button>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))", gap: 18 }}>
        {visible.map((album) => (
          <div
            key={album.ratingKey}
            className="album-card"
            onClick={() =>
              loadTracks({
                ...album,
                localCover: getLocalAlbumCover(album, label),
                localBlur: getLocalAlbumBlur(album, label),
              })
            }
            style={{ cursor: "pointer" }}
          >
            <div style={{ width: "100%", aspectRatio: "1", borderRadius: 8, overflow: "hidden", background: "var(--s2)", marginBottom: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.5)", transition: "outline .15s" }}>
              <CardImg
                src={getLocalAlbumCover(album, label) || pi(album.thumb, 500, 500)}
                blurSrc={getLocalAlbumBlur(album, label)}
                alt={album.title}
              />
            </div>

            <div style={{ fontSize: 13, fontWeight: 500, color: "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {album.title}
            </div>

            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", marginTop: 2 }}>
              {album.year || ""}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

  function VideoGrid({ items, label }) {
    const [expanded, setExpanded] = useState(false);
    if (!items?.length) return null;
    const approxCols = Math.floor((window.innerWidth - 104) / 178) || 6;
    const visible = expanded ? items : items.slice(0, approxCols);
    return (
      <div style={{ marginBottom: 52 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
          <div style={{ fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: "rgba(255,255,255,0.35)", fontWeight: 400 }}>{label}</div>
          {items.length > approxCols && <button onClick={() => setExpanded(!expanded)} style={{ background: "none", border: "none", color: "var(--gold)", fontSize: 10, letterSpacing: "1.5px", textTransform: "uppercase", cursor: "pointer", fontFamily: "'Outfit',sans-serif" }}>{expanded ? "Show Less" : `See All (${items.length})`}</button>}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))", gap: 14 }}>
          {visible.map(v => (
            <div key={v.ratingKey} className="video-card" style={{ cursor: "pointer" }} onClick={() => setPlayVideo(v)}>
              <div style={{ width: "100%", aspectRatio: "16/9", borderRadius: 7, overflow: "hidden", background: "var(--s2)", marginBottom: 8, position: "relative" }}>
                <CardImg
  src={pi(v.thumb, 140)}
  alt={v.title}
/>
              </div>
              <div style={{ fontSize: 13, fontWeight: 500, color: "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{v.title}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (playVideo) return <VideoPlayer item={playVideo} onClose={() => setPlayVideo(null)} />;
  return (
    <>
      {modalAlbum && <AlbumModal album={modalAlbum} tracks={tracks} loadingTracks={loadingTracks} onClose={() => { setModalAlbum(null); setTracks([]); }} onPlayTrack={playTrack} />}
      <div style={{ position: "fixed", inset: 0, zIndex: 500, background: "var(--bg)", overflowY: "auto", animation: "fi .2s ease" }}>
      <div style={{ position: "relative", height: "55vh", minHeight: 420, overflow: "hidden" }}>
        {artSrc && <div style={{ position: "absolute", inset: 0, backgroundImage: `url(${artSrc})`, backgroundSize: "cover", backgroundPosition: "center top" }} />}
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top,rgba(6,6,9,1) 0%,rgba(6,6,9,0.55) 40%,rgba(6,6,9,0.1) 100%)" }} />
        <button onClick={onBack} style={{ position: "absolute", top: 24, left: 24, zIndex: 10, background: "rgba(0,0,0,0.6)", border: "1px solid rgba(255,255,255,0.12)", color: "#fff", padding: "8px 16px", borderRadius: 20, cursor: "pointer", fontSize: 13, fontFamily: "Outfit,sans-serif", backdropFilter: "blur(8px)" }}>← Back</button>
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "0 52px 36px", display: "flex", alignItems: "flex-end", gap: 28 }}>
          {thumbSrc && <div style={{ width: 140, height: 140, borderRadius: "50%", overflow: "hidden", border: "3px solid rgba(255,255,255,0.15)", flexShrink: 0, boxShadow: "0 12px 40px rgba(0,0,0,0.8)" }}>
            <img loading="lazy" decoding="async" src={thumbSrc} alt={artist.title} style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top" }} onError={e => e.target.style.display = "none"} />
          </div>}
          <div>
            <div style={{ fontFamily: "Bebas Neue,sans-serif", fontSize: "clamp(42px,8vw,96px)", lineHeight: 0.88, letterSpacing: 0.5, marginBottom: 12 }}>{artist.title}</div>
            {genres && <div style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", fontWeight: 300 }}>{genres}</div>}
          </div>
        </div>
      </div>
      <div style={{ padding: "36px 52px 132px" }}>
        {loading ? <div className="spin" style={{ margin: "40px auto", display: "block" }} /> : (
          <>
            <AlbumGrid items={albums} label="Albums" />
            <AlbumGrid items={singles} label="Singles & EPs" collapsed />
            <AlbumGrid items={liveAlbums} label="Live Albums" />
            <VideoGrid items={musicVideos} label="Music Videos" />
            <VideoGrid items={liveVideos} label="Live Performances" />
            {similarArtists.length > 0 && (
              <div style={{ marginBottom: 52 }}>
                <div style={{ fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: "rgba(255,255,255,0.35)", marginBottom: 18, fontWeight: 400 }}>Similar Artists</div>
                <div style={{ display: "flex", gap: 20, overflowX: "auto", paddingBottom: 8 }}>
                  {similarArtists.map(a => (
                    <div key={a.ratingKey} className="artist-card" onClick={() => onArtist && onArtist(a)} style={{ flexShrink: 0, width: 120, textAlign: "center", cursor: "pointer" }}>
                      <div style={{ width: 120, height: 120, borderRadius: 12, overflow: "hidden", background: "var(--s2)", marginBottom: 8, border: "2px solid rgba(255,255,255,0.08)" }}>
                        {a.thumb ? <img loading="lazy" decoding="async" src={pi(a.thumb, 130)} alt={a.title} style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top" }} onError={e => e.target.style.display = "none"} /> : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--t3)", fontSize: 28 }}>{a.title?.[0]}</div>}
                      </div>
                      <div style={{ fontSize: 13, color: "rgba(255,255,255,0.7)", fontWeight: 400 }}>{a.title}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
      </div>
    </>
  );
}


const formatMiniPlayerTime = (seconds = 0) => {
  const safeSeconds = Math.max(0, Math.floor(Number(seconds) || 0));
  const minutes = Math.floor(safeSeconds / 60);
  const remaining = String(safeSeconds % 60).padStart(2, "0");
  return `${minutes}:${remaining}`;
};

// ── MUSIC BOTTOM MINI PLAYER ──────────────────────────────────────────────────
function MusicBottomPlayer({
  track,
  expandRef,
  currentTime = 0,
  isPlaying = false,
  onPlay,
  onPause,
  onPrev,
  onNext,
  onSeek,
  queue = [],
  queueIdx = 0,
}) {
  useEffect(() => {
    if (!expandRef) return undefined;
    expandRef.current = () => {};
    return () => {
      expandRef.current = null;
    };
  }, [expandRef]);

  const duration = Number(track?.duration) || 0;
  const progress = duration > 0 ? Math.min(1, Math.max(0, currentTime / duration)) : 0;
  const openDisplay = () => window.open("/display", "_blank");
  const seekFromPointer = (event) => {
    event.stopPropagation();
    if (!onSeek) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const fraction = rect.width ? (event.clientX - rect.left) / rect.width : 0;
    onSeek(Math.min(1, Math.max(0, fraction)));
  };

  return (
    <div
      className="music-bottom-player"
      style={{
        position: "fixed",
        left: 28,
        right: 28,
        bottom: 24,
        zIndex: 950,
        minHeight: 86,
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) auto",
        alignItems: "center",
        gap: 18,
        padding: "12px 16px",
        borderRadius: 22,
        border: "1px solid rgba(255,255,255,0.14)",
        background: "linear-gradient(135deg, rgba(18,18,24,0.92), rgba(6,6,10,0.86))",
        boxShadow: "0 18px 60px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.08)",
        backdropFilter: "blur(24px) saturate(150%)",
        WebkitBackdropFilter: "blur(24px) saturate(150%)",
      }}
    >
      <div
        className="music-bottom-player-main"
        role="button"
        tabIndex={0}
        onClick={openDisplay}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openDisplay();
          }
        }}
        style={{
          minWidth: 0,
          display: "grid",
          gridTemplateColumns: "58px minmax(0, 1fr)",
          alignItems: "center",
          gap: 14,
          cursor: "pointer",
        }}
      >
        {track?.artUrl ? (
          <img
            loading="lazy"
            decoding="async"
            src={track.artUrl}
            alt={track.title}
            style={{
              width: 58,
              height: 58,
              borderRadius: 14,
              objectFit: "cover",
              border: "1px solid rgba(255,255,255,0.16)",
              boxShadow: "0 10px 28px rgba(0,0,0,0.45)",
            }}
          />
        ) : (
          <div
            style={{
              width: 58,
              height: 58,
              borderRadius: 14,
              background: "linear-gradient(135deg,#242436,#0d0d16)",
              border: "1px solid rgba(255,255,255,0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--gold)",
              fontSize: 22,
            }}
          >
            M
          </div>
        )}
        <div style={{ minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, minWidth: 0 }}>
            <div style={{ color: "#fff", fontWeight: 800, fontSize: 16, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {track?.title || "Now Playing"}
            </div>
            {queue.length > 0 && (
              <div style={{ color: "rgba(255,255,255,0.42)", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                {queueIdx + 1}/{queue.length}
              </div>
            )}
          </div>
          <div style={{ marginTop: 3, color: "rgba(255,255,255,0.58)", fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {[track?.artist, track?.album].filter(Boolean).join(" / ") || "Poster Junkie Music"}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "38px minmax(0, 1fr) 38px", gap: 10, alignItems: "center", marginTop: 10 }}>
            <div style={{ color: "rgba(255,255,255,0.46)", fontSize: 11, textAlign: "right" }}>{formatMiniPlayerTime(currentTime)}</div>
            <div
              onClick={seekFromPointer}
              style={{
                height: 8,
                borderRadius: 999,
                background: "rgba(255,255,255,0.12)",
                overflow: "hidden",
                cursor: onSeek ? "pointer" : "default",
              }}
            >
              <div
                style={{
                  width: `${progress * 100}%`,
                  height: "100%",
                  borderRadius: 999,
                  background: "linear-gradient(90deg, rgba(229,160,13,0.95), rgba(255,220,132,0.95))",
                }}
              />
            </div>
            <div style={{ color: "rgba(255,255,255,0.46)", fontSize: 11 }}>{formatMiniPlayerTime(duration)}</div>
          </div>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <button
          type="button"
          className="music-bottom-player-control"
          onClick={(event) => {
            event.stopPropagation();
            onPrev?.();
          }}
          style={{
            width: 42,
            height: 42,
            borderRadius: "50%",
            border: "1px solid rgba(255,255,255,0.14)",
            background: "rgba(255,255,255,0.08)",
            color: "#fff",
            cursor: "pointer",
            fontSize: 18,
          }}
          aria-label="Previous"
        >
          {"<<"}
        </button>
        <button
          type="button"
          className="music-bottom-player-control"
          onClick={(event) => {
            event.stopPropagation();
            if (isPlaying) onPause?.();
            else onPlay?.();
          }}
          style={{
            width: 54,
            height: 54,
            borderRadius: "50%",
            border: "1px solid rgba(229,160,13,0.42)",
            background: "rgba(229,160,13,0.92)",
            color: "#08080c",
            cursor: "pointer",
            fontSize: 18,
            fontWeight: 900,
            boxShadow: "0 10px 30px rgba(229,160,13,0.24)",
          }}
          aria-label={isPlaying ? "Pause" : "Play"}
        >
          {isPlaying ? "II" : ">"}
        </button>
        <button
          type="button"
          className="music-bottom-player-control"
          onClick={(event) => {
            event.stopPropagation();
            onNext?.();
          }}
          style={{
            width: 42,
            height: 42,
            borderRadius: "50%",
            border: "1px solid rgba(255,255,255,0.14)",
            background: "rgba(255,255,255,0.08)",
            color: "#fff",
            cursor: "pointer",
            fontSize: 18,
          }}
          aria-label="Next"
        >
          {">>"}
        </button>
        <button
          type="button"
          className="music-bottom-player-control"
          onClick={(event) => {
            event.stopPropagation();
            openDisplay();
          }}
          style={{
            height: 42,
            padding: "0 14px",
            borderRadius: 999,
            border: "1px solid rgba(255,255,255,0.14)",
            background: "rgba(255,255,255,0.08)",
            color: "rgba(255,255,255,0.82)",
            cursor: "pointer",
            fontSize: 11,
            fontWeight: 900,
            letterSpacing: 1.2,
          }}
        >
          DISPLAY
        </button>
      </div>
    </div>
  );
}

// ── MUSIC PAGE ────────────────────────────────────────────────────────────────
function MusicPage() {
  const [musicSource, setMusicSource] = useState(null);
  const [tab, setTab] = useState("home");
  const [artists, setArtists] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [musicArt, setMusicArt] = useState({});
  const [musicQuality, setMusicQuality] = useState({ tracks: {}, albums: {}, artists: {}, counts: {} });
  const [videos, setVideos] = useState([]);
  const [videosLoading, setVideosLoading] = useState(false);
  const [artistLimit, setArtistLimit] = useState(60);
  const [albumLimit, setAlbumLimit] = useState(60);
  const [loading, setLoading] = useState(true);
  const [playerTrack, setPlayerTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(70);
  const [shuffle, setShuffle] = useState(false);
  const [repeat, setRepeat] = useState(0);
  const [queue, setQueue] = useState([]);
  const [queueIdx, setQueueIdx] = useState(0);
  const [artistPage, setArtistPage] = useState(null);
  const [searchAlbumModal, setSearchAlbumModal] = useState(null);
  const [searchAlbumTracks, setSearchAlbumTracks] = useState([]);
  const [searchAlbumLoading, setSearchAlbumLoading] = useState(false);
  const audioRef = useRef(null);
  const playerExpandRef = useRef(null);
  const artistLoadRef = useRef(null);
  const albumLoadRef = useRef(null);
  const pendingVideoFocusRef = useRef(null);
  const lastDisplayCommandIdRef = useRef(null);
  const [pendingVideoFocusKey, setPendingVideoFocusKey] = useState(null);

  const musicScrollReturnRef = useRef(0);

const openArtistPage = (artist) => {
  musicScrollReturnRef.current = window.scrollY;
  setArtistPage(artist);
  window.scrollTo({ top: 0, behavior: "instant" });
};

const closeArtistPage = () => {
  setArtistPage(null);
  requestAnimationFrame(() => {
    window.scrollTo({
      top: musicScrollReturnRef.current || 0,
      behavior: "instant",
    });
  });
};

  const musicOpenDataRef = useRef({ artists, albums, videos });

  useEffect(() => {
    musicOpenDataRef.current = { artists, albums, videos };
  }, [artists, albums, videos]);

  useEffect(() => {
    const findByRatingKey = (items, ratingKey) =>
      (items || []).find(
        (item) => String(item?.ratingKey || "") === String(ratingKey || "")
      );

    const openArtistFromSearch = (detail, attempt = 0) => {
      const target =
        findByRatingKey(musicOpenDataRef.current.artists, detail.ratingKey) ||
        detail.item;

      if (target) {
        openArtistPage(target);
        return;
      }

      if (attempt < 4) {
        window.setTimeout(() => openArtistFromSearch(detail, attempt + 1), 200);
      }
    };

    const openAlbumFromSearch = (detail, attempt = 0) => {
      const target =
        findByRatingKey(musicOpenDataRef.current.albums, detail.ratingKey) ||
        detail.item;

      if (target) {
        setSearchAlbumModal(target);
        setSearchAlbumTracks([]);
        setSearchAlbumLoading(true);

        plex(`/library/metadata/${target.ratingKey}/children`)
          .then((d) => setSearchAlbumTracks(d?.Metadata || []))
          .catch(() => setSearchAlbumTracks([]))
          .finally(() => setSearchAlbumLoading(false));
        return;
      }

      if (attempt < 4) {
        window.setTimeout(() => openAlbumFromSearch(detail, attempt + 1), 200);
      }
    };

    const focusVideoFromSearch = (detail, attempt = 0) => {
      const target =
        findByRatingKey(musicOpenDataRef.current.videos, detail.ratingKey) ||
        detail.item;

      if (target) {
        pendingVideoFocusRef.current = String(target.ratingKey);
        setPendingVideoFocusKey(String(target.ratingKey));
        return;
      }

      if (attempt < 4) {
        window.setTimeout(() => focusVideoFromSearch(detail, attempt + 1), 200);
      }
    };

    const handler = (event) => {
      const detail = event.detail || {};

      setMusicSource("posterjunkie");
      setArtistPage(null);

      if (detail.type === "artist") {
        setTab("artists");
        openArtistFromSearch(detail);
        return;
      }

      if (detail.type === "album") {
        setTab("albums");
        openAlbumFromSearch(detail);
        return;
      }

      if (detail.type === "video") {
        setTab("videos");
        focusVideoFromSearch(detail);
      }
    };

    window.addEventListener("pj:musicOpen", handler);
    return () => window.removeEventListener("pj:musicOpen", handler);
  }, []);

  useEffect(() => {
    if (tab !== "videos" || videosLoading || !pendingVideoFocusKey) return undefined;

    let attempts = 0;
    let timer = null;

    const focusPendingVideo = () => {
      const ratingKey = pendingVideoFocusRef.current;
      if (!ratingKey) return;

      const el = Array.from(document.querySelectorAll(".video-card[data-ratingkey]"))
        .find((candidate) => String(candidate.dataset.ratingkey) === String(ratingKey));

      if (!el) {
        attempts += 1;
        if (attempts < 5) {
          timer = window.setTimeout(focusPendingVideo, 120);
        }
        return;
      }

      document
        .querySelectorAll(".napflix-tv-focused")
        .forEach((node) => node.classList.remove("napflix-tv-focused"));
      el.classList.add("napflix-tv-focused");
      el.focus?.({ preventScroll: true });
      window.__napflixCurrentFocus = el;
      el.scrollIntoView?.({ behavior: "smooth", block: "center" });
      pendingVideoFocusRef.current = null;
      setPendingVideoFocusKey(null);
    };

    timer = window.setTimeout(focusPendingVideo, 0);
    return () => {
      if (timer) window.clearTimeout(timer);
    };
  }, [tab, videosLoading, videos.length, pendingVideoFocusKey]);

  useEffect(() => {
    audioRef.current = new Audio();
    const audio = audioRef.current;
    const onTime = () => setCurrentTime(Math.floor(audio.currentTime));
    const onEnded = () => {
      setQueue(q => { setQueueIdx(i => { const n = (i + 1) % (q.length || 1); if (q[n]) { setPlayerTrack(q[n]); setCurrentTime(0); setIsPlaying(true); audio.src = q[n].streamUrl || ""; audio.load(); audio.play().catch(() => {}); } return n; }); return q; });
    };
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("ended", onEnded);
    return () => { audio.pause(); audio.removeEventListener("timeupdate", onTime); audio.removeEventListener("ended", onEnded); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    if (isPlaying) a.play().catch(() => {});
    else a.pause();
    if (playerTrack) {
      publishPJDisplayState(playerTrack, { playing: isPlaying });
      setPlayerState({ source: "posterjunkie", playing: isPlaying });
    }
  }, [isPlaying]);
  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume / 100;
    if (playerTrack) {
      publishPJDisplayState(playerTrack, { volume });
      setPlayerState({ source: "posterjunkie", volume });
    }
  }, [volume]);

  const buildTrackObj = (item) => {
  const media = item.Media?.[0];
  const stream = media?.Part?.[0]?.Stream?.find(s => s.streamType === 1);
  const partKey = media?.Part?.[0]?.key;

  const artUrl =
    item.localCover ||
    item.parentLocalCover ||
    (item.parentThumb
      ? pi(item.parentThumb, 500, 500)
      : item.thumb
      ? pi(item.thumb, 500, 500)
      : null);

  console.debug('Track art source for', item.title, ':', artUrl ? (artUrl.startsWith('/art-cache') ? 'local' : 'plex') : 'none');

  const q = musicQuality.tracks?.[String(item.ratingKey)] || {};

  return {
    title: item.title,
    artist: item.grandparentTitle || item.originalTitle || item.parentTitle || "",
    album: item.parentTitle || "",
    year: item.parentYear || item.year,

    artUrl,

    duration: Math.round((item.duration || 240000) / 1000),
    format: q.codec || media?.audioCodec?.toUpperCase() || "UNKNOWN",
    bitrate: q.bitrate || media?.bitrate || null,
    bitDepth: q.bitDepth || stream?.bitDepth || null,
    sampleRate: q.sampleRate || stream?.samplingRate || null,
    quality: q.quality || "Lossless",
    rating: item.userRating ? Math.round(item.userRating / 2) : 0,
    loved: (item.userRating || 0) >= 9,
    ratingKey: item.ratingKey,
    streamUrl: partKey
      ? `/api/plex-stream?path=${encodeURIComponent(partKey)}`
      : null,
  };
};

  const slimDisplayQueue = (items = []) =>
    (items || []).map((item) => ({
      ratingKey: item?.ratingKey || null,
      title: item?.title || "",
      artist: item?.artist || "",
      album: item?.album || "",
      duration: item?.duration || 0,
      art: item?.artUrl || item?.art || item?.thumb || null,
    }));

  const publishPJDisplayState = (track, overrides = {}) => {
    const displayQueue = overrides.queue ?? queue;
    try {
      const displayPayload = {
        source: "posterjunkie",
        service: "Poster Junkie",
        ratingKey: track?.ratingKey || track?.parentRatingKey || null,
        track: track?.title || "Nothing Playing",
        artist: track?.artist || "Poster Junkie Player",
        album: track?.album || "",
        year: track?.year || track?.albumYear || track?.parentYear || track?.originalYear || null,
        originallyAvailableAt: track?.originallyAvailableAt || null,
        art: track?.artUrl || track?.art || track?.thumb || null,
        duration: track?.duration || 0,
        position: overrides.position ?? currentTime ?? 0,
        playing: overrides.playing ?? isPlaying,
        volume: volume,
        format: track?.format || "UNKNOWN",
        quality: track?.quality || "Lossless",
        bitrate: track?.bitrate || null,
        bitDepth: track?.bitDepth || null,
        sampleRate: track?.sampleRate || null,
        queue: slimDisplayQueue(displayQueue),
        queueIndex: overrides.queueIndex ?? queueIdx ?? 0,
        updatedAt: Date.now()
      };

      localStorage.setItem("pj_display_state", JSON.stringify(displayPayload));

      fetch("/api/display-state", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(displayPayload),
      }).catch(() => {});
    } catch {}
  };

  const startTrack = (obj, newQ, newIdx) => {
    publishPJDisplayState(obj, {
      position: 0,
      playing: true,
      queue: newQ || queue,
      queueIndex: newIdx || 0,
    });

    setPlayerState({
      source: "posterjunkie",
      playing: true,
      title: obj.title,
      artist: obj.artist,
      album: obj.album,
      artwork: obj.artUrl,
      duration: obj.duration,
      position: 0,
      quality: obj.quality,
      codec: obj.format,
      bitDepth: obj.bitDepth,
      sampleRate: obj.sampleRate,
      bitrate: obj.bitrate,
      queue: newQ || queue,
      queueIndex: newIdx || 0,
    });

    setPlayerTrack(obj); if (newQ) { setQueue(newQ); setQueueIdx(newIdx || 0); } setCurrentTime(0); setIsPlaying(true);
    const a = audioRef.current; if (a) { a.pause(); a.src = obj.streamUrl || ""; a.load(); a.play().catch(() => {}); }
    if (playerExpandRef.current) playerExpandRef.current();
  };

  const playTrack = async (item) => {
    if (!item.Media || item.Media.length === 0) {
      try { const d = await plex("/library/metadata/" + item.ratingKey + "/children"); const tracks = (d?.Metadata || []).filter(t => t.type === "track"); if (tracks.length > 0) { const objs = tracks.map(buildTrackObj); startTrack(objs[0], objs, 0); return; } } catch (e) { console.error(e); }
    }
    startTrack(buildTrackObj(item), [buildTrackObj(item)], 0);
  };

  const handleNext = () => { setQueue(q => { setQueueIdx(i => { const n = (i + 1) % (q.length || 1); if (q[n]) startTrack(q[n], null, n); return n; }); return q; }); };
  const handlePrev = () => { const a = audioRef.current; if (a && a.currentTime > 3) { a.currentTime = 0; setCurrentTime(0); return; } setQueue(q => { setQueueIdx(i => { const p = (i - 1 + (q.length || 1)) % (q.length || 1); if (q[p]) startTrack(q[p], null, p); return p; }); return q; }); };

  useEffect(() => {
    const handleDisplayCommand = (payload) => {
      if (!payload?.id || payload.id === lastDisplayCommandIdRef.current) return;
      lastDisplayCommandIdRef.current = payload.id;

      if (payload.type === "pj:displayPlay") {
        setIsPlaying(true);
        audioRef.current?.play?.().catch(() => {});
        return;
      }

      if (payload.type === "pj:displayPause") {
        setIsPlaying(false);
        audioRef.current?.pause?.();
        return;
      }

      if (payload.type === "pj:displayTogglePlay") {
        if (audioRef.current?.paused) {
          setIsPlaying(true);
          audioRef.current.play().catch(() => {});
        } else {
          setIsPlaying(false);
          audioRef.current?.pause?.();
        }
        return;
      }

      if (payload.type === "pj:displayNext") {
        handleNext();
        return;
      }

      if (payload.type === "pj:displayPrevious") {
        handlePrev();
        return;
      }

      if (payload.type === "pj:displayPlayQueueIndex") {
        const idx = Number(payload.detail?.index);
        const t = queue[idx];
        if (Number.isFinite(idx) && t) startTrack(t, null, idx);
      }
    };

    const onStorage = (event) => {
      if (event.key !== "pj_display_command" || !event.newValue) return;

      try {
        handleDisplayCommand(JSON.parse(event.newValue));
      } catch {}
    };

    window.addEventListener("storage", onStorage);

    const pollDisplayCommand = async () => {
      try {
        const res = await fetch("/api/display-command", { cache: "no-store" });
        const payload = await res.json();
        if (payload?.createdAt && Date.now() - payload.createdAt < 5000) {
          handleDisplayCommand(payload);
        }
      } catch {}
    };

    const commandPoll = setInterval(pollDisplayCommand, 500);
    pollDisplayCommand();

    try {
      const raw = localStorage.getItem("pj_display_command");
      const payload = raw ? JSON.parse(raw) : null;
      if (payload?.createdAt && Date.now() - payload.createdAt < 5000) {
        handleDisplayCommand(payload);
      }
    } catch {}

    return () => {
      clearInterval(commandPoll);
      window.removeEventListener("storage", onStorage);
    };
  }, [queue, handleNext, handlePrev, startTrack]);

  useEffect(() => {
    let alive = true;

    fetch(`/art-cache/music-quality-manifest.json?t=${Date.now()}`, { cache: "no-store" })
      .then((r) => r.json())
      .then((data) => {
        if (alive) setMusicQuality(data || { tracks: {}, albums: {}, artists: {}, counts: {} });
      })
      .catch(() => {
        if (alive) setMusicQuality({ tracks: {}, albums: {}, artists: {}, counts: {} });
      });

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
  let alive = true;

  const loadMusicArt = () => {
    fetch(`/art-cache/music-manifest.json?t=${Date.now()}`, {
      cache: "no-store",
    })
      .then((r) => r.json())
      .then((data) => {
        if (alive) setMusicArt(data || {});
      })
      .catch(() => {
        if (alive) setMusicArt({});
      });
  };

  loadMusicArt();

  const interval = setInterval(loadMusicArt, 10000);

  return () => {
    alive = false;
    clearInterval(interval);
  };
}, []);

  useEffect(() => {
  const MUSIC_CACHE_KEY = "pj_music";
  const MUSIC_CACHE_TTL = 1000 * 60 * 60 * 24;

  const fetchMusic = () => Promise.all([
  plex(`/library/sections/${MUSIC_KEY}/all?type=8&sort=titleSort:asc&X-Plex-Container-Start=0&X-Plex-Container-Size=2000`)
    .catch(() => ({ Metadata: [] })),
  plex(`/library/sections/${MUSIC_KEY}/all?type=9&sort=addedAt:desc&X-Plex-Container-Start=0&X-Plex-Container-Size=2000`)
    .catch(() => ({ Metadata: [] })),
]).then(([a, alb]) => {
      const ar = (a?.Metadata || []).filter(
        x => String(x.librarySectionID) !== String(MUSIC_BANDS_KEY)
      );

      const vi = videos || [];
      const al = alb?.Metadata || [];

      setArtists(ar);
      setVideos(vi);
      setAlbums(al);

      try {
        sessionStorage.setItem(
          MUSIC_CACHE_KEY,
          JSON.stringify({ artists: ar, videos: vi, albums: al, ts: Date.now() })
        );
      } catch {}

      setLoading(false);
    });

  try {
    const raw = sessionStorage.getItem(MUSIC_CACHE_KEY);

    if (raw) {
      const { artists: ca, videos: cv, albums: cal, ts } = JSON.parse(raw);

      const filteredCa = (ca || []).filter(
        x => String(x.librarySectionID) !== String(MUSIC_BANDS_KEY)
      );

      setArtists(filteredCa);
      setVideos(cv || []);
      setAlbums(cal || []);
      setLoading(false);

      if (Date.now() - ts >= MUSIC_CACHE_TTL) {
        setTimeout(() => fetchMusic().catch(() => {}), 1200);
      }

      return;
    }
  } catch {}

  fetchMusic().catch(() => {
    setLoading(false);
  });
}, []);

useEffect(() => {
  if (tab !== "videos") return;
  if (videos.length > 0 || videosLoading) return;

  const VIDEO_CACHE_KEY = "pj_music_videos";
  const VIDEO_CACHE_TTL = 1000 * 60 * 60 * 24;

  try {
    const raw = sessionStorage.getItem(VIDEO_CACHE_KEY);
    if (raw) {
      const { videos: cachedVideos, ts } = JSON.parse(raw);
      setVideos(cachedVideos || []);

      if (Date.now() - ts < VIDEO_CACHE_TTL) return;
    }
  } catch {}

  setVideosLoading(true);

  plex(`/library/sections/${MUSIC_VIDEO_KEY}/all`)
    .then((d) => {
      const vi = d?.Metadata || [];
      setVideos(vi);
      try {
        sessionStorage.setItem(
          VIDEO_CACHE_KEY,
          JSON.stringify({ videos: vi, ts: Date.now() })
        );
      } catch {}
    })
    .catch(() => {})
    .finally(() => setVideosLoading(false));
}, [tab, videos.length, videosLoading]);

useEffect(() => {
  if (tab !== "artists" && tab !== "albums") return;

  const artistTarget = artistLoadRef.current;
  const albumTarget = albumLoadRef.current;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        if (tab === "artists" && entry.target === artistTarget) {
          setArtistLimit((v) => Math.min(v + 60, artists.length));
        }

        if (tab === "albums" && entry.target === albumTarget) {
          setAlbumLimit((v) => Math.min(v + 60, albums.length));
        }
      });
    },
    {
      root: null,
      rootMargin: "700px",
      threshold: 0.01,
    }
  );

  if (tab === "artists" && artistTarget) observer.observe(artistTarget);
  if (tab === "albums" && albumTarget) observer.observe(albumTarget);

  return () => observer.disconnect();
}, [tab, artists.length, albums.length]);

useEffect(() => {
  const onScroll = () => {
    const nearBottom =
      window.innerHeight + window.scrollY >=
      document.documentElement.scrollHeight - 1200;

    if (!nearBottom) return;

    if (tab === "artists") {
      setArtistLimit((v) =>
        Math.min(v + 60, artists.length)
      );
    }

    if (tab === "albums") {
      setAlbumLimit((v) =>
        Math.min(v + 60, albums.length)
      );
    }
  };

  window.addEventListener("scroll", onScroll, {
    passive: true,
  });

  return () =>
    window.removeEventListener("scroll", onScroll);
}, [tab, artists.length, albums.length]);

  useEffect(() => {
    if (playerTrack) {
      publishPJDisplayState(playerTrack, { position: currentTime, playing: isPlaying });
      setPlayerState({
        source: "posterjunkie",
        position: currentTime,
        playing: isPlaying,
      });
    }
  }, [currentTime, playerTrack, isPlaying]);

  const open = (item) => window.open(`${PLEX_URL}/web/index.html#!/server/${TOKEN}/details?key=/library/metadata/${item.ratingKey}`, "_blank");
  const recentAlbums = useMemo(() => {
  return [...albums]
    .sort((a, b) => (b.addedAt || 0) - (a.addedAt || 0))
    .slice(0, 20);
}, [albums]);

const recentVideos = useMemo(() => {
  return [...videos]
    .sort((a, b) => (b.addedAt || 0) - (a.addedAt || 0))
    .slice(0, 20);
}, [videos]);

  const albumMap = useMemo(() => {
  const map = new Map();
  albums.forEach((a) => map.set(String(a.ratingKey), a));
  return map;
}, [albums]);

const handleAlbumClick = useCallback((e) => {
  const rk = e.currentTarget.dataset.ratingkey;
  const album = albumMap.get(String(rk));
  if (album) playTrack(album);
}, [albumMap]);

  if (loading) return <div className="loading" style={{ minHeight: "60vh" }}><div className="spin" /></div>;

  if (!musicSource) {
    return (
      <div style={{
        minHeight: "calc(100vh - 120px)",
        padding: 52,
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}>
        <div style={{ width: "min(1100px, 92vw)" }}>
          <div style={{
            color: "var(--gold)",
            textTransform: "uppercase",
            letterSpacing: 4,
            fontSize: 12,
            fontWeight: 900,
            marginBottom: 14
          }}>
            Poster Junkie Music
          </div>

          <div style={{
            fontSize: 58,
            lineHeight: 1,
            fontWeight: 950,
            letterSpacing: -2,
            marginBottom: 12
          }}>
            Choose Music Source
          </div>

          <div style={{
            color: "rgba(255,255,255,.58)",
            fontSize: 17,
            marginBottom: 42
          }}>
            One player language. One cable-box display. Two music libraries.
          </div>

          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: 24
          }}>
            <button
              autoFocus
              onClick={() => setMusicSource("spotify")}
              style={{
                minHeight: 230,
                borderRadius: 26,
                border: "1px solid rgba(255,255,255,.14)",
                background: "linear-gradient(135deg, rgba(30,215,96,.18), rgba(255,255,255,.04))",
                color: "#fff",
                padding: 32,
                display: "flex",
                alignItems: "center",
                gap: 28,
                textAlign: "left",
                cursor: "pointer",
                boxShadow: "0 24px 80px rgba(0,0,0,.4)"
              }}
            >
              <img src="/spotify_logo.png" alt="Spotify" style={{ width: 180, maxHeight: 80, objectFit: "contain" }} />
              <div>
                <div style={{ fontSize: 30, fontWeight: 950, marginBottom: 10 }}>Spotify</div>
                <div style={{ color: "rgba(255,255,255,.64)", fontSize: 14, lineHeight: 1.45 }}>
                  Your playlists, liked songs, followed artists, saved albums, and streaming playback.
                </div>
              </div>
            </button>

            <button
              onClick={() => setMusicSource("posterjunkie")}
              style={{
                minHeight: 230,
                borderRadius: 26,
                border: "1px solid rgba(255,255,255,.14)",
                background: "linear-gradient(135deg, rgba(229,160,13,.20), rgba(255,255,255,.04))",
                color: "#fff",
                padding: 32,
                display: "flex",
                alignItems: "center",
                gap: 28,
                textAlign: "left",
                cursor: "pointer",
                boxShadow: "0 24px 80px rgba(0,0,0,.4)"
              }}
            >
              <div style={{
                width: 132,
                height: 132,
                borderRadius: 28,
                display: "grid",
                placeItems: "center",
                background: "linear-gradient(135deg, var(--gold), #5d3b00)",
                color: "#000",
                fontSize: 44,
                fontWeight: 950,
                letterSpacing: -3,
                flexShrink: 0
              }}>
                PJ
              </div>

              <div>
                <div style={{ fontSize: 30, fontWeight: 950, marginBottom: 10 }}>Poster Junkie Library</div>
                <div style={{ color: "rgba(255,255,255,.64)", fontSize: 14, lineHeight: 1.45 }}>
                  Your Plex music, artists, albums, local files, and music videos.
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (musicSource === "spotify") {
    return (
      <div style={{ minHeight: "100vh" }}>
        <button
          onClick={() => setMusicSource(null)}
          style={{
            margin: "28px 0 0 42px",
            border: "1px solid rgba(255,255,255,.12)",
            background: "rgba(255,255,255,.06)",
            color: "#fff",
            borderRadius: 999,
            padding: "10px 16px",
            fontWeight: 900,
            cursor: "pointer"
          }}
        >
          ← Music Sources
        </button>
        <SpotifyPage />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      {artistPage ? (
        <div style={{ flex: 1, minWidth: 0, overflowY: 'auto', paddingTop: 62 }}>
          <ArtistPage
  artist={artistPage}
  musicArt={musicArt}
  onBack={closeArtistPage}
  startTrack={startTrack}
  onArtist={(a) => setArtistPage(a)}
/>
        </div>
      ) : (
        <div className="media-page" style={{ flex: 1, minWidth: 0, paddingBottom: playerTrack ? 132 : undefined }}>
          <div className="media-page-title">Music</div>
          <div className="media-page-sub">{artists.length} artists · {albums.length} albums · {videos.length} music videos</div>
          <div className="media-tabs">
            {[["home", "Home"], ["artists", "Artists"], ["albums", "Albums"], ["videos", "Music Videos"], ["spotify", "Spotify"]].map(([k, l]) => <button key={k} className={`mtab${tab === k ? " on" : ""}`} onClick={() => setTab(k)}>{k === "spotify" ? <img src="/spotify_logo.png" alt="Spotify" style={{ height: 18, display: "block" }} /> : l}</button>)}
          </div>
          {tab === "spotify" && <SpotifyPage />}
          {tab === "home" && (
            <>
              <div className="music-hero"><div className="music-hero-icon">🎵</div><div><div className="music-hero-title">Your Music Library</div><div className="music-hero-sub">{artists.length} artists · {albums.length} albums · {videos.length} music videos</div></div></div>
              {recentAlbums.length > 0 && (<div className="sec" style={{ padding: 0, marginBottom: 44 }}><div className="sec-head" style={{ padding: 0, marginBottom: 16 }}><div className="sec-left"><div className="sec-bar" /><div><div className="sec-title">Recently Added Albums</div></div></div></div><div className="album-grid">{recentAlbums.map(a => <div key={a.ratingKey} className="album-card" onClick={() => playTrack(a)}><div className="album-img">{a.thumb ? <img loading="lazy" decoding="async" src={pi(a.thumb, 150)} alt={a.title} onError={e => e.target.style.display = "none"} /> : <div style={{ width: "100%", height: "100%", background: "var(--s2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 32 }}>🎵</div>}</div><div className="album-title">{a.title}</div><div className="album-artist">{a.parentTitle || "Unknown Artist"}</div></div>)}</div></div>)}
              {recentVideos.length > 0 && (<div className="sec" style={{ padding: 0, marginBottom: 44 }}><div className="sec-head" style={{ padding: 0, marginBottom: 16 }}><div className="sec-left"><div className="sec-bar" /><div><div className="sec-title">Recent Music Videos</div></div></div></div><div className="video-grid">{recentVideos.map(v => <div key={v.ratingKey} className="video-card" onClick={() => open(v)}><div className="video-thumb">{v.thumb ? <img loading="lazy" decoding="async" src={pi(v.thumb, 200)} alt={v.title} onError={e => e.target.style.display = "none"} /> : <div className="card-ph">{v.title}</div>}<div className="video-ov"><div style={{ width: 46, height: 46, borderRadius: "50%", background: "rgba(229,160,13,0.85)", display: "flex", alignItems: "center", justifyContent: "center" }}><svg width="14" height="14" viewBox="0 0 16 16" fill="#000"><path d="M4 2l10 6-10 6V2z" /></svg></div></div></div><div className="video-title">{v.title}</div>{v.parentTitle && <div className="video-sub">{v.parentTitle}</div>}</div>)}</div></div>)}
              <div className="sec" style={{ padding: 0 }}>
  <div className="sec-head" style={{ padding: 0, marginBottom: 16 }}>
    <div className="sec-left">
      <div className="sec-bar" />
      <div>
        <div className="sec-title">All Artists</div>
      </div>
    </div>
  </div>

  <div className="artist-grid">
    {artists.slice(0, 12).map((a) => (
      <div
        key={a.ratingKey}
        className="artist-card"
        onClick={() => openArtistPage(a)}
      >
        <div className="artist-img">
          {a.thumb ? (
            <img
        decoding="async"
              src={pi(a.thumb, 150)}
              alt={a.title}
              onError={(e) => (e.target.style.display = "none")}
            />
          ) : (
            <div
              style={{
                width: "100%",
                height: "100%",
                background: "var(--s2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "var(--t3)",
                fontSize: 28,
              }}
            >
              {a.title?.[0]}
            </div>
          )}
        </div>

        <div className="artist-name">{a.title}</div>
      </div>
    ))}
  </div>
</div>
            </>
          )}

    {tab === "artists" && (
  <>
    <div className="artist-grid">
      {artists.slice(0, artistLimit).map(a => (
        <div
          key={a.ratingKey}
          className="artist-card"
          onClick={() => openArtistPage(a)}
        >
          <div className="artist-img">
            {a.thumb ? (
              <CardImg src={pi(a.thumb, 220)} alt={a.title} />
            ) : (
              <div style={{ width: "100%", height: "100%", background: "var(--s2)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--t3)", fontSize: 28 }}>
                {a.title?.[0]}
              </div>
            )}
          </div>
          <div className="artist-name">{a.title}</div>
        </div>
      ))}
    </div>

    {artistLimit < artists.length && (
      <div style={{ height: 80, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--t3)", fontSize: 12 }}>
        Loading more artists...
      </div>
    )}
  </>
)}

{tab === "albums" && (
  <>
    <div className="album-grid">
      {albums.slice(0, albumLimit).map(a => (
        <div
          key={a.ratingKey}
          className="album-card"
          data-ratingkey={a.ratingKey}
          onClick={handleAlbumClick}
        >
          <div className="album-img">
            {a.thumb ? (
              <CardImg src={pi(a.thumb, 320, 320)} alt={a.title} />
            ) : (
              <div style={{ width: "100%", height: "100%", background: "var(--s2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 32 }}>
                🎵
              </div>
            )}
          </div>
          <div className="album-title">{a.title}</div>
          <div className="album-artist">{a.parentTitle || "Unknown Artist"}</div>
          {musicQuality.albums?.[String(a.ratingKey)]?.qualities?.["Hi-Res Lossless"] ? (
            <div style={{ marginTop: 6, color: "#d5a85f", fontSize: 10, fontWeight: 900, letterSpacing: 1.4 }}>
              HI-RES LOSSLESS
            </div>
          ) : musicQuality.albums?.[String(a.ratingKey)]?.qualities?.Lossless ? (
            <div style={{ marginTop: 6, color: "rgba(255,255,255,.55)", fontSize: 10, fontWeight: 900, letterSpacing: 1.4 }}>
              LOSSLESS
            </div>
          ) : null}
        </div>
      ))}
    </div>

    {albumLimit < albums.length && (
      <div style={{ height: 80, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--t3)", fontSize: 12 }}>
        Loading more albums...
      </div>
    )}
  </>
)}

{tab === "videos" && (
  <>
    {videosLoading && (
      <div className="loading" style={{ minHeight: 260 }}>
        <div className="spin" />
      </div>
    )}

    {!videosLoading && videos.length === 0 && (
      <div style={{ padding: 40, textAlign: "center", color: "var(--t3)" }}>
        No music videos found
      </div>
    )}

    {!videosLoading && videos.length > 0 && (
      <div className="video-grid">
        {videos.map(v => (
          <div key={v.ratingKey} className="video-card" data-ratingkey={v.ratingKey} tabIndex={0} role="button" onClick={() => open(v)}>
            <div className="video-thumb">
              {v.thumb ? (
                <CardImg src={pi(v.thumb, 240)} alt={v.title} />
              ) : (
                <div className="card-ph">{v.title}</div>
              )}

              <div className="video-ov">
                <div style={{ width: 46, height: 46, borderRadius: "50%", background: "rgba(229,160,13,0.85)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="#000">
                    <path d="M4 2l10 6-10 6V2z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="video-title">{v.title}</div>
            {v.parentTitle && <div className="video-sub">{v.parentTitle}</div>}
          </div>
        ))}
      </div>
    )}
  </>
)}
        </div>
      )}

{playerTrack && (
  <MusicBottomPlayer
    track={playerTrack}
    expandRef={playerExpandRef}
    currentTime={currentTime}
    isPlaying={isPlaying}
    onPlay={() => setIsPlaying(true)}
    onPause={() => setIsPlaying(false)}
    onPrev={handlePrev}
    onNext={handleNext}
    onSeek={(f) => {
      const t = Math.round(f * (playerTrack?.duration || 0));
      setCurrentTime(t);
      if (audioRef.current) audioRef.current.currentTime = t;
    }}
    queue={queue}
    queueIdx={queueIdx}
  />
)}

{searchAlbumModal && (
  <AlbumModal
    album={searchAlbumModal}
    tracks={searchAlbumTracks}
    loadingTracks={searchAlbumLoading}
    onClose={() => {
      setSearchAlbumModal(null);
      setSearchAlbumTracks([]);
    }}
    onPlayTrack={playTrack}
  />
)}

    </div>
  );
}

// ── SEARCH ────────────────────────────────────────────────────────────────────
function Search({ query, movies, shows, onCard, onClose }) {
  const q = query.toLowerCase().trim();
  const all = [...movies, ...shows];
  const results = q.length > 1 ? all.filter(m => m.title?.toLowerCase().includes(q) || m.summary?.toLowerCase().includes(q)).slice(0, 48) : [];
  return (
    <div className="sovl">
      <button className="sclose" onClick={onClose}>✕</button>
      <div className="shead">{q.length > 1 ? `"${query}"` : "Search your library..."}</div>
      <div className="sgrid">{results.map(item => <MediaCard key={item.ratingKey} item={item} onClick={r => { onCard(r); onClose(); }} />)}</div>
    </div>
  );
}

// ── APP ───────────────────────────────────────────────────────────────────────
export default 
function App() {

  const isNapflixKiosk = getSystemState().isNapflixOS;

  const bootDoneFromQuery =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("bootdone") === "1";

  const [bootDone, setBootDone] = useState(!isNapflixKiosk || bootDoneFromQuery);

  useEffect(() => {
    document.documentElement.classList.toggle("napflix-kiosk", isNapflixKiosk);
    return () => document.documentElement.classList.remove("napflix-kiosk");
  }, [isNapflixKiosk]);

  useEffect(() => {
    // Napflix OS runtime shell v1
    return installNapflixOSRuntimeShell();
  }, []);

  useEffect(() => {
    // NapflixOS Runtime Layer v2
    return installSystemStateRuntime();
  }, []);

  useEffect(() => {
    // NapflixOS BootManager v3
    if (!isNapflixKiosk) return undefined;
    return installBootManager();
  }, [isNapflixKiosk]);

  useEffect(() => {
    // NapflixOS Ambient Runtime v5
    if (!isNapflixKiosk) return undefined;
    return installAmbientManager();
  }, [isNapflixKiosk]);

  useEffect(() => {
    // NapflixOS Route Manager v11
    if (!isNapflixKiosk) return undefined;
    return installRouteManager();
  }, [isNapflixKiosk]);

  useEffect(() => {
    // NapflixOS Back Manager v13.2
    if (!isNapflixKiosk) return undefined;
    return installBackRuntime();
  }, [isNapflixKiosk]);

  const [startupHold, setStartupHold] = useState(true);
  const [movies, setMovies] = useState([]);
  const [shows, setShows] = useState([]);
  const [tmdbRows, setTmdbRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState("home");
  const [detail, setDetail] = useState(null);
  const [person, setPerson] = useState(null);
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const scrolled = useRef(false);
  const navRef = useRef(null);
  const logoRef = useRef(null);

  useEffect(() => {
    const h = () => {
      if (!logoRef.current) return;

      const atTop = window.scrollY <= 8;
        logoRef.current.style.opacity = atTop ? "1" : "0";
        logoRef.current.style.transform = atTop ? "scale(1)" : "scale(0.92)";
        logoRef.current.style.pointerEvents = atTop ? "auto" : "none";
    };

    window.addEventListener("scroll", h, { passive: true });
    h();

    return () => window.removeEventListener("scroll", h);
  }, []);

  useEffect(() => {
    const handler = (event) => {
      setPage(event.detail || "home");
      setDetail(null);
      setPerson(null);
      window.scrollTo({ top: 0, behavior: "smooth" });
    };

    window.addEventListener("pj:setPage", handler);
    return () => window.removeEventListener("pj:setPage", handler);
  }, []);

  useEffect(() => {
    if (!isNapflixKiosk) return;
    if (page !== "movies" && page !== "shows") return;

    let tries = 0;

    const timer = setInterval(() => {
      tries += 1;

      const tabs = Array.from(document.querySelectorAll(".lib-sub-tab"))
        .filter((el) => {
          const r = el.getBoundingClientRect();
          const st = window.getComputedStyle(el);
          return (
            r.width > 20 &&
            r.height > 20 &&
            st.display !== "none" &&
            st.visibility !== "hidden"
          );
        });

      const wantedText = page === "shows" ? "Featured" : "Featured";
      const el =
        tabs.find((tab) => tab.textContent?.trim() === wantedText) ||
        tabs.find((tab) => tab.textContent?.includes("Featured")) ||
        tabs[0];

      if (!el && tries < 30) return;
      clearInterval(timer);

      if (!el) return;

      document
        .querySelectorAll(".napflix-tv-focused")
        .forEach((x) => x.classList.remove("napflix-tv-focused"));

      el.classList.add("napflix-tv-focused");
      el.focus({ preventScroll: true });
      window.__napflixCurrentFocus = el;
    }, 200);

    return () => clearInterval(timer);
  }, [page, isNapflixKiosk]);


// NAPFLIX KIOSK SAFE REMOTE FOCUS LAYER
useEffect(() => {
  if (!isNapflixKiosk) return;

  const FOCUS_SELECTOR = [
  ".nav-btn",
  ".btn-play",
  ".btn-more",
  ".btn-wl",
  ".detail-back",
  ".sec-all",
  ".lib-sub-tab",
  ".albtn",
  ".card",
  ".feat-card",
  ".season-card",
  ".ep-card",
  ".extra-card",
  ".cast-card",
  ".rel-card",
  ".artist-card",
  ".album-card",
  ".album-modal-track",
  ".video-card",

  ".tvguide-player-control",
  ".tvguide-program-cell",
  ".tvguide-channel-cell",

  "button",
  "a"
].join(",");

  let currentEl = null;
  let navLocked = false;

  const clickEl = (el) => {
    el?.dispatchEvent(new MouseEvent("click", {
      bubbles: true,
      cancelable: true,
      view: window,
    }));
  };

  const getItems = () => {
    const items = Array.from(document.querySelectorAll(FOCUS_SELECTOR))
      .filter((el) => {
        const albumModal = document.querySelector(".album-modal");
        if (albumModal && !el.closest(".album-modal")) return false;

        const detailPage = document.querySelector(".detail-page");
        if (detailPage && !el.closest(".detail-page")) return false;

        const r = el.getBoundingClientRect();
        const st = window.getComputedStyle(el);
        const tag = el.tagName?.toLowerCase();
        const excludedTvGuideFocus =
          !el.closest(".tvguide-back-btn") &&
          el.closest(
            ".tvguide-preview-box, .tvguide-preview-video, .tvguide-edge-btn"
          );

        if (excludedTvGuideFocus) return false;

        const clickable =
          tag === "button" ||
          tag === "a" ||
          el.onclick ||
          el.getAttribute("role") === "button" ||
          [
            "card",
            "feat-card",
            "season-card",
            "ep-card",
            "extra-card",
            "cast-card",
            "rel-card",
            "artist-card",
            "album-card",
            "album-modal-track",
            "video-card",
            "nav-btn",
            "btn-play",
            "btn-more",
            "btn-wl",
            "detail-back",
            "lib-sub-tab",
            "albtn",
            "tvguide-player-control",
            "tvguide-program-cell",
            "tvguide-channel-cell"
          ].some((cls) => el.classList?.contains(cls));

        return (
          clickable &&
          r.width > 30 &&
          r.height > 20 &&
          st.display !== "none" &&
          st.visibility !== "hidden" &&
          st.pointerEvents !== "none"
        );
      });

    items.forEach((el) => {
      el.tabIndex = 0;
      el.dataset.napflixFocusable = "true";
    });

    return items;
  };

  const focusEl = (el, dir = null) => {
    if (!el) return;

    document
      .querySelectorAll(".napflix-tv-focused")
      .forEach((x) => x.classList.remove("napflix-tv-focused"));

    currentEl = el;
    window.__napflixCurrentFocus = el;
    el.classList.add("napflix-tv-focused");
    el.focus({ preventScroll: true });

    requestAnimationFrame(() => {
      const detailPage = document.querySelector(".detail-page");
      if (
        detailPage &&
        el.closest(".detail-page") &&
        (
          el.classList.contains("detail-back") ||
          el.classList.contains("btn-play") ||
          el.classList.contains("btn-more") ||
          el.classList.contains("btn-wl")
        )
      ) {
        detailPage.scrollTo({ top: 0, behavior: "smooth" });
      }

      const row = el.closest(".row, .featured-row, .rel-row-h, .seasons-row, .cast-row");

      if ((dir === "left" || dir === "right") && row) {
        const rowRect = row.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();

        const target =
          row.scrollLeft +
          (elRect.left - rowRect.left) -
          rowRect.width / 2 +
          elRect.width / 2;

        row.scrollTo({
          left: target,
          behavior: "smooth",
        });

        return;
      }

      el.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
    });
  };

  const rows = () => {
    const items = getItems().map((el) => ({ el, r: el.getBoundingClientRect() }));
    const grouped = [];

    items.forEach((item) => {
      const y = item.r.top + item.r.height / 2;
      let row = grouped.find((g) => Math.abs(g.y - y) < 70);
      if (!row) {
        row = { y, items: [] };
        grouped.push(row);
      }
      row.items.push(item);
      row.y = row.items.reduce((sum, x) => sum + x.r.top + x.r.height / 2, 0) / row.items.length;
    });

    grouped.forEach((g) => g.items.sort((a, b) => a.r.left - b.r.left));
    return grouped.sort((a, b) => a.y - b.y);
  };

  const focusFirst = () => {
    const allRows = rows();
    focusEl(allRows[0]?.items?.[0]?.el);
  };

  const isTopNavEl = (el) =>
    el?.matches?.(".nav-btn");

  const closestInRow = (row, currentX) => {
    return row?.items?.reduce((best, item) => {
      const x = item.r.left + item.r.width / 2;
      const d = Math.abs(x - currentX);
      return !best || d < best.d ? { item, d } : best;
    }, null)?.item?.el;
  };

  const move = (dir) => {
    const allRows = rows();
    if (!allRows.length) return;

    const active = window.__napflixCurrentFocus || currentEl || document.activeElement;
    let ri = -1;
    let ii = -1;

    allRows.forEach((row, rIndex) => {
      const found = row.items.findIndex((x) => x.el === active);
      if (found !== -1) {
        ri = rIndex;
        ii = found;
      }
    });

    if (ri === -1) {
      focusFirst();
      return;
    }

    const row = allRows[ri];

    if (dir === "right") return row.items[ii + 1]?.el ? focusEl(row.items[ii + 1].el, "right") : null;
    if (dir === "left") return row.items[ii - 1]?.el ? focusEl(row.items[ii - 1].el, "left") : null;

    const currentX = row.items[ii].r.left + row.items[ii].r.width / 2;
    const navRows = allRows.filter((r) => r.items.some((x) => isTopNavEl(x.el)));
    const contentRows = allRows.filter((r) => r.items.some((x) => !isTopNavEl(x.el)));

    const currentIsNav = row.items.some((x) => isTopNavEl(x.el));
    const contentIndex = contentRows.findIndex((r) => r === row);

    if (currentIsNav && dir === "down") {
      return focusEl(closestInRow(contentRows[0], currentX), "down");
    }

    if (!currentIsNav && dir === "up") {
      if (contentIndex > 0) {
        return focusEl(closestInRow(contentRows[contentIndex - 1], currentX), "up");
      }

      return focusEl(closestInRow(navRows[navRows.length - 1], currentX), "up");
    }

    if (!currentIsNav && dir === "down") {
      if (contentIndex >= 0 && contentRows[contentIndex + 1]) {
        return focusEl(closestInRow(contentRows[contentIndex + 1], currentX), "down");
      }

      window.scrollBy({ top: 520, behavior: "smooth" });
      setTimeout(() => {
        const freshRows = rows().filter((r) => r.items.some((x) => !isTopNavEl(x.el)));
        focusEl(closestInRow(freshRows[freshRows.length - 1], currentX), "down");
      }, 220);
    }
  };

  const onKey = (e) => {
    const tvGuideOwnedSelector = [
      ".tvguide-back-btn",
      ".tvguide-channel-cell",
      ".tvguide-program-cell",
      ".tvguide-player-control"
    ].join(",");
    const tvGuideGridFocusedEls = [
      currentEl,
      window.__napflixCurrentFocus,
      document.querySelector(".napflix-tv-focused"),
      document.activeElement
    ];
    const tvGuideGridHasFocus = tvGuideGridFocusedEls.some((el) =>
      el?.isConnected &&
      (el.matches?.(tvGuideOwnedSelector) || el.closest?.(tvGuideOwnedSelector))
    );

    if (tvGuideGridHasFocus) return;

    const k = e.key;
    const albumModal = document.querySelector(".album-modal");
    const albumModalKeys = [
      "ArrowDown",
      "ArrowUp",
      "Enter",
      "Select",
      " ",
      "Spacebar",
      "Escape",
      "Backspace"
    ];
    if (albumModal && albumModalKeys.includes(k)) return;

    if (k === "ArrowRight") { e.preventDefault(); move("right"); return; }
    if (k === "ArrowLeft") { e.preventDefault(); move("left"); return; }

    if (k === "ArrowDown") {
      e.preventDefault();
      move("down");
      return;
    }

    if (k === "ArrowUp") {
      e.preventDefault();

      move("up");
      return;
    }

    if (k === "Enter" || k === "Select") {
  e.preventDefault();

  const active = currentEl || document.activeElement;

  if (active?.classList?.contains("tvguide-preview-video")) {
    active.paused ? active.play() : active.pause();
    return;
  }

  clickEl(active);
}

    if (k === "Escape" || k === "Backspace") {
      const albumModal = document.querySelector(".album-modal");
      if (albumModal) {
        e.preventDefault();
        e.stopPropagation();
        clickEl(albumModal.querySelector(".album-modal-close"));
        return;
      }

      e.preventDefault();
      const back = document.querySelector(".detail-back");
      back ? clickEl(back) : window.history.back();
    }
  };

  window.addEventListener("keydown", onKey, true);
  const t = setTimeout(focusFirst, 2000);

  return () => {
    clearTimeout(t);
    window.removeEventListener("keydown", onKey, true);
  };
}, [isNapflixKiosk]);


useEffect(() => {
  let cancelled = false;

  async function loadRows() {
    const CACHE_KEY = "pj_tmdb_home_rows";
    const CACHE_TTL = 1000 * 60 * 60 * 12;

    try {
      const raw = sessionStorage.getItem(CACHE_KEY);

      if (raw) {
        const { rows, ts } = JSON.parse(raw);

        if (rows?.length) {
          setTmdbRows(rows);

          if (Date.now() - ts < CACHE_TTL) return;
        }
      }
    } catch {}

    const rows = await getTMDBPlaylists("home");

    if (!cancelled) {
      setTmdbRows(rows);

      try {
        sessionStorage.setItem(
          CACHE_KEY,
          JSON.stringify({ rows, ts: Date.now() })
        );
      } catch {}
    }
  }

  loadRows();

  return () => {
    cancelled = true;
  };
}, []);

useEffect(() => {
  const CACHE_KEY = "pj_library";
  const CACHE_TTL = 1000 * 60 * 60 * 4;

  const fetchLibrary = () =>
  fetch("/api/cache/home")
    .then((r) => r.json())
    .catch(() => ({ movies: [], shows: [] }))
    .then((homeData) => {
    const mMap = new Map();
    (homeData?.movies || []).forEach((m) =>
      mMap.set(m.ratingKey, m)
    );

    const sMap = new Map();
    (homeData?.shows || []).forEach((s) =>
      sMap.set(s.ratingKey, s)
    );

    const mv = [...mMap.values()];
    const sh = [...sMap.values()];

    setMovies(mv);
    setShows(sh);

    if (mv.length || sh.length) {
      try {
        sessionStorage.setItem(
          CACHE_KEY,
          JSON.stringify({
            movies: mv,
            shows: sh,
            ts: Date.now(),
          })
        );
      } catch {}
    }

    setLoading(false);
  });

  const init = async () => {
    try {
      const raw = sessionStorage.getItem(CACHE_KEY);

      if (raw) {
        const { movies: cm, shows: cs, ts } = JSON.parse(raw);

        if ((cm?.length || 0) || (cs?.length || 0)) {
          setMovies(cm);
          setShows(cs);
          setLoading(false);

          if (Date.now() - ts >= CACHE_TTL) {
            fetchLibrary().catch(() => {});
          }
          return;
        }
      }

      await fetchLibrary();
    } catch (e) {
      console.error(e);
    }

    setLoading(false);
  };

  init();
}, []);

  const scrollReturnRef = useRef(0);
  const detailReturnKeyRef = useRef(null);
  const detailReturnRectRef = useRef(null);

const handleCard = useCallback((item) => {
  scrollReturnRef.current = window.scrollY;
  detailReturnKeyRef.current = item?.ratingKey || null;

  const activeCard = document.querySelector(".napflix-tv-focused");
  const rect = activeCard?.getBoundingClientRect?.();

  detailReturnRectRef.current = rect
    ? {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      }
    : null;

  setDetail(item);
  setPerson(null);
  window.scrollTo({ top: 0, behavior: "instant" });
}, []);

useEffect(() => {
  const handler = (event) => {
    const { item, ratingKey, type } = event.detail || {};
    const libraryItem =
      item ||
      [...movies, ...shows].find(
        (candidate) =>
          String(candidate?.ratingKey || "") === String(ratingKey || "") &&
          (!type || candidate?.type === type)
      );

    if (libraryItem) handleCard(libraryItem);
  };

  window.addEventListener("pj:openItem", handler);
  return () => window.removeEventListener("pj:openItem", handler);
}, [handleCard, movies, shows]);

const handleBack = useCallback(() => {
  const returnKey = detailReturnKeyRef.current;
  const returnRect = detailReturnRectRef.current;

  setDetail(null);

  setTimeout(() => {
    window.scrollTo({
      top: scrollReturnRef.current || 0,
      behavior: "instant",
    });

    requestAnimationFrame(() => {
      let el = returnKey
        ? document.querySelector(`[data-rating-key="${returnKey}"]`)
        : null;

      if (!el && returnRect) {
        const candidates = Array.from(
          document.querySelectorAll(".card, .feat-card, .rel-card, .season-card, .ep-card, .extra-card")
        ).filter((node) => {
          const r = node.getBoundingClientRect();
          return r.width > 30 && r.height > 20;
        });

        el = candidates.reduce((best, node) => {
          const r = node.getBoundingClientRect();
          const cx = r.left + r.width / 2;
          const cy = r.top + r.height / 2;
          const d = Math.hypot(cx - returnRect.x, cy - returnRect.y);
          return !best || d < best.d ? { node, d } : best;
        }, null)?.node;
      }

      if (el) {
        document
          .querySelectorAll(".napflix-tv-focused")
          .forEach((x) => x.classList.remove("napflix-tv-focused"));

        el.classList.add("napflix-tv-focused");
        el.focus({ preventScroll: true });
        window.__napflixCurrentFocus = el;
      }
    });
  }, 150);
}, []);
  useEffect(() => {
    const timer = setTimeout(() => setStartupHold(false), 7000);
    return () => clearTimeout(timer);
  }, []);

  const handlePersonSearch = useCallback((name) => { setDetail(null); setPerson(name); }, []);
  const handlePersonBack = useCallback(() => { setPerson(null); }, []);

  // StartupGate disabled for Napflix boot sequence
  // StartupGate disabled for Napflix boot sequence
const showUI = !detail && !person;

  if (window.location.pathname === "/display") {
    return <DisplayPage />;
  }

  return (
    <>
      {isNapflixKiosk && <AmbientOverlay />}
      {isNapflixKiosk && <NotificationOverlay />}
      {isNapflixKiosk && <CommandCenterOverlay />}
      {isNapflixKiosk && <SearchOverlay />}
      {isNapflixKiosk && <NowPlayingOverlay />}
      {isNapflixKiosk && <SystemStatusOverlay />}
      {!bootDone && <NapflixBoot onDone={() => setBootDone(true)} />}

      <style>{S}

</style>

{showUI && !searching && !detail && !person && page !== "home" && page !== "tv" && (
  <button
    className="tab-back-btn"
    onClick={() => {
      setPage("home");
      setSearching(false);
      setQuery("");
      setPerson(null);
    }}
  >
    ← Back
  </button>
)}

{showUI && page === "home" && (
  <nav className="nav" ref={navRef}>
    <ul className="nav-links">
      {[["home", "Home"], ["movies", "Movies"], ["shows", "TV Shows"], ["music", "Music"], ["tv", "TV Guide"], ["categories", "Browse"], ["watchlist", "Watchlist"]].map(([p, l]) => (
        <li key={p}>
          <button
            className={`nav-btn${page === p ? " on" : ""}`}
            onClick={() => {
              setPage(p);
              setSearching(false);
              setQuery("");
              setPerson(null);
            }}
          >
            {l}
          </button>
        </li>
      ))}
    </ul>

    <div className="nav-right" style={{ position: "absolute", right: 52 }}>
      <div className="search-wrap">
        <svg className="search-ico" width="13" height="13" viewBox="0 0 16 16" fill="rgba(255,255,255,0.5)">
          <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85zm-5.242 1.656a5.5 5.5 0 1 1 0-11 5.5 5.5 0 0 1 0 11z" />
        </svg>
        <input
          className="search-inp"
          placeholder="Search everything..."
          value={query}
          onChange={e => {
            setQuery(e.target.value);
            setSearching(e.target.value.length > 1);
          }}
        />
      </div>
    </div>
  </nav>
)}

      {detail && <DetailPage item={detail} allMovies={movies} allShows={shows} onBack={handleBack} onCard={item => { setDetail(null); setTimeout(() => setDetail(item), 60); }} onPersonSearch={handlePersonSearch} />}
      {!detail && person && <PersonPage name={person} movies={movies} shows={shows} onCard={handleCard} onBack={handlePersonBack} />}
      {showUI && searching && <Search query={query} movies={movies} shows={shows} onCard={handleCard} onClose={() => { setQuery(""); setSearching(false); }} />}
      {window.location.pathname === "/spotify-callback" && <SpotifyPage />}
      {window.location.pathname !== "/spotify-callback" && window.location.pathname !== "/display" && showUI && !searching && page === "home" && (
        <Home
           movies={movies}
           shows={shows}
           tmdbRows={tmdbRows}
          onCard={handleCard}
        />
      )}
      {showUI && !searching && page === "movies" && <LibraryTabPage title="Movies" items={movies} onCard={handleCard} type="movie" />}
      {showUI && !searching && page === "shows" && <LibraryTabPage title="TV Shows" items={shows} onCard={handleCard} type="show" />}
      {showUI && !searching && page === "music" && <MusicPage />}
      {showUI && !searching && page === "tv" && <TVGuide />}
      {showUI && !searching && page === "categories" && <CatsPage movies={movies} shows={shows} onCard={handleCard} />}
      {showUI && !searching && page === "watchlist" && <WlPage movies={movies} shows={shows} onCard={handleCard} />}
      }
    </>
  );
}
